import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { CHANGE_EVENT as PREF_CHANGE_EVENT, usePref } from '../lib/prefs.js';
// Les TEXTES des deux fenetres de confirmation vivent la-bas, en fabriques a
// nombre (le chantier 3_0 les reprend telles quelles pour le lot). On les
// APPELLE, on ne les recopie pas : une phrase ecrite deux fois est une phrase
// qui divergera.
import { archiveConfirm, removeConfirm } from '../components/ConfirmDialog.js';
import { DEFAULT_SEARCH_PLACEHOLDER } from '../components/ScreenLayout.js';
import type { ScreenAction, ScreenStat } from '../components/ScreenLayout.js';
import type {
  BulkEditField,
  Cell,
  Column,
  Cursor,
  EditorKind,
  FieldMap,
  Option,
  ProjectedRow,
  SaveState,
  SaveTarget,
  ScreenFavorite,
  ScreenQuery,
  SortLevel,
  ViewBinding,
  ViewColumn,
  ViewId,
  ViewLevel,
  ViewOption,
  ViewProps,
  ViewStatus,
  ViewType,
} from './contract.js';
/*
 * Le catalogue des champs modifiables en lot est declare dans le CONTRAT (il
 * ne parle que de roles, de colonnes et d'editeurs, tous definis la-bas), mais
 * il se LIT sur l'etat d'ecran : on le re-exporte ici pour que la fenetre de
 * lot l'importe au meme endroit que le reste de ce qu'elle recoit, sans avoir
 * a deviner lequel des deux fichiers le porte.
 */
export type { BulkEditField } from './contract.js';
/*
 * LE VOCABULAIRE PARTAGE FICHE / LOT, importe et jamais recopie. Le catalogue
 * de champs modifiables se construit ici, mais il decrit des champs que la
 * FICHE sait deja nommer et editer : la clef opaque d'un champ (`fieldKeyOf`)
 * et la deduction du type d'editeur (`editorKindOf`) doivent donc etre LES
 * MEMES des deux cotes, au caractere pres.
 *
 * Les deux ont d'abord ete RECOPIEES ici. C'est le mode de panne exact que
 * l'extraction de `CellEditor.tsx` voulait fermer : une regle ajoutee a
 * `editorKindOf` et la fiche monte le nouvel editeur pendant que la fenetre de
 * lot continue de monter un champ texte, donc le lot ecrit une chaine dans un
 * champ booleen, sur douze fiches d'un coup, sans qu'un seul build rougisse.
 */
import { editorKindOf, fieldKeyOf } from './core/CellEditor.js';
import { assertBinding, unmetRequirement } from './assertBinding.js';
import { buildCursor, firstOf, freezeIds } from './cursor.js';
import {
  joinFilterIds,
  pickScreenSearch,
  readArchivesParam,
  screenSearchKey,
  splitFilterIds,
  useScreenUrlOwnership,
  writeArchivesParam,
} from './screenUrl.js';
import type { ScreenSearch } from './screenUrl.js';
import {
  applyColumnPatch,
  applyRolePatch,
  catalogOf,
  compareCells,
  matchesSearch,
  projectRecord,
  projectRecords,
  resolvesSortKey,
  sameCell,
  sortValueOf,
} from './project.js';
import type { NewRecordsInput } from './project.js';
import {
  markSeen,
  newBadgePrefKey,
  newMemoryPrefKey,
  readNewMemory,
  writeNewMemory,
} from './newRecords.js';
import type { NewMemory } from './newRecords.js';

/**
 * useScreen : L'ETAT D'ECRAN, tenu par le core.
 *
 * Il est appele DANS le fichier d'ecran, pas cache dans un contexte : quand
 * une vue se comporte mal, on lit l'etat la ou il est cree. Changer de vue
 * n'est qu'un setState ici, donc l'etat partage (recherche, tri, fenetre,
 * selection, curseur) survit par construction : aucune vue ne refiltre, ne
 * retrie, ne recharge.
 *
 * Ce que ce hook possede : la requete, la selection, la vue active, la
 * derniere vue collection, l'element ouvert, le curseur, l'etat jetable de
 * chaque vue, et l'ECRITURE (optimiste). Ce que l'app possede : l'appel de
 * donnees (`load`), les bindings, les catalogues (`options()`), l'ecriture
 * reelle (`set()`).
 *
 * DEUX MODES DE CHARGEMENT, choisis par l'ecran (`pagination`), et c'est le
 * SEUL endroit ou la difference existe : aucune vue ne sait lequel tourne.
 *
 * - `client` (DEFAUT, comportement historique) : le chargeur rend tout d'un
 *   coup, le core filtre, trie et fenetre en memoire, avec un plafond visible
 *   (`DEFAULT_LIMIT`) et un bandeau au-dela. Honnete tant que la collection
 *   tient dans un onglet.
 * - `server` : le chargeur rend UNE PAGE (`query.limit` lignes au plus, apres
 *   `query.after`), le core les EMPILE, et `loadMore()` demande la suivante.
 *   Le tri devient un parametre de requete (le changer repart de la page 1),
 *   et "il reste des pages" se lit "la derniere page etait pleine", le contrat
 *   du SDK (`runList`, @taki/sdk).
 *
 * CE QUE LE MODE SERVEUR NE FAIT PAS, et qu'il faut dire a l'utilisateur
 * plutot que lui laisser croire : Firestore n'a pas de recherche plein texte,
 * donc la RECHERCHE et les PASTILLES restent CLIENTES, sur les pages deja
 * chargees. Le bandeau du `ViewHost` le formule au lieu de laisser penser
 * qu'un resultat absent n'existe pas.
 *
 * Le champ `demand` de chaque vue reste declare honnetement : c'est ce qui a
 * evite d'avoir a rouvrir les cinq vues en passant au mode serveur.
 *
 * LES PORTES (gabarit v2). La zone 1 n'est plus la segmentation d'une
 * collection : chaque entree du menu est une PORTE, un JEU DE DONNEES de l'app
 * (decision fondateur du 01/08/2026 : la zone 1 dit QUEL JEU, la zone 4 dit
 * QUELLE TRANCHE). Une porte peut apporter son chargement, son catalogue de
 * vues, son catalogue de filtres, ses actions, ses stats, son placeholder de
 * recherche et ses presets de favoris (`ScreenDoor`) ; ce qu'elle ne declare
 * pas retombe sur les options de l'ecran. L'id de porte voyage dans le
 * parametre `s` de l'adresse, exactement la ou voyageait la section qu'il
 * remplace : meme parametre, meme sens ("quel jeu"). CHANGER DE PORTE remet a
 * zero la recherche, les filtres et la pile de tri, referme la fiche, vide la
 * selection et recharge : un autre jeu de donnees ne garde rien du precedent
 * (le placeholder, les actions et les stats suivent la porte par
 * construction, l'ecran les lit sur `state.door`).
 *
 * RETRO-COMPATIBILITE, non negociable : un ecran sans `doors` = une porte
 * IMPLICITE unique reprenant les options de l'ecran, et `section` garde
 * exactement son sens libre d'avant (des tranches du MEME jeu : aucune remise
 * a zero au changement, le core ne connait pas leur catalogue). Les ecrans
 * ecrits avant les portes compilent et se comportent a l'identique.
 *
 * PERSISTANCE, quatre etages. L'ADRESSE passe devant tout le reste quand elle
 * porte quelque chose : un lien partage doit montrer ce qu'il annonce, pas la
 * derniere fiche que CE navigateur avait ouverte.
 * - l'URL `?view=&id=&s=&f=` : l'etat PARTAGEABLE, branche par le shell via
 *   `ScreenUrlProvider` (voir screenUrl.tsx). `f` est une liste d'ids separes
 *   par des virgules depuis le gabarit v2 (multi-filtres additifs). Absent
 *   hors shell : le hook retombe alors exactement sur les etages ci-dessous ;
 * - sessionStorage `taki.screen.<appId>.<screenId>` : l'etat VIVANT de la
 *   session (vue active, element ouvert, derniere vue collection) ;
 * - pref locale `app.<appId>.<screenId>.view` : la DERNIERE vue collection
 *   utilisee, ecrite toute seule a chaque bascule ;
 * - pref locale `app.<appId>.<screenId>.view.pinned` : la vue EPINGLEE par
 *   l'utilisateur dans les reglages de l'app. Vide = "derniere vue utilisee".
 *   C'est un choix explicite, donc il passe devant la memoire automatique ;
 * - pref locale `app.<appId>.<screenId>.<porte>.favorites` : les FAVORIS
 *   gardes par l'utilisateur (zone 4b), une liste PAR PORTE, meme mecanique
 *   d'event `taki:pref-change` que les prefs de vues (le hub Parametres et
 *   l'ecran se resynchronisent tout seuls, tant qu'ils sont dans le MEME
 *   document : l'event ne franchit pas l'onglet).
 * - pref locale `app.<appId>.<screenId>.<porte>.nouveau` : la memoire du
 *   reperage des NOUVEAUTES, une par porte (date de reference + ids deja
 *   ouverts, `NEW_SEEN_MAX` au plus). Elle s'allume ou s'eteint pour toute l'app par
 *   `app.<appId>.nouveau`. Tout est dans `newRecords.ts`, y compris pourquoi
 *   c'est local au poste et ou il faudra toucher le jour du multi-utilisateur.
 * Une cle par ECRAN, jamais par app : deux sections d'une meme app n'ont pas
 * les memes vues. La pile de tri, elle, ne persiste NULLE PART : ni adresse
 * (meme decision que la recherche), ni session ; elle vit et meurt avec
 * l'ecran, et les favoris sont sa seule memoire nommee.
 *
 * ECRITURE, quatre garanties tenues ici et nulle part ailleurs (une app ne
 * recode aucune des quatre) :
 * - une ecriture plus RECENTE annule l'effet d'une plus ancienne (jeton de
 *   sequence par couple id + cle), sinon une reponse lente ecrase un etat frais ;
 * - une ecriture reussie RECHARGE le jeu (`reload`) : c'est notre seule parade
 *   au multi-utilisateur dans ce lot, et elle reconcilie les stats de la zone 3 ;
 * - un echec devient VISIBLE (`saveState.mode === 'error'` + `retrySave`), au
 *   lieu de disparaitre dans la console ;
 * - la PILE D'ANNULATION vit le temps de la session de fiche : elle NAIT vide a
 *   l'ouverture, survit aux fleches precedent / suivant, et meurt a la sortie.
 *   Un JETON DE SESSION (distinct du jeton de sequence, qui est par cible)
 *   garantit le bord d'apres : une ecriture partie avant la sortie et revenue
 *   apres ne repeuple ni la pile ni `saveState`.
 */

/** Plafond de fenetre du mode CLIENT (500 lignes, bandeau au-dela). */
export const DEFAULT_LIMIT = 500;

/**
 * Taille de page du mode SERVEUR. Volontairement bien plus petite que le
 * plafond client : une page, c'est ce qu'un ecran affiche, pas ce qu'un onglet
 * supporte. 50 lignes remplissent un tableau sans faire attendre.
 */
export const DEFAULT_PAGE_SIZE = 50;

/**
 * Plafond dur de la fenetre CLIENTE. Aligne sur le plafond de page du SDK
 * (`MAX_LIST_LIMIT`, @taki/sdk), recopie ici plutot qu'importe : le design
 * system ne depend pas de la couche donnees. Un ecran client qui relaie
 * `query.limit` au SDK ne peut donc pas, a force de clics, demander une page
 * que le SDK refusera.
 */
export const MAX_CLIENT_WINDOW = 1000;

/**
 * Profondeur de la pile d'annulation. On coupe par la TETE (les pas les plus
 * anciens tombent) : un plafond qui jetterait les pas recents rendrait
 * `Cmd+Z` imprevisible.
 */
const UNDO_LIMIT = 20;

/** Vue de repli universelle quand l'ecran n'en impose aucune. */
const FALLBACK_VIEW_ID: ViewId = 'core.liste';

const SESSION_PREFIX = 'taki.screen.';

/** Cle de la DERNIERE vue collection utilisee (memoire automatique). */
export function lastViewPrefKey(appId: string, screenId: string): string {
  return `app.${appId}.${screenId}.view`;
}

/** Cle de la vue EPINGLEE par l'utilisateur dans les reglages de l'app. */
export function pinnedViewPrefKey(appId: string, screenId: string): string {
  return `app.${appId}.${screenId}.view.pinned`;
}

/**
 * Cle des favoris GARDES par l'utilisateur (zone 4b). Une liste PAR PORTE :
 * les filtres d'un jeu de donnees ne veulent rien dire dans un autre (le
 * proto montrait les memes favoris partout, simplification de proto a ne pas
 * reproduire). L'ecran sans portes range les siens sous la porte implicite.
 */
export function favoritesPrefKey(appId: string, screenId: string, doorId: string): string {
  return `app.${appId}.${screenId}.${doorId}.favorites`;
}

/**
 * Cle des LARGEURS DE COLONNES reglees a la main (`core.tableau`). Meme portee
 * que les favoris, PLUS la vue : deux vues tableau d'une meme porte n'affichent
 * pas les memes colonnes, donc une largeur reglee sur l'une ne veut rien dire
 * sur l'autre. Valeur stockee : `{ [cleDeColonne]: largeurEnRem }`, en rem et
 * jamais en px (le shell corrige la racine rem selon la densite de l'ecran,
 * `packages/shell/src/uiScale.ts`).
 */
export function columnWidthsPrefKey(
  appId: string,
  screenId: string,
  doorId: string,
  viewId: string,
): string {
  return `app.${appId}.${screenId}.${doorId}.${viewId}.columnWidths`;
}

/**
 * L'id de la porte IMPLICITE d'un ecran sans `doors`. Il ne sort JAMAIS dans
 * l'adresse (le parametre `s` d'un ecran sans portes reste la section libre) :
 * il ne sert qu'a donner une cle de pref aux favoris et une identite a
 * `state.door`.
 */
export const IMPLICIT_DOOR_ID = 'default';

/**
 * Le placeholder de recherche du gabarit v2 quand la porte n'en declare pas.
 * La constante VIT dans ScreenLayout (c'est le chassis qui la rend en 2b, et
 * qui la sert de defaut aux ecrans V1) ; elle est re-exportee ici, ou la
 * resolution de porte l'utilise, pour garder la surface publique historique.
 * Aucune IA n'est cablee derriere : c'est un placeholder, la promesse
 * appartient au shell (Ask Taki AI), pas a l'ecran.
 */
export { DEFAULT_SEARCH_PLACEHOLDER };

/**
 * Oublie l'etat de session d'un ecran. Appele quand l'utilisateur epingle une
 * vue dans les reglages : sans ca, la vue en cours de session passerait devant
 * son choix tout neuf, et l'epinglage n'aurait aucun effet visible avant la
 * fermeture de l'onglet.
 */
export function forgetScreenSession(appId: string, screenId: string): void {
  try {
    window.sessionStorage.removeItem(`${SESSION_PREFIX}${appId}.${screenId}`);
  } catch {
    /* sessionStorage indisponible : rien a oublier. */
  }
}

/* ------------------------------------------------------------------ */
/* Types publics du hook                                               */
/* ------------------------------------------------------------------ */

/**
 * Un filtre declare par l'ecran (V1) ou par une porte (v2), vu par le core.
 * Le `match` est ce qui permet au CORE de filtrer : sans lui, le filtre est
 * purement decoratif (a l'app de filtrer dans son `load`). En multi-filtres
 * additifs, un filtre sans `match` ne contribue donc RIEN au OU : il ne
 * neutralise pas les autres (un "laisse tout passer" dans un OU rendrait les
 * voisins muets), et la lecture V1 (une seule pastille active, sans `match`,
 * qui montre tout) reste exactement ce qu'elle etait.
 */
export interface ScreenFilterDef<T> {
  id: string;
  label: string;
  match?(record: T): boolean;
}

/**
 * Un filtre tel que l'UI le recoit (`ScreenState.filters`). Superset de la
 * forme que `ScreenLayout.filters` accepte deja (id + label) : les ecrans V1
 * le passent tel quel a leurs pastilles, le popover de l'outil filtre (v2) y
 * lit en plus le compteur.
 */
export interface ScreenFilterOption {
  id: string;
  label: string;
  /**
   * Combien de lignes de la PORTE BRUTE ce filtre retient (archives exclues,
   * recherche et autres filtres ignores : regle "un compteur ne bouge pas
   * quand on filtre"). Absent quand le filtre n'a pas de `match` : le core ne
   * peut pas compter ce qu'il ne sait pas evaluer, et un zero serait un
   * mensonge.
   */
  count?: number;
}

/**
 * UNE PORTE (zone 1 du gabarit v2) : un JEU DE DONNEES de l'app, avec tout ce
 * qui l'accompagne. Chaque champ optionnel retombe sur l'option de meme nom de
 * l'ecran (`UseScreenOptions`) : trois portes qui partagent le meme chargeur
 * ne le declarent qu'une fois et le distinguent par `query.section`, exactement
 * comme les sections d'avant.
 */
export interface ScreenDoor<T> {
  /** Id stable : l'entree de menu (zone 1b) ET la valeur du parametre `s`. */
  id: string;
  /** Libelle de l'entree de menu. Le compteur, lui, reste calcule par l'ecran (jeu complet, gele). */
  label: string;
  /** Le chargement de CE jeu de donnees. Absent : le `load` de l'ecran. */
  load?(query: ScreenQuery): Promise<T[]>;
  /** Chargement d'UN enregistrement (entree a froid). Absent : le `get` de l'ecran. */
  get?(id: string): Promise<T | null>;
  /**
   * ARCHIVER une fiche (`sdk.<famille>.archive`). Absent : l'option de meme
   * nom de l'ecran ; absente aussi, l'entree du menu de la rangee 3c est
   * GRISEE avec sa raison (regle G16), JAMAIS absente. Un geste qui apparait
   * et disparait selon l'app fabrique exactement le "pourquoi je ne peux pas
   * archiver ici" qu'on veut eviter.
   *
   * Le core se charge du reste : la fenetre de confirmation, la fermeture de
   * la fiche, le rechargement de la porte et le compte rendu de l'ecriture
   * (`saveState`). La porte ne fait qu'ECRIRE.
   */
  archive?(record: T): Promise<void>;
  /** DESARCHIVER une fiche. Meme patron, meme repli que `archive`. */
  unarchive?(record: T): Promise<void>;
  /**
   * SUPPRIMER une fiche (`sdk.<famille>.remove` : les trois etats du socle,
   * `archivedAt` + `deletedAt`). Meme patron, meme repli que `archive`. Pour
   * l'utilisateur, supprime veut dire supprime : la fiche quitte les listes ET
   * les archives, et rien dans l'interface ne la recupere.
   */
  remove?(record: T): Promise<void>;
  /** Le catalogue de vues de la porte. Absent : les `types` de l'ecran. */
  types?: ViewType[];
  /** Un binding par type de vue. Absent : les `bindings` de l'ecran. */
  bindings?: ViewBinding<T>[];
  /** Vue d'arrivee quand la vue courante n'existe pas derriere cette porte. */
  defaultView?: ViewId;
  /**
   * Le CATALOGUE DE FILTRES de la porte (popover de l'outil filtre, zone 3b).
   * Multi-choix ADDITIF : l'etat est une liste d'ids de ce catalogue
   * (`query.filterIds`), vide au passage de la porte. Absent : les `filters`
   * de l'ecran, comme les autres replis.
   */
  filters?: ScreenFilterDef<T>[];
  /**
   * `false` = cette porte ne se TRANCHE pas (ex. Livraisons) : aucun filtre,
   * aucun favori, l'ETOILE est masquee. Depuis le 07/08/2026 (regle G20),
   * l'outil FILTRE reste RENDU et ne porte que l'entree Archives ; et le
   * tiroir de la zone 4, qui n'existe pas sur cette porte, se rend tant que
   * le mode Archives est allume, avec le seul tag de mode. Le groupe d'outils
   * garde donc le tri ET le filtre. Le mot vient du vocabulaire du fondateur
   * ("sections" = les tranches d'un jeu), pas des sections V1 de la zone 1.
   * Defaut : true.
   */
  sections?: boolean;
  /**
   * Les actions de la porte (zone 3a). La regle du libelle universel
   * ("+ Ajouter", premier, lime) est tenue par le gabarit, pas ici : la porte
   * declare, l'ecran passe `state.door.actions` au gabarit.
   */
  actions?: ScreenAction[];
  /**
   * Les stats de la porte (zone 2c, plafond 3 tenu par le gabarit). Forme
   * fonction : elle recoit le jeu VIVANT de la porte (archives exclues, AVANT
   * recherche et filtres : doctrine "stats sur la porte brute, insensibles aux
   * filtres"), a chaque rendu. Forme tableau : des valeurs deja calculees par
   * l'ecran.
   */
  stats?: ScreenStat[] | ((live: T[]) => ScreenStat[]);
  /** Placeholder de la recherche (2b). Absent : `DEFAULT_SEARCH_PLACEHOLDER`. */
  searchPlaceholder?: string;
  /**
   * Les favoris PRESETS de la porte (zone 4c), servis avant ceux que
   * l'utilisateur garde. Ignores quand `sections` est false.
   */
  presets?: ScreenFavorite[];
}

/**
 * LA PORTE ACTIVE, RESOLUE : ce que l'ecran passe au gabarit sans rien
 * recalculer (placeholder, actions, stats). Recomposee a chaque rendu ; pour
 * un ecran sans portes elle decrit la porte implicite (valeurs neutres).
 */
export interface ScreenDoorState {
  id: string;
  label: string;
  searchPlaceholder: string;
  /** false = pas de filtres ni de favoris ici (outil filtre, etoile et tiroir masques). */
  sections: boolean;
  actions: ScreenAction[];
  stats: ScreenStat[];
}

export interface UseScreenOptions<T> {
  /** Id de l'app (cle de persistance). */
  appId: string;
  /** Id de l'ecran dans l'app (cle de persistance : une cle par ecran). */
  screenId: string;
  /** L'appel de donnees. Recoit la requete courante ; c'est l'APP qui parle au SDK. */
  load(query: ScreenQuery): Promise<T[]>;
  /** L'identite d'un enregistrement. */
  getId(record: T): string;
  /**
   * Chargement d'UN enregistrement, pour l'entree a froid et l'element sorti
   * du filtre : on affiche la fiche sans charger 10 000 lignes. Absent, une
   * fiche hors du jeu courant affiche "Fiche introuvable".
   */
  get?(id: string): Promise<T | null>;
  /**
   * ARCHIVER une fiche, pour TOUTES les portes de l'ecran (repli de
   * `ScreenDoor.archive`). C'est ici qu'un ecran a une seule porte declare son
   * geste, comme il declare deja `load` et `get`.
   */
  archive?(record: T): Promise<void>;
  /** DESARCHIVER une fiche, pour toutes les portes. Repli de `ScreenDoor.unarchive`. */
  unarchive?(record: T): Promise<void>;
  /** SUPPRIMER une fiche, pour toutes les portes. Repli de `ScreenDoor.remove`. */
  remove?(record: T): Promise<void>;
  /** Les types de vues proposes par cet ecran, importes EXPLICITEMENT. */
  types: ViewType[];
  /** Un binding par type de vue : la meme donnee ne se raconte pas pareil partout. */
  bindings: ViewBinding<T>[];
  /** Vue par defaut a la premiere ouverture (avant toute preference utilisateur). */
  defaultView?: ViewId;
  /**
   * LES PORTES de l'ecran (gabarit v2) : les jeux de donnees de la zone 1.
   * Des qu'il y en a au moins une, `section` devient l'id de la porte active
   * (verifie contre ce catalogue, contrairement aux sections libres : ici le
   * core LE connait), `setSection` fait la remise a zero de changement de
   * porte, et les champs non declares d'une porte retombent sur les options
   * de l'ecran. Absent ou vide : l'ecran est sa propre porte implicite, et
   * TOUT garde le comportement d'avant les portes.
   */
  doors?: ScreenDoor<T>[];
  /**
   * Filtres de l'ecran (lecture V1 : pastilles a choix unique via `setFilter`,
   * une active en permanence). Les portes declarent le leur (`ScreenDoor.filters`,
   * multi-choix additif) ; celui-ci sert de repli quand la porte n'en a pas.
   */
  filters?: ScreenFilterDef<T>[];
  /**
   * Pastille active au demarrage (lecture V1 seulement). Defaut : la premiere
   * declaree. Ignoree des que des portes existent : derriere une porte, l'etat
   * de depart des filtres est VIDE ("Tout le parc"), le catalogue n'a pas de
   * pseudo-entree "Tous".
   */
  defaultFilterId?: string;
  /**
   * Section active au demarrage : l'entree de menu de la zone 1 (`Tous`,
   * `Camera`, `Lumiere`...). L'ecran passe ensuite `screen.section` a
   * `activeMenuId` et `screen.setSection` a `onMenuSelect` ; il ne tient PLUS
   * cet etat en `useState` chez lui, et il n'a plus besoin d'un effet qui
   * rappelle `reload()` quand la section change (le core recharge tout seul,
   * la section fait partie de la requete).
   *
   * Le core ne CONNAIT pas la liste des sections : elle vit dans les
   * `menuItems` que l'ecran donne au gabarit. Une adresse qui porte une section
   * inconnue rend donc ce que le chargeur rend pour elle, en general rien, et
   * l'ecran affiche son etat vide. C'est honnete, et c'est le seul cout de ne
   * pas dupliquer ce catalogue dans deux endroits.
   *
   * AVEC DES PORTES, cette option designe la porte d'arrivee : elle est alors
   * VERIFIEE (le catalogue est connu ici), et une valeur inconnue retombe sur
   * la premiere porte plutot que sur un ecran vide.
   */
  defaultSection?: string;
  /**
   * CLE DE RECHARGEMENT EXTERNE. Quand la SOURCE que le chargeur lit change en
   * dehors de l'ecran (une lecture partagee entre plusieurs ecrans, rechargee
   * par l'un d'eux), l'app passe ici sa generation : un horodatage, un compteur,
   * n'importe quelle valeur qui change quand la source change.
   *
   * Elle fait partie de la cle de chargement, donc le core recharge TOUT SEUL,
   * exactement comme pour la section. C'est ce qui evite le
   * `useEffect(() => reload(), [source])` maison que la checklist interdit
   * (item 7) : ce n'etait pas un caprice de style, un effet d'app rechargeait en
   * plus du rechargement que le core fait deja apres chaque ecriture reussie,
   * donc deux lectures completes par ecriture.
   *
   * Laisser `undefined` quand le chargeur est la seule source de verite.
   */
  reloadKey?: string | number;
  /**
   * Qui pagine. Defaut `client` : le chargeur rend TOUT et le core fenetre en
   * memoire (comportement historique, inchange).
   *
   * `server` : le chargeur ne rend qu'une page. Il DOIT alors honorer trois
   * champs de la requete qu'il recevait deja sans les lire, sinon la
   * pagination ment : `limit` (taille de page), `after` (curseur, l'id du
   * dernier element rendu precedemment) et `sort` (tri serveur). En pratique,
   * c'est un passe-plat vers `sdk.<famille>.list()`, qui prend exactement ces
   * arguments.
   */
  pagination?: 'client' | 'server';
  /**
   * Mode client : plafond de fenetre (defaut `DEFAULT_LIMIT`).
   * Mode serveur : TAILLE DE PAGE (defaut `DEFAULT_PAGE_SIZE`).
   */
  limit?: number;
}

/** L'etat d'ecran complet, passe tel quel au `ViewHost`. */
export interface ScreenState<T> {
  /* Donnees */
  /** Les enregistrements bruts rendus par `load` (archives compris). */
  records: T[];
  /** Les lignes que la vue active doit rendre (fenetre courante). */
  rows: ProjectedRow[];
  /** Le jeu ORDONNE courant, complet : ce qui alimente le curseur de fiche. */
  orderedIds: string[];
  /** Les colonnes du binding actif. */
  columns: ViewColumn[];
  /** Chargement, erreur, vide, tronque. */
  status: ViewStatus;

  /* Requete */
  query: ScreenQuery;
  setQuery(patch: Partial<ScreenQuery>): void;
  setSearch(value: string): void;
  /**
   * Le catalogue de filtres de la porte active, pret pour l'UI : les pastilles
   * V1 le passent tel quel a `ScreenLayout`, le popover v2 y lit en plus les
   * compteurs (calcules sur la porte brute, insensibles au filtrage courant).
   */
  filters: ScreenFilterOption[];
  /**
   * Lecture V1 : l'id quand EXACTEMENT UN filtre est actif, undefined sinon.
   * Les pastilles historiques s'allument avec ; les tags v2 lisent
   * `activeFilterIds`.
   */
  activeFilterId: string | undefined;
  /** Lecture V1 : REMPLACE l'etat par ce seul filtre (la pastille a choix unique). */
  setFilter(id: string): void;
  /* ---- Multi-filtres additifs (gabarit v2) ---- */
  /** Les filtres actifs, dans l'ordre de pose : ce que les tags 4a rendent. */
  activeFilterIds: string[];
  /** Pose ou retire un filtre du catalogue (popover 3b). Id inconnu : rien. */
  toggleFilter(id: string): void;
  /** Retire UN filtre (la croix d'un tag 4a). Absent : rien. */
  removeFilter(id: string): void;
  /** Retire tous les filtres (l'entree de sortie du popover, "Tout le parc"). */
  clearFilters(): void;
  /* ---- Tri en pile (gabarit v2) ---- */
  /** La pile de tri, ORDONNEE (= `query.sortStack`) : ce que les tags de tri rendent. */
  sortStack: SortLevel[];
  /**
   * Un clic du popover de tri : AJOUTE un niveau ascendant en bout de pile si
   * la cle n'y est pas, la RETIRE sinon. L'ordre des clics est l'ordre des
   * niveaux : rien a glisser.
   */
  toggleSort(key: string): void;
  /** Inverse le sens d'UN niveau (le corps d'un tag de tri, le second clic d'un en-tete). */
  invertSort(key: string): void;
  /** Retire UN niveau (la croix d'un tag de tri). Absent : rien. */
  removeSort(key: string): void;
  /** Vide la pile : retour a l'ordre du chargeur ("Ordre d'origine"). */
  clearSort(): void;
  /**
   * Vide les filtres ET la pile de tri EN UN SEUL RENDU : le mot "Effacer
   * filtres" de la zone 4a. Ce n'est PAS `clearFilters()` suivi de
   * `clearSort()` : deux setState en cascade recalculent la liste deux fois
   * (elle clignote) et reecrivent l'adresse deux fois. Rien d'autre ne bouge :
   * ni la recherche, ni la porte, ni la fiche ouverte.
   *
   * IL ETEINT AUSSI LE MODE ARCHIVES (chantier 2_0) : "tout effacer" veut dire
   * tout. Le mode pose un tag comme les autres dans le tiroir, et laisser
   * derriere lui le seul tag que la croix de tout effacer ne retire pas
   * ferait un tiroir qui refuse de se vider.
   */
  clearAll(): void;
  /* ---- Le MODE ARCHIVES (gabarit v2.2, chantier 2_0) ---- */
  /**
   * LE MODE EST-IL ALLUME. Allume, l'ecran montre les fiches archivees et RIEN
   * d'autre ; eteint, on n'en voit aucune.
   *
   * CE N'EST PAS UN FILTRE, c'est un ETAT A PART de l'ecran, et c'est la seule
   * chose a retenir : il n'entre pas dans `activeFilterIds`, son test passe
   * AVANT eux, et les filtres poses NE S'APPLIQUENT PAS a l'interieur des
   * archives (une archive est hors du travail courant : la trancher par
   * statut n'aurait pas de sens). La surface le montre en grisant les autres
   * entrees du popover ; le moteur, lui, se contente de ne pas les appliquer.
   */
  archivesMode: boolean;
  /**
   * Allumer / eteindre le mode. Il pose (ou retire) son tag en zone 4, ouvre
   * le tiroir au premier tag pose exactement comme un filtre, et voyage dans
   * l'adresse (`arch=1`). Il s'eteint aussi tout seul a `clearAll()` et au
   * changement de porte.
   */
  setArchivesMode(on: boolean): void;
  /**
   * LES GESTES RARES DE LA FICHE (menu "..." de la rangee 3c, niveau record) :
   * Archiver / Desarchiver et Supprimer, construits depuis les verbes de la
   * porte (`archive`, `unarchive`, `remove`) avec leur repli sur les options
   * de meme nom de l'ecran.
   *
   * RIEN A CABLER : `tools={screen}` les porte, `ScreenLayout` les lit dans
   * `ScreenToolset`. C'est ce qui tient la promesse du chantier, "dans toutes
   * les apps, sur toutes les portes, sans qu'aucune app n'ecrive une ligne" :
   * tant qu'ils n'arrivaient que par une prop explicite, ils etaient ABSENTS
   * (et non grises) sur toute app qui n'y avait pas pense, ce qui est
   * exactement le bouton qui apparait et disparait selon l'app que le brief
   * interdit. La prop de meme nom de `ScreenLayout` ne reste qu'une SURCHARGE.
   *
   * `recordMenuActions` et pas `recordActions` : a cote de `quickActions` et
   * `viewActions`, qui vivent tous deux dans la rangee 3a, un troisieme
   * `*Actions` qui atterrit dans le menu "..." de la 3c induit en erreur. Le
   * nom dit OU ca sort.
   *
   * Trois garanties tenues ici, et nulle part ailleurs :
   * - une porte qui ne declare pas le verbe rend l'entree GRISEE avec sa
   *   raison complete (G16), jamais absente ;
   * - "Archiver" DEVIENT "Desarchiver" quand la fiche ouverte est archivee :
   *   une entree, deux sens, jamais deux dont une grisee ;
   * - apres l'action, le core referme la fiche et recharge la porte ;
   *   l'ecriture rend compte par `saveState`, et un echec se rejoue par
   *   `retrySave` comme n'importe quelle autre ecriture.
   * Tableau VIDE quand aucune fiche n'est ouverte : il n'y a alors rien sur
   * quoi agir, et le menu n'est de toute facon rendu qu'au niveau record.
   */
  recordMenuActions: ScreenAction[];
  /* ---- Le tiroir de la zone 4 (tags + favoris) ---- */
  /**
   * Le tiroir est-il deplie. Il s'ouvre TOUT SEUL au premier tag pose (jamais
   * aux suivants : refermer a la main reste respecte), l'etoile (3b) le
   * commande ensuite, et il se referme au passage d'une porte. Escape et le
   * clic dehors ferment les popovers, JAMAIS lui.
   */
  drawerOpen: boolean;
  /** L'etoile : ouvre / referme le tiroir a la demande. */
  setDrawerOpen(open: boolean): void;
  /* ---- Favoris (gabarit v2, zone 4b + 4c) ---- */
  /**
   * Les favoris de la porte active : ses presets d'abord, puis ceux que
   * l'utilisateur a gardes (pref locale par app+ecran+porte). Vide quand la
   * porte declare `sections: false`.
   */
  favorites: ScreenFavorite[];
  /**
   * Le favori qui correspond EXACTEMENT a l'etat courant (memes filtres, sans
   * ordre ; meme pile de tri, ordonnee), ou null. C'est lui que la zone 4c
   * allume.
   */
  activeFavoriteId: string | null;
  /** Applique un favori : pose ses filtres et sa pile de tri. Id inconnu : rien. */
  applyFavorite(id: string): void;
  /**
   * Garde l'etat courant en favori, sous un nom auto ("Favori 1", "Favori 2",
   * premier numero libre). Sans effet quand `canKeepFavorite` est faux.
   */
  keepFavorite(): void;
  /**
   * Le bouton 4b est-il actif : il y a un etat a garder (au moins un tag) ET
   * cet etat n'est pas deja un favori. Faux = bouton desactive, avec sa raison
   * en title (le gabarit la formule).
   */
  canKeepFavorite: boolean;
  /** Oublie un favori GARDE par l'utilisateur. Les presets de la porte, eux, ne s'oublient pas. */
  forgetFavorite(id: string): void;
  /* ---- Porte active ---- */
  /**
   * La porte active, RESOLUE : id, libelle, placeholder, actions (3a), stats
   * (2c), et si elle se tranche (`sections`). L'ecran la passe au gabarit sans
   * rien recalculer ; changer de porte change donc actions et placeholder par
   * construction. Ecran sans portes : la porte implicite, neutre.
   */
  door: ScreenDoorState;
  /**
   * La SECTION active (zone 1) : l'id de PORTE quand l'ecran en declare, la
   * section libre d'avant sinon. A passer a `ScreenLayout.activeMenuId`.
   * `null` quand l'ecran n'a pas de menu.
   */
  section: string | null;
  /**
   * Change de section : a passer a `ScreenLayout.onMenuSelect`. Recharge (la
   * section est un parametre de requete) et REFERME la fiche ouverte, qui
   * n'appartient pas forcement a la nouvelle section. AVEC des portes, c'est
   * LE changement de porte : recherche, filtres, tri, selection et tiroir
   * sont remis a zero en plus.
   */
  setSection(id: string): void;

  /* ---- SELECTION MULTIPLE ET ACTIONS EN LOT (chantier 3_0) ---- */
  /**
   * LES LIGNES CHOISIES, dans l'ordre ou elles ont ete prises.
   *
   * L'INVARIANT, le plus important de ce chantier : cette liste ne contient
   * QUE des ids AFFICHES. Une ligne qui sort de l'ecran (filtre, recherche,
   * rechargement, mode Archives) en sort dans le MEME rendu, et le compte
   * suit. Une selection invisible est une bombe : on croit agir sur trois
   * fiches et on en modifie douze.
   *
   * VIDE hors tableau, toujours (voir `selectionLevel`).
   */
  selection: string[];
  /**
   * Poser la selection : la liste COMPLETE voulue, jamais un delta. Le core
   * la repasse par l'invariant, donc un id qu'on ne voit pas n'y entre pas.
   */
  setSelection(ids: string[]): void;
  /** Vider la selection : la croix de la barre de lot, et `Echap`. */
  clearSelection(): void;
  /**
   * QUELLE SURFACE PORTE LA SELECTION, ou `null` quand aucune ne la porte.
   *
   * `'table'` en vue tableau, `null` PARTOUT AILLEURS : ni liste, ni kanban,
   * ni calendrier, ni fiche. Ce n'est pas une limite d'implementation, c'est
   * un arbitrage : agir sur un lot demande de VOIR les valeurs sur lesquelles
   * on agit, et le tableau est la seule vue qui les montre toutes. Choisir
   * douze fiches dans un kanban, c'est choisir douze titres.
   *
   * COROLLAIRE : QUITTER LE TABLEAU ANNULE LA SELECTION, au meme titre que
   * changer de porte ou de filtre. Une selection gardee en sommeil derriere
   * une vue qui ne la montre pas EST une selection invisible.
   */
  selectionLevel: 'table' | null;
  /**
   * LES CHAMPS MODIFIABLES de la vue active : ce que la fenetre de lot
   * propose. Vide = rien ne s'ecrit ici, et ca se DIT (voir
   * `bulkAvailability.edit`) ; la fenetre ne s'ouvre pas vide.
   */
  bulkEditFields: BulkEditField[];
  /**
   * LES TROIS PRIMITIVES A DOCUMENT UNIQUE. Elles ecrivent UN document et
   * rendent la main, rien de plus : ni boucle, ni progression, ni compte
   * rendu, ni rejeu (tout cela appartient au moteur de lot, qui les appelle).
   *
   * ELLES NE RATTRAPENT AUCUNE ERREUR : elle remonte telle quelle, pour que
   * l'appelant puisse NOMMER la fiche tombee. Une primitive qui avale son
   * echec rend un lot a moitie ecrit indiscernable d'un lot reussi.
   *
   * Elles ne posent PAS d'ecriture optimiste, et c'est un choix : sinon les
   * lignes modifiees quitteraient la vue une par une PENDANT l'ecriture, et
   * l'invariant les retirerait du lot en cours de route. Le rechargement de
   * la porte a la fin du lot fait le rendu complet, d'un seul coup.
   */
  archiveOne(id: string): Promise<void>;
  removeOne(id: string): Promise<void>;
  /**
   * Ecrire UN champ sur UN document. `fieldKey` est la cle OPAQUE d'un
   * `BulkEditField`, rendue telle quelle (elle porte son espace de cles).
   */
  writeCell(id: string, fieldKey: string, value: unknown): Promise<void>;
  /**
   * CE QUE LA PORTE SAIT FAIRE, par geste : `null` quand il est disponible,
   * sinon LA RAISON, ecrite pour celui qui survole le bouton et pas pour
   * celui qui a ecrit l'app (regle G16 : grise avec sa raison, jamais cache).
   */
  bulkAvailability: { archive: string | null; remove: string | null; edit: string | null };

  /* Vues */
  /** Les entrees du selecteur, grisees avec leur raison quand elles ne sont pas satisfaites. */
  views: ViewOption[];
  viewId: ViewId;
  setView(id: ViewId): void;
  /** La derniere vue de niveau collection : ou la puce de retour ramene. */
  lastCollectionViewId: ViewId;
  /** Le descripteur de la vue active. */
  activeType: ViewType | null;
  /** Le sac libre du binding actif (`ViewBinding.options`). */
  viewOptions: Record<string, unknown>;
  /**
   * CE QUE LA VUE ACTIVE RECOIT, assemble UNE fois par le core. Le `ViewHost`
   * le passe tel quel a la vue ; il sert aussi a calculer `viewActions`.
   */
  viewProps: ViewProps;
  /**
   * Les actions que la vue active propose a la rangee 3a (`ViewType.actions`),
   * deja forcees en SECONDAIRE : le bouton lime unique reste la propriete de
   * l'ecran. A passer a `ScreenLayout` (prop `viewActions`), qui les rend sous
   * les `quickActions` de l'ecran. Tableau vide quand la vue n'en declare pas.
   */
  viewActions: ScreenAction[];

  /* Fiche */
  openId: string | null;
  openRecord(id: string | null): void;
  cursor: Cursor;
  /**
   * MARQUER UNE FICHE LUE sur ce poste, sans l'ouvrir. Meme memoire, meme
   * regle de file, meme porte que `openRecord` : c'est le meme geste, sans
   * l'affichage.
   *
   * A APPELER APRES UNE CREATION FAITE DEPUIS L'ECRAN, juste avant `reload()`.
   * Le modele est la boite mail : un message qu'on a envoye soi-meme ne
   * s'affiche pas comme non lu. Sans cet appel, la fiche qu'on vient de saisir
   * revient du serveur avec son lisere et sa pastille, et l'utilisateur doit
   * l'ouvrir pour eteindre ce qu'il a lui-meme ecrit.
   *
   * Sans effet quand l'id est vide ou deja le dernier lu.
   */
  markRead(id: string): void;

  /* Ecriture */
  /**
   * Ecrire un ROLE. Signature FIGEE : le kanban l'appelle depuis un
   * glisser-deposer (`onPatchRecord(id, 'groupKey', valeur)`).
   */
  patchRecord(id: string, role: string, value: Cell): Promise<void>;
  /** Ecrire une COLONNE. Meme optimisme, meme rollback, meme pile d'annulation. */
  patchColumn(id: string, key: string, value: Cell): Promise<void>;
  /**
   * L'etat d'enregistrement de l'ecran, a passer a `ScreenLayout` : il le rend
   * dans la rangee 3, au niveau `record` uniquement.
   */
  saveState: SaveState;
  /** Rejoue la DERNIERE ecriture echouee, telle quelle. Sans echec en attente : rien. */
  retrySave(): void;
  /**
   * Annule / retablit le dernier pas. Une annulation est une ECRITURE
   * ORDINAIRE : meme chemin, meme optimisme, meme rollback, jamais un mecanisme
   * parallele.
   */
  undo(): void;
  redo(): void;
  canUndo: boolean;
  canRedo: boolean;

  /* Etat jetable, cloisonne par vue */
  viewState: unknown;
  setViewState(state: unknown): void;

  /* Divers */
  optionsOf(role: string): Option[];
  /**
   * Le mode de chargement effectif. Lu par le `ViewHost` pour formuler son
   * bandeau : "affinez la recherche" (client, tout est charge, on en cache) et
   * "il reste des pages" (serveur, la recherche ne porte que sur le charge) ne
   * disent pas la meme chose a l'utilisateur.
   */
  pagination: 'client' | 'server';
  reload(): void;
  loadMore(): void;
  setRange(from: Date, to: Date): void;
  /** La zone 5 : a passer a `ScreenLayout` (prop `scrollRef`), pretee aux vues. */
  scrollRef: RefObject<HTMLElement | null>;

  /**
   * L'ETAT D'ECRAN VU COMME UNE ADRESSE, et l'inverse. Branches tout seuls sur
   * le routeur quand le shell fournit le port d'URL (screenUrl.tsx) : un ecran
   * n'appelle ni l'un ni l'autre, ils restent exposes pour les tests et pour
   * une surface qui voudrait fabriquer un lien.
   */
  toSearch(): Record<string, string>;
  fromSearch(params: Record<string, string | undefined>): void;
}

/* ------------------------------------------------------------------ */
/* Outils internes                                                     */
/* ------------------------------------------------------------------ */

/** Un id de vue valide est namespace. Protege des valeurs venues du storage. */
function asViewId(value: unknown): ViewId | null {
  return typeof value === 'string' && value.includes('.') ? (value as ViewId) : null;
}

interface ScreenSession {
  viewId?: string;
  openId?: string | null;
  lastCollectionViewId?: string;
}

/** Lecture defensive du sessionStorage : indisponible = pas de reprise, jamais d'exception. */
function readSession(key: string): ScreenSession {
  try {
    const raw = window.sessionStorage.getItem(SESSION_PREFIX + key);
    return raw == null ? {} : (JSON.parse(raw) as ScreenSession);
  } catch {
    return {};
  }
}

function writeSession(key: string, value: ScreenSession): void {
  try {
    window.sessionStorage.setItem(SESSION_PREFIX + key, JSON.stringify(value));
  } catch {
    // Storage indisponible (mode prive, quota) : on n'ecrit pas, sans casser.
  }
}

/** Suppression logique du socle : `archivedAt` non nul. Les vues ne voient jamais un archive. */
function isArchived(record: unknown): boolean {
  return (record as { archivedAt?: unknown } | null)?.archivedAt != null;
}

/**
 * Deux memoires du "nouveau" disent-elles la meme chose ? Relire le stockage
 * fabrique un objet neuf a chaque fois : sans cette comparaison, chaque event
 * de pref (y compris ceux des autres cles) ferait reprojeter tout l'ecran.
 */
function sameNewMemory(a: NewMemory, b: NewMemory): boolean {
  return (
    a.since === b.since &&
    a.seen.length === b.seen.length &&
    a.seen.every((id, index) => id === b.seen[index])
  );
}

/**
 * Empile une page sur les precedentes SANS doublon.
 *
 * Le dedoublonnage n'est pas de la prudence gratuite : une ecriture concurrente
 * peut deplacer une ligne d'une page a l'autre entre deux appels, et la meme
 * ligne reviendrait deux fois. Deux lignes de meme `id` dans le jeu, et le
 * curseur de fiche ("3 / 47") se met a mentir.
 */
function mergeById<T>(previous: T[], page: T[], getId: (record: T) => string): T[] {
  if (page.length === 0) return previous;
  const seen = new Set(previous.map(getId));
  const fresh = page.filter((record) => !seen.has(getId(record)));
  return fresh.length === 0 ? previous : [...previous, ...fresh];
}

/**
 * Le curseur de pagination pointe un document disparu (`CursorLostError` du
 * SDK). Reconnu par le NOM et non par `instanceof` : `@taki/ui` ne depend pas
 * de `@taki/sdk` et ne doit pas commencer maintenant (le design system ne
 * connait pas la couche donnees, c'est ce qui lui permet de tourner dans
 * ui-lab sans Firebase). Le nom fait partie du contrat, il est teste cote SDK.
 */
function isCursorLost(cause: unknown): boolean {
  return (cause as { name?: unknown } | null)?.name === 'CursorLostError';
}

/** Deux tris identiques ? Sert a ne pas repartir de la page 1 pour rien. */
function sameSort(a: ScreenQuery['sort'], b: ScreenQuery['sort']): boolean {
  if (a == null || b == null) return a == b;
  return a.key === b.key && a.dir === b.dir;
}

/** Deux intervalles identiques ? Meme role que `sameSort`. */
function sameRange(a: ScreenQuery['range'], b: ScreenQuery['range']): boolean {
  if (a == null || b == null) return a == b;
  return a.from.getTime() === b.from.getTime() && a.to.getTime() === b.to.getTime();
}

/**
 * LE SEUL ENDROIT OU LA REQUETE CHANGE, `loadMore` mis a part (qui, lui, pose
 * le curseur au lieu de l'annuler). Deux responsabilites, dans cet ordre :
 *
 * 1. LES MIROIRS. `filterIds` / `sortStack` sont l'etat canonique,
 *    `filterId` / `sort` leurs lectures V1 (contract.ts). Chaque patch, d'ou
 *    qu'il vienne (les mutations v2 patchent le canonique, l'en-tete du
 *    tableau patche encore `sort`, un ecran V1 peut patcher `filterId`), est
 *    renormalise ICI : les deux paires ne peuvent pas diverger puisqu'une
 *    seule fonction les ecrit. Le canonique gagne quand un patch porte les
 *    deux a la fois.
 *
 * 2. LA PEREMPTION (mode serveur). Tout champ que le CHARGEUR exploite perime
 *    les pages deja empilees : les changer sans remettre `after` a null
 *    ferait empiler la page suivante d'une requete sous les pages d'une autre
 *    (une page du mois de mars sous celles de fevrier, une page du nouveau
 *    tri sous celles de l'ancien). Cette liste doit donc rester alignee sur
 *    `loadKey` : un champ ajoute a l'un sans l'autre produit exactement ce
 *    melange, en silence. Le tri s'y juge sur le MIROIR (le niveau 1, seul
 *    parametre serveur) : empiler ou inverser un niveau 2 ne perime rien, il
 *    raffine en memoire.
 */
function nextQuery(
  previous: ScreenQuery,
  patch: Partial<ScreenQuery>,
  server: boolean,
): ScreenQuery {
  const next = { ...previous, ...patch };
  if ('filterIds' in patch) {
    next.filterId = next.filterIds.length === 1 ? (next.filterIds[0] ?? null) : null;
  } else if ('filterId' in patch) {
    next.filterIds = next.filterId == null ? [] : [next.filterId];
  }
  if ('sortStack' in patch) {
    next.sort = next.sortStack[0] ?? null;
  } else if ('sort' in patch) {
    next.sortStack = next.sort == null ? [] : [next.sort];
  }
  if (!server) return next;
  const stale =
    (('sort' in patch || 'sortStack' in patch) && !sameSort(previous.sort, next.sort)) ||
    ('range' in patch && !sameRange(previous.range, next.range)) ||
    ('section' in patch && previous.section !== next.section) ||
    ('includeArchived' in patch && previous.includeArchived !== next.includeArchived) ||
    // Le MODE ARCHIVES change le JEU que le chargeur rend : empiler la page
    // suivante des archives sous les pages des actifs melangerait deux
    // collections que rien a l'ecran ne distingue.
    ('archivesMode' in patch && previous.archivesMode !== next.archivesMode) ||
    ('limit' in patch && previous.limit !== next.limit);
  if (stale) next.after = null;
  return next;
}

/** Deux listes d'ids identiques, ORDRE COMPRIS (l'ordre des tags est l'ordre de pose). */
function sameIdList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id, index) => b[index] === id);
}

/**
 * L'etat courant correspond-il a ce favori. Les filtres se comparent EN
 * ENSEMBLE (poser Camera puis Optique ou l'inverse, c'est le meme etat : un
 * OU n'a pas d'ordre), la pile de tri EN SEQUENCE (le niveau 1 commande le
 * niveau 2, l'inverser n'est pas le meme ordre).
 */
function matchesFavorite(
  favorite: ScreenFavorite,
  filterIds: string[],
  sortStack: SortLevel[],
): boolean {
  return (
    favorite.filterIds.length === filterIds.length &&
    favorite.filterIds.every((id) => filterIds.includes(id)) &&
    favorite.sortStack.length === sortStack.length &&
    favorite.sortStack.every(
      (level, index) =>
        sortStack[index]?.key === level.key && sortStack[index]?.dir === level.dir,
    )
  );
}

/**
 * Relecture DEFENSIVE d'une liste de favoris venue du localStorage : une pref
 * est du JSON que n'importe quelle version passee de l'app a pu ecrire, on ne
 * lui fait pas confiance. Une entree malformee est JETEE en silence (le favori
 * est un confort, pas une donnee) plutot que de faire planter l'ecran entier.
 */
function sanitizeFavorites(value: unknown): ScreenFavorite[] {
  if (!Array.isArray(value)) return [];
  const kept: ScreenFavorite[] = [];
  for (const item of value) {
    const raw = item as {
      id?: unknown;
      label?: unknown;
      filterIds?: unknown;
      sortStack?: unknown;
    } | null;
    if (raw == null || typeof raw.id !== 'string' || typeof raw.label !== 'string') continue;
    if (!Array.isArray(raw.filterIds) || !Array.isArray(raw.sortStack)) continue;
    if (!raw.filterIds.every((id) => typeof id === 'string')) continue;
    const stack: SortLevel[] = [];
    let broken = false;
    for (const level of raw.sortStack) {
      const cast = level as { key?: unknown; dir?: unknown } | null;
      if (cast == null || typeof cast.key !== 'string' || (cast.dir !== 'asc' && cast.dir !== 'desc')) {
        broken = true;
        break;
      }
      stack.push({ key: cast.key, dir: cast.dir });
    }
    if (broken) continue;
    kept.push({
      id: raw.id,
      label: raw.label,
      filterIds: raw.filterIds as string[],
      sortStack: stack,
    });
  }
  return kept;
}

/**
 * Retire UN patch optimiste du sac, une fois que la donnee fraiche a pris le
 * relais pour cette cible. Laisser l'overlay en place le ferait survivre a la
 * verite : il masquerait la prochaine valeur venue du serveur.
 */
function dropPatch(
  bag: Record<string, Record<string, Cell>>,
  id: string,
  key: string,
): Record<string, Record<string, Cell>> {
  const rowBag = bag[id];
  if (rowBag == null || !(key in rowBag)) return bag;
  const nextRow = { ...rowBag };
  delete nextRow[key];
  const next = { ...bag };
  if (Object.keys(nextRow).length === 0) delete next[id];
  else next[id] = nextRow;
  return next;
}

/** Les roles d'un binding, lus sans le mensonge de type de l'intersection. */
function rolesOf<T>(binding: ViewBinding<T> | undefined): Record<string, FieldMap<T> | undefined> {
  return (binding?.roles ?? {}) as Record<string, FieldMap<T> | undefined>;
}

/** Les deux cibles d'ecriture. Deux espaces de cles distincts, jamais melanges. */
type WriteKind = 'role' | 'column';

/** Une demande d'ecriture, telle qu'elle traverse le hook. */
interface WriteRequest {
  kind: WriteKind;
  id: string;
  key: string;
  value: Cell;
}

/**
 * UN PAS D'ANNULATION : de quoi refaire le chemin inverse par le MEME
 * `commitWrite`. On garde l'`id` parce qu'annuler une modification faite sur une
 * autre fiche doit d'abord y ramener.
 */
interface WriteStep extends WriteRequest {
  /** La valeur d'avant : ce que `undo` reecrit. */
  before: Cell;
}

/** La cle du jeton de sequence : une ecriture n'annule que SA propre cible. */
function writeKeyOf(kind: WriteKind, id: string, key: string): string {
  return `${kind}:${id}:${key}`;
}

/**
 * LES TROIS GESTES DE FICHE du chantier 2_0 : ils portent sur le DOCUMENT
 * entier, pas sur une case, d'ou un chemin a part de `commitWrite` (aucune
 * valeur d'avant a retenir, donc rien a annuler : archiver n'entre pas dans la
 * pile d'annulation, elle est faite pour les cellules).
 */
type RecordActionKind = 'archive' | 'unarchive' | 'remove';

/**
 * La raison G16 d'un geste que la porte ne declare pas. Le bouton reste LA,
 * grise, avec sa raison : un geste qui apparait et disparait selon l'app
 * fabrique exactement le "pourquoi je ne peux pas supprimer ici" qu'on veut
 * eviter.
 *
 * ELLE S'ADRESSE A CELUI QUI SURVOLE LE BOUTON, pas a celui qui a ecrit l'app.
 * Le fondateur n'est pas developpeur, l'utilisateur final encore moins : une
 * infobulle qui lui demande de declarer une fonction lui apprend seulement que
 * le message n'est pas pour lui. Elle dit donc UN fait, celui qu'il peut
 * verifier a l'ecran : ce geste n'existe pas ici. La consigne technique, elle,
 * vit dans la jsdoc de `ScreenDoor.archive` et dans le `console.warn` du
 * chemin refuse, la ou un developpeur regarde.
 */
const RECORD_ACTION_REASON: Record<RecordActionKind, string> = {
  archive: "Cet ecran ne permet pas d'archiver ses fiches.",
  unarchive: 'Cet ecran ne permet pas de desarchiver ses fiches.',
  remove: 'Cet ecran ne permet pas de supprimer ses fiches.',
};

/* ------------------------------------------------------------------ */
/* La SELECTION et les actions en LOT (chantier 3_0)                   */
/* ------------------------------------------------------------------ */

/**
 * LA SEULE VUE QUI PORTE LA SELECTION. Le test se fait sur l'id, en un seul
 * endroit, et c'est assume : il n'existe pas de "cette vue sait choisir" dans
 * le contrat, et en inventer un ferait croire qu'une vue custom n'a qu'a le
 * declarer pour recevoir une barre de lot qu'elle ne sait pas dessiner.
 *
 * La raison n'est pas technique : agir sur un lot demande de VOIR les valeurs
 * sur lesquelles on agit, et le tableau est la seule vue qui les montre toutes.
 */
const SELECTION_VIEW_ID: ViewId = 'core.tableau';

/**
 * LA SELECTION VIDE, d'identite STABLE. Rendre un `[]` neuf a chaque fois
 * ferait repartir tous les memos qui en dependent a chaque frappe de
 * recherche. NE JAMAIS LA MUTER.
 */
const NO_SELECTION: string[] = [];

/**
 * Le delai apres lequel un lot termine recharge la porte.
 *
 * Le lot ecrit un document a la fois : entre deux, le compteur d'ecritures en
 * vol repasse par zero. Recharger la porte a ce moment-la la rechargerait
 * douze fois pour douze fiches. On attend donc un souffle apres la DERNIERE
 * ecriture ; toute ecriture qui repart dans cette fenetre annule l'attente.
 * Cale large : ce qui se joue entre deux fiches d'un meme lot est un rendu
 * React, jamais un dixieme de seconde.
 */
const BULK_RELOAD_DELAY_MS = 400;

/**
 * Les raisons G16 des gestes de lot que la porte ne sait pas rendre. Les deux
 * verbes manquants reprennent MOT POUR MOT celles de la fiche
 * (`RECORD_ACTION_REASON`) : c'est le meme fait, il ne se raconte pas de deux
 * facons selon l'endroit ou on le lit.
 */
const BULK_REASON = {
  /** Mode Archives : elles y sont deja, l'archivage n'aurait rien a faire. */
  alreadyArchived: 'Ces fiches sont deja archivees.',
  /** Aucune colonne ni aucun role de la vue ne porte d'ecriture. */
  noEditableField: 'Aucun champ de ce tableau ne se modifie.',
} as const;

/** Le nom francais d'un type d'editeur, annonce DANS l'option du champ. */
const EDITOR_LABEL: Record<EditorKind, string> = {
  text: 'texte',
  multiline: 'texte long',
  number: 'nombre',
  date: 'date',
  select: 'liste de choix',
};

/**
 * Les roles MULTI-VALUES, exclus du lot. Un lot ecrit UNE valeur, et le patch
 * de role n'en garde qu'une : l'appliquer a un role qui en porte plusieurs
 * effacerait les autres en silence, sur douze fiches d'un coup. La fiche paie
 * deja cette lecon (elle refuse d'editer un couloir multi-value) ; ici on ne
 * peut meme pas juger ligne par ligne, puisque la valeur est saisie une fois
 * pour tout le lot.
 */
const BULK_EXCLUDED_ROLES = new Set(['badges', 'lane']);

/** Libelles de repli des roles du core, quand l'ecran n'en declare pas. */
const BULK_ROLE_LABEL: Record<string, string> = {
  title: 'Nom',
  subtitle: 'Sous-titre',
  avatar: 'Image',
  status: 'Statut',
  groupKey: 'Groupe',
  dateStart: 'Debut',
  dateEnd: 'Fin',
};

/*
 * LA CLEF D'UN CHAMP DE LOT est celle de la FICHE, la meme fonction et pas une
 * copie : `fieldKeyOf`, importee de `core/CellEditor.js` en tete de fichier.
 * `WriteKind` ('role' | 'column') est exactement son premier parametre.
 */

/**
 * Les DEUX PREFIXES, lus de la fabrique elle-meme et jamais reecrits a la main.
 * C'est ce qui rend la lecture inverse INCAPABLE de diverger : changer la forme
 * dans `fieldKeyOf` change les deux prefixes ici, dans le meme geste. Ecrits en
 * clair, ils auraient rejoue le defaut qu'on vient de fermer, un cran plus loin.
 */
const ROLE_KEY_PREFIX = fieldKeyOf('role', '');
const COLUMN_KEY_PREFIX = fieldKeyOf('column', '');

/**
 * La lecture inverse. Une clef SANS prefixe est acceptee et lue comme une
 * COLONNE d'abord, un role ensuite (meme priorite que `sortValueOf`) : un
 * appel ecrit a la main ne doit pas echouer sur une convention interne.
 */
function parseBulkFieldKey(fieldKey: string): { kind: WriteKind | null; key: string } {
  if (fieldKey.startsWith(ROLE_KEY_PREFIX)) {
    return { kind: 'role', key: fieldKey.slice(ROLE_KEY_PREFIX.length) };
  }
  if (fieldKey.startsWith(COLUMN_KEY_PREFIX)) {
    return { kind: 'column', key: fieldKey.slice(COLUMN_KEY_PREFIX.length) };
  }
  return { kind: null, key: fieldKey };
}

/**
 * La valeur saisie, ramenee dans le vocabulaire pauvre qui traverse la
 * frontiere core / ecran (`Cell`). Une valeur d'un autre genre (un objet, une
 * fonction) est REFUSEE plutot qu'ecrite : elle ne passerait de toute facon
 * pas la validation Zod du SDK, mais elle le ferait une fiche sur douze et
 * sans dire laquelle.
 */
function asCell(value: unknown): Cell {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (value instanceof Date) return value;
  throw new Error("Cette valeur ne peut pas etre enregistree telle quelle.");
}

/**
 * Les CATALOGUES des colonnes du binding actif, par clef, resolus UNE fois.
 * Ils servent a traduire une valeur en libelle dans les patches optimistes :
 * sans eux, une colonne a catalogue affiche sa valeur stockee le temps de
 * l'aller-retour serveur, puis son libelle au rechargement, ce qui clignote a
 * chaque choix.
 *
 * Seules les colonnes qui declarent `options()` sont resolues : on n'appelle
 * jamais le catalogue d'une colonne que personne n'edite.
 */
function columnCatalogs<T>(binding: ViewBinding<T> | undefined): Record<string, Option[]> {
  const catalogs: Record<string, Option[]> = {};
  for (const column of binding?.columns ?? []) {
    if (typeof column.options === 'function') catalogs[column.key] = column.options();
  }
  return catalogs;
}

/**
 * Rejoue les deux sacs de patches sur une ligne deja projetee. Le memo
 * `projected` le fait pour la fenetre courante ; `openRow` en a besoin pour ses
 * deux replis, qui reprojettent depuis le record BRUT.
 */
function replayPatches<T>(
  row: ProjectedRow,
  rolePatches: Record<string, Cell> | undefined,
  cellPatches: Record<string, Cell> | undefined,
  roles: Record<string, FieldMap<T> | undefined>,
  catalogs: Record<string, Option[]>,
): ProjectedRow {
  let patched = row;
  for (const [role, value] of Object.entries(rolePatches ?? {})) {
    patched = applyRolePatch(patched, role, value, roles[role]);
  }
  for (const [key, value] of Object.entries(cellPatches ?? {})) {
    patched = applyColumnPatch(patched, key, value, catalogs[key]);
  }
  return patched;
}

/**
 * La valeur COURANTE d'une cible, patch optimiste compris. C'est le `before`
 * d'un pas d'annulation : le record en memoire porte encore la valeur d'avant
 * la derniere ecriture optimiste, s'y fier ferait remonter une valeur perimee.
 * Lecture par `in` et non par `??` : `null` est une valeur legitime.
 */
function currentValueOf(
  bag: Record<string, Record<string, Cell>>,
  id: string,
  key: string,
  fallback: Cell,
): Cell {
  const rowBag = bag[id];
  if (rowBag != null && key in rowBag) return rowBag[key] as Cell;
  return fallback;
}

/* ------------------------------------------------------------------ */
/* Le hook                                                             */
/* ------------------------------------------------------------------ */

export function useScreen<T>(options: UseScreenOptions<T>): ScreenState<T> {
  const { appId, screenId } = options;
  const sessionKey = `${appId}.${screenId}`;
  const pagination = options.pagination ?? 'client';
  const limitDefault =
    options.limit ?? (pagination === 'server' ? DEFAULT_PAGE_SIZE : DEFAULT_LIMIT);
  // Le mode est lu dans des callbacks stables (`loadMore`, `setQuery`, le
  // chemin d'ecriture) : par ref, pour ne pas les recreer a chaque rendu.
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;

  // Les callbacks et tableaux fournis par l'ecran sont recrees a chaque rendu :
  // on les lit par ref pour ne jamais les mettre en dependance d'effet.
  //
  // ATTENTION, DEUX TEMPS D'ASSIGNATION : ici les valeurs d'ECRAN, puis, une
  // fois la requete declaree et la porte active resolue (plus bas), les
  // valeurs de PORTE prennent le dessus pour le reste du rendu. Tout ce qui
  // consomme ces refs (memos, effets, callbacks) vit apres ce second temps.
  const loadRef = useRef(options.load);
  loadRef.current = options.load;
  const getIdRef = useRef(options.getId);
  getIdRef.current = options.getId;
  const getOneRef = useRef(options.get);
  getOneRef.current = options.get;
  // Les TROIS VERBES de la fiche (chantier 2_0), meme patron et meme repli que
  // `load` et `get` : les valeurs d'ECRAN ici, celles de la PORTE plus bas.
  const archiveRef = useRef(options.archive);
  archiveRef.current = options.archive;
  const unarchiveRef = useRef(options.unarchive);
  unarchiveRef.current = options.unarchive;
  const removeRef = useRef(options.remove);
  removeRef.current = options.remove;
  const typesRef = useRef(options.types);
  typesRef.current = options.types;
  const bindingsRef = useRef(options.bindings);
  bindingsRef.current = options.bindings;
  const filterDefsRef = useRef(options.filters ?? []);
  filterDefsRef.current = options.filters ?? [];
  /** Les portes declarees. Tableau vide = pas de portes (ecran V1). */
  const doorsRef = useRef(options.doors ?? []);
  doorsRef.current = options.doors ?? [];
  const hasDoors = doorsRef.current.length > 0;
  /**
   * Les options d'ECRAN entieres, pour `openDoor` : les refs ci-dessus portent
   * les catalogues de la porte ACTIVE, or le changement de porte doit resoudre
   * ceux de la porte CIBLE (ses replis compris) dans le meme tick.
   */
  const optionsRef = useRef(options);
  optionsRef.current = options;

  /* ---- Vue par defaut : preference de l'utilisateur, puis reprise de session ---- */

  const [prefView, setPrefView] = usePref<string | null>(lastViewPrefKey(appId, screenId), null);
  const prefViewRef = useRef(prefView);
  prefViewRef.current = prefView;

  // Choix explicite de l'utilisateur dans les reglages de l'app. Il passe
  // devant la memoire automatique, jamais devant la navigation en cours.
  const [pinnedView] = usePref<string | null>(pinnedViewPrefKey(appId, screenId), null);
  const pinnedViewRef = useRef(pinnedView);
  pinnedViewRef.current = pinnedView;
  // `usePref` rend un setter neuf a chaque rendu : on le lit par ref pour
  // garder `setView` stable.
  const setPrefViewRef = useRef(setPrefView);
  setPrefViewRef.current = setPrefView;

  const [session] = useState<ScreenSession>(() => readSession(sessionKey));

  /* ---- L'ADRESSE, lue AVANT tout le reste ---- */

  // Le port d'URL, fourni par le shell. `null` hors shell, ET null pour le
  // SECOND etat d'ecran d'une meme route (une adresse ne decrit qu'un ecran,
  // voir screenUrl.tsx) : dans les deux cas, l'ecran garde exactement son
  // comportement d'avant ce chantier, en memoire.
  const urlPort = useScreenUrlOwnership(sessionKey);
  // L'adresse du PREMIER rendu, figee : c'est elle qui decide de l'etat de
  // depart. Les suivantes passent par l'effet de relecture plus bas.
  const initialSearchRef = useRef<ScreenSearch>(pickScreenSearch(urlPort?.params ?? {}));
  // "L'adresse dit quelque chose" se juge sur le JEU ENTIER, pas cle par cle.
  // Sinon un lien vers une collection (`?view=core.tableau`, sans `id`)
  // laisserait la reprise de session rouvrir la derniere fiche de CE
  // navigateur : le destinataire du lien ne verrait pas ce qu'on lui a envoye.
  const urlHasState = Object.keys(initialSearchRef.current).length > 0;
  const initialSearch = urlHasState ? initialSearchRef.current : null;

  /* ---- La PORTE DE DEPART, resolue une fois pour toutes ---- */

  /**
   * Contrairement a une section libre, une porte venue de l'adresse EST
   * verifiable : le catalogue vit ici, dans les options. Un `?s=` inconnu
   * (faute de frappe, porte retiree depuis) retombe donc sur la premiere
   * porte plutot que sur un ecran vide inexplicable, et c'est cette porte-la
   * qui fournit les catalogues (vues, filtres) dont l'etat de depart a besoin.
   * `null` = pas de portes : tout l'etat de depart se resout comme avant.
   */
  const initialDoorRef = useRef<ScreenDoor<T> | null>(null);
  const initialDoorResolvedRef = useRef(false);
  if (!initialDoorResolvedRef.current) {
    initialDoorResolvedRef.current = true;
    if (hasDoors) {
      const doors = doorsRef.current;
      const asked = initialSearch?.['s'] ?? options.defaultSection ?? null;
      initialDoorRef.current = doors.find((door) => door.id === asked) ?? doors[0] ?? null;
    }
  }
  const initialDoor = initialDoorRef.current;
  /** Le catalogue de vues du PREMIER rendu : celui de la porte de depart. */
  const initialTypes = initialDoor?.types ?? options.types;

  // Le catalogue de vues d'un ecran est fige : la vue collection de repli se
  // calcule une seule fois, au premier rendu (celui de la porte de depart ;
  // `openDoor` recalcule le sien au passage d'une porte).
  const firstCollectionRef = useRef<ViewId | null>(null);
  if (firstCollectionRef.current == null) {
    firstCollectionRef.current =
      initialTypes.find((type) => type.level === 'collection')?.id ??
      initialTypes[0]?.id ??
      FALLBACK_VIEW_ID;
  }
  const firstCollectionId = firstCollectionRef.current;

  /**
   * LA VUE DE DEPART, resolue une fois pour toutes.
   *
   * Toute vue candidate est verifiee contre le CATALOGUE de l'ecran, d'ou
   * qu'elle vienne (adresse, session, epinglage, pref). Une vue inconnue ne
   * doit jamais passer : `activeType` vaudrait null, le `ViewHost` n'aurait
   * rien a monter, et la zone 5 resterait BLANCHE, sans message ni erreur, avec
   * un selecteur ou rien n'est allume. Une faute de frappe dans un lien
   * (`?view=core.list`) suffisait, et la valeur se recopiait ensuite en session
   * et dans l'adresse : l'ecran blanc survivait au rechargement.
   */
  const startViewRef = useRef<ViewId | null>(null);
  if (startViewRef.current == null) {
    // Le catalogue de reference est celui de la PORTE DE DEPART : une vue de
    // session ou d'epinglage qui n'existe pas derriere cette porte est rejetee
    // ici, exactement comme une faute de frappe.
    const known = (candidate: string | null | undefined): ViewId | null => {
      const view = asViewId(candidate);
      return view != null && initialTypes.some((type) => type.id === view) ? view : null;
    };
    let resolved: ViewId | null = null;
    if (initialSearch != null) {
      // L'`id` FAIT FOI SUR LE NIVEAU : une adresse qui porte un element ouvre
      // la fiche, meme si `view` dit autre chose. `view` n'est honore ici que
      // s'il designe lui aussi une vue de niveau fiche (une app peut en avoir
      // une a elle). Le raccourci est ensuite REECRIT dans l'adresse par
      // l'effet d'ecriture : le lien qu'on recopie dit la verite.
      if (initialSearch['id'] != null) {
        const asked = initialTypes.find((type) => type.id === initialSearch['view']);
        resolved =
          asked?.level === 'record'
            ? asked.id
            : (initialTypes.find((type) => type.level === 'record')?.id ?? null);
      }
      resolved ??= known(initialSearch['view']);
    }
    resolved ??=
      known(session.viewId) ??
      known(pinnedViewRef.current) ??
      known(prefViewRef.current) ??
      known(initialDoor?.defaultView) ??
      options.defaultView ??
      firstCollectionId;
    startViewRef.current = resolved;
  }
  const startView = startViewRef.current;

  const [viewId, setViewIdState] = useState<ViewId>(startView);
  const [lastCollectionViewId, setLastCollectionViewId] = useState<ViewId>(() => {
    // La vue de depart est une COLLECTION : c'est elle la sortie de fiche, pas
    // la premiere vue declaree. Sans ca, un lien vers un kanban rendait une
    // puce de retour marquee "Liste", et refermer une fiche ouverte depuis ce
    // kanban posait l'utilisateur sur une vue qu'il n'avait jamais choisie.
    // `setView` n'est PAS passe par la (la vue de depart ne vient pas d'un
    // clic), donc personne d'autre ne l'ecrit.
    const startLevel = initialTypes.find((type) => type.id === startView)?.level;
    if (startLevel === 'collection') return startView;
    return asViewId(session.lastCollectionViewId) ?? firstCollectionId;
  });
  const [openId, setOpenId] = useState<string | null>(() =>
    initialSearch != null ? (initialSearch['id'] ?? null) : (session.openId ?? null),
  );

  /* ---- Requete ---- */

  /**
   * LES FILTRES DE DEPART, resolus une fois : ils nourrissent la requete ET le
   * tiroir (ouvert des l'arrivee quand un lien pose des tags : le destinataire
   * doit VOIR ce qui filtre la liste qu'on lui a envoyee, pas le deviner).
   *
   * Tout id venu de l'adresse est VERIFIE contre le catalogue : un `f` inconnu
   * ne filtrerait rien tout en s'affichant comme un etat actif, le pire des
   * deux mondes. Avec des portes, le catalogue est celui de la porte de depart
   * et la liste entiere est gardee (multi-filtres additifs, etat vide par
   * defaut). Sans portes, lecture V1 : UNE pastille active en permanence, le
   * premier id connu de la liste, sinon la pastille par defaut.
   */
  const initialFilterIdsRef = useRef<string[] | null>(null);
  if (initialFilterIdsRef.current == null) {
    const asked = splitFilterIds(initialSearch?.['f']);
    if (initialDoor != null) {
      const defs =
        initialDoor.sections === false ? [] : (initialDoor.filters ?? options.filters ?? []);
      initialFilterIdsRef.current = asked.filter((id) => defs.some((def) => def.id === id));
    } else {
      const defs = options.filters ?? [];
      const known = asked.find((id) => defs.some((def) => def.id === id));
      const fallback = options.defaultFilterId ?? defs[0]?.id ?? null;
      const single = known ?? fallback;
      initialFilterIdsRef.current = single == null ? [] : [single];
    }
  }
  const initialFilterIds = initialFilterIdsRef.current;

  /**
   * LE MODE ARCHIVES DE DEPART, lu dans l'adresse (`arch=1`). Il n'a rien a
   * verifier contre un catalogue (ce n'est pas un filtre) : toute valeur autre
   * que `1` retombe sur "eteint", c'est `readArchivesParam` qui le tient.
   *
   * L'ADRESSE FAIT FOI, ET ELLE SEULE : aucune reprise de SESSION, aucune
   * PREF, contrairement a la vue active et a la fiche ouverte. Un mode n'est
   * pas une preference, c'est un endroit ou l'on est.
   *
   * Y COMPRIS L'ADRESSE QUE LE DOCK RESTAURE. Le shell garde la derniere
   * adresse de chaque app visitee dans la page (`lastSearchRef`, TakiShell) et
   * y retourne directement : revenir dans une app par le dock rallume donc le
   * mode qu'on y avait laisse. C'est VOULU, c'est deja le comportement de la
   * fiche ouverte (`id`) et des filtres (`f`), restaures par cette meme
   * memoire, et le tag orange du tiroir dit ou l'on est des l'arrivee. Ce
   * n'est pas une reprise d'etat en douce : c'est une adresse, la meme que
   * celle qu'on aurait collee.
   *
   * SANS PORTES, IL EST IGNORE (chantier 2_0, correctif). Un ecran sans portes
   * ne passe pas `tools` au gabarit : il n'a ni outil filtre, ni tag, ni
   * tiroir, ni "tout effacer". Le mode s'y allumerait sans que RIEN a l'ecran
   * ne le montre ni ne puisse l'eteindre, et la liste resterait vide pour
   * toujours. Un mode qu'aucune surface ne peut eteindre ne doit pas pouvoir
   * s'allumer : `arch` est donc lu comme un parametre qui ne nous concerne
   * pas, exactement comme un `f` inconnu.
   */
  const initialArchives = hasDoors && readArchivesParam(initialSearch?.['arch']);

  const [query, setQueryState] = useState<ScreenQuery>(() => ({
    search: '',
    filterIds: initialFilterIds,
    // Le miroir V1, aligne des la naissance (meme regle que `nextQuery`).
    filterId: initialFilterIds.length === 1 ? (initialFilterIds[0] ?? null) : null,
    sortStack: [],
    sort: null,
    groupBy: null,
    limit: limitDefault,
    after: null,
    range: null,
    includeArchived: false,
    archivesMode: initialArchives,
    // Avec des portes, la section EST la porte de depart (deja verifiee).
    // Sans, elle ne peut pas etre verifiee ici : son catalogue vit dans les
    // `menuItems` du gabarit, que le core ne voit pas (cf. `defaultSection`).
    section:
      initialDoor != null
        ? initialDoor.id
        : (initialSearch?.['s'] ?? options.defaultSection ?? null),
  }));

  /* ---- La PORTE ACTIVE, resolue a chaque rendu ---- */

  /**
   * La porte suit `query.section` (c'est la meme information : `?s=` porte
   * l'id de porte). Le repli sur la premiere porte couvre l'etat que rien ne
   * devrait produire (une section ecrite a la main via `setQuery` avec un id
   * inconnu) : le moteur reste sain plutot que de rendre un ecran sans
   * catalogue. Une fois la porte connue, ses catalogues PRENNENT LES REFS :
   * tout ce qui lit `loadRef`, `typesRef`, `bindingsRef` ou `filterDefsRef`
   * plus bas travaille pour la porte active sans le savoir.
   */
  const activeDoor: ScreenDoor<T> | null = hasDoors
    ? (doorsRef.current.find((door) => door.id === query.section) ?? doorsRef.current[0] ?? null)
    : null;
  /** La porte se tranche-t-elle (filtres + favoris). Toujours vrai sans portes. */
  const doorSections = activeDoor == null ? true : activeDoor.sections !== false;
  const activeTypes = activeDoor?.types ?? options.types;
  const activeFilterDefs: ScreenFilterDef<T>[] = !doorSections
    ? []
    : (activeDoor?.filters ?? options.filters ?? []);
  if (activeDoor != null) {
    if (activeDoor.load != null) loadRef.current = activeDoor.load;
    if (activeDoor.get != null) getOneRef.current = activeDoor.get;
    if (activeDoor.bindings != null) bindingsRef.current = activeDoor.bindings;
    // Les trois verbes de la fiche suivent la porte, comme `load` et `get` :
    // une porte peut savoir archiver ce qu'une autre ne sait pas.
    if (activeDoor.archive != null) archiveRef.current = activeDoor.archive;
    if (activeDoor.unarchive != null) unarchiveRef.current = activeDoor.unarchive;
    if (activeDoor.remove != null) removeRef.current = activeDoor.remove;
  }
  typesRef.current = activeTypes;
  filterDefsRef.current = activeFilterDefs;

  /* ---- Le tiroir de la zone 4 (tags + favoris) ---- */

  /**
   * REPLIE par defaut. Il s'ouvre TOUT SEUL au premier tag pose, et seulement
   * la (la transition "aucun tag -> des tags" vit dans `mutateTags`) : si la
   * simple presence de tags forcait l'ouverture, refermer a la main deviendrait
   * impossible et l'etoile serait un bouton mort des qu'un filtre existe.
   * Il nait OUVERT quand l'adresse d'arrivee pose deja des filtres : le
   * destinataire d'un lien doit voir les tags qui expliquent sa liste. Sur un
   * ecran sans portes il reste ferme quoi qu'il arrive : la pastille par
   * defaut d'un ecran V1 est un etat de repos, pas un tag pose (et son
   * `setFilter` remplace toujours un id par un autre, la transition "aucun
   * tag -> des tags" n'y existe pas).
   *
   * LE MODE ARCHIVES COMPTE COMME UN TAG (chantier 2_0) : arriver sur un lien
   * `arch=1` ouvre le tiroir pour la meme raison exactement, et c'est encore
   * plus vrai pour lui que pour un filtre (il ne montre pas une tranche de la
   * liste attendue, il en montre une AUTRE).
   */
  const [drawerOpen, setDrawerOpenState] = useState<boolean>(
    () => (hasDoors && initialFilterIds.length > 0) || initialArchives,
  );

  /* ---- Favoris : les presets de la porte, puis ceux gardes par l'utilisateur ---- */

  /**
   * La liste gardee vit en pref locale PAR PORTE (`favoritesPrefKey`), par la
   * MEME mecanique que les prefs de vues : `usePref` ecoute `taki:pref-change`,
   * donc tout ce qui touche la liste DANS LE MEME DOCUMENT (le hub Parametres,
   * un autre ecran monte) la resynchronise ici sans rien cabler. L'event ne
   * franchit pas l'onglet : un second onglet verra le changement a son
   * prochain rechargement. Le changement de porte change la cle, et `usePref`
   * recharge la bonne liste tout seul.
   */
  const doorId = activeDoor?.id ?? IMPLICIT_DOOR_ID;
  /**
   * LA PORTE DU MOMENT, lisible depuis un callback STABLE. Meme patron que
   * `typesRef` et `lastCollectionRef`, et pour la meme raison de bord : elle
   * est aussi avancee dans `openDoor`, parce que la suite du MEME geste doit
   * deja raisonner avec la nouvelle porte. Le cas qui l'exige : le bouton
   * Suivant du navigateur vers la fiche d'une AUTRE porte, ou `fromSearch`
   * enchaine `openDoor` puis `openRecord` dans le meme tick. Sans cette ref,
   * `markReadRef.current` fermait sur le `doorId` du rendu courant et marquait
   * la fiche lue sous l'ANCIENNE porte : elle restait nouvelle dans la sienne.
   */
  const doorIdRef = useRef(doorId);
  doorIdRef.current = doorId;
  const [storedFavoritesRaw, setStoredFavorites] = usePref<ScreenFavorite[]>(
    favoritesPrefKey(appId, screenId, doorId),
    [],
  );
  // Une pref est du JSON ecrit par n'importe quelle version passee : relecture
  // defensive, une entree malformee tombe sans bruit.
  const storedFavorites = useMemo(() => sanitizeFavorites(storedFavoritesRaw), [storedFavoritesRaw]);

  /**
   * Presets D'ABORD (l'ordre de la porte est un choix d'app), gardes ensuite
   * (l'ordre d'enregistrement). Un id garde qui entre en collision avec un
   * preset est ignore : deux favoris du meme id rendraient `applyFavorite` et
   * l'allumage de la zone 4c ambigus.
   */
  const presetsSource = doorSections ? (activeDoor?.presets ?? null) : null;
  const favorites = useMemo<ScreenFavorite[]>(() => {
    if (!doorSections) return [];
    const presets = presetsSource ?? [];
    const seen = new Set(presets.map((preset) => preset.id));
    const merged = [...presets];
    for (const favorite of storedFavorites) {
      if (seen.has(favorite.id)) continue;
      seen.add(favorite.id);
      merged.push(favorite);
    }
    return merged;
  }, [doorSections, presetsSource, storedFavorites]);

  /** Le favori qui decrit EXACTEMENT l'etat courant : celui que la 4c allume. */
  const activeFavoriteId = useMemo<string | null>(
    () =>
      favorites.find((favorite) => matchesFavorite(favorite, query.filterIds, query.sortStack))
        ?.id ?? null,
    [favorites, query.filterIds, query.sortStack],
  );

  /**
   * "Garder" (4b) n'a de sens que s'il y a un ETAT a garder (au moins un tag)
   * et qu'il n'est pas DEJA un favori : garder deux fois le meme etat ferait
   * deux pastilles indiscernables dont une seule s'allumerait.
   */
  const canKeepFavorite =
    doorSections &&
    activeFavoriteId == null &&
    (query.filterIds.length > 0 || query.sortStack.length > 0);

  // Lus par les callbacks stables (`keepFavorite`, `applyFavorite`...) : par
  // ref, comme tout ce que l'ecran recree a chaque rendu.
  const favoritesRef = useRef(favorites);
  favoritesRef.current = favorites;
  const storedFavoritesRef = useRef(storedFavorites);
  storedFavoritesRef.current = storedFavorites;
  const setStoredFavoritesRef = useRef(setStoredFavorites);
  setStoredFavoritesRef.current = setStoredFavorites;
  const canKeepFavoriteRef = useRef(canKeepFavorite);
  canKeepFavoriteRef.current = canKeepFavorite;

  /* ---- La pastille "Nouveau" : ce que ce poste a deja vu ---- */

  /**
   * LE REGLAGE, par APP (`app.<appId>.nouveau`), ALLUME PAR DEFAUT : une cle
   * absente vaut "allume", et une valeur abimee aussi (seul un `false` franc
   * eteint). Eteint, le core ne calcule RIEN : pas de date de reference posee,
   * pas d'id memorise, `isNew` faux partout.
   */
  const [newBadgePref] = usePref<boolean>(newBadgePrefKey(appId), true);
  const newBadgeOn = newBadgePref !== false;

  /**
   * LA MEMOIRE DE LA PORTE (date de reference + ids deja ouverts), lue par
   * `readNewMemory` et par lui seul (voir newRecords.ts). Elle vit en etat
   * React, pas en `usePref`, pour cette raison precise : `usePref` lirait le
   * stockage de son cote, ce qui ferait un TROISIEME point de lecture et
   * couterait a la fiche `nouveau-multi-utilisateur` un endroit de plus a
   * reprendre.
   *
   * ELLE PORTE SA CLE AVEC ELLE, et ce n'est pas de la decoration. Entre un
   * changement de porte et la resynchronisation, l'etat parle encore de la
   * porte PRECEDENTE : sans la cle, un effet pouvait decider sur la memoire
   * d'une porte et ECRIRE sur une autre. Il etait juste par coincidence
   * d'ordonnancement ; il l'est maintenant par construction, chaque effet
   * concerne sortant en tete quand la cle rangee ne vaut pas `newMemoryKey`.
   */
  const newMemoryKey = newMemoryPrefKey(appId, screenId, doorId);
  const [newMemoryState, setNewMemoryState] = useState<{ key: string; memory: NewMemory }>(() => ({
    key: newMemoryPrefKey(appId, screenId, doorId),
    memory: readNewMemory(appId, screenId, doorId),
  }));

  /**
   * Le meme etat, lisible depuis un callback STABLE (`markReadRef.current`).
   * Meme patron que `storedFavoritesRef` : la ref suit a chaque rendu.
   */
  const newMemoryRef = useRef(newMemoryState);
  newMemoryRef.current = newMemoryState;

  /**
   * La memoire de CETTE porte, ou `null` tant que l'etat parle encore d'une
   * autre. Un `null` d'une frame ne coute rien a l'ecran : changer de porte
   * vide deja les lignes (`openDoor`), il n'y a rien a marquer pendant ce
   * battement, et mieux vaut ne rien signaler que signaler d'apres la memoire
   * du jeu de donnees precedent.
   */
  const newMemory = newMemoryState.key === newMemoryKey ? newMemoryState.memory : null;

  const syncNewMemory = useCallback((): void => {
    const key = newMemoryPrefKey(appId, screenId, doorId);
    setNewMemoryState((previous) => {
      const next = readNewMemory(appId, screenId, doorId);
      return previous.key === key && sameNewMemory(previous.memory, next)
        ? previous
        : { key, memory: next };
    });
  }, [appId, screenId, doorId]);

  /**
   * Relecture au changement de CLE (changer de porte change la memoire) et a
   * chaque event de pref sur cette cle : "Tout marquer comme lu" repose la
   * date depuis le hub Parametres, dans CE document. Meme mecanique que
   * `usePref`, avec le meme event, donc la meme limite : l'event ne franchit
   * pas le document et aucun ecouteur "storage" n'existe, donc un SECOND
   * ONGLET ne se resynchronise pas ; il verra le changement a son prochain
   * rechargement.
   */
  useEffect(() => {
    syncNewMemory();
    const onChange = (event: Event): void => {
      if ((event as CustomEvent<string>).detail !== newMemoryKey) return;
      syncNewMemory();
    };
    window.addEventListener(PREF_CHANGE_EVENT, onChange);
    return () => window.removeEventListener(PREF_CHANGE_EVENT, onChange);
  }, [newMemoryKey, syncNewMemory]);

  /**
   * LA DATE DE REFERENCE, posee a la PREMIERE VISITE de la porte et jamais
   * rebougee ensuite. C'est elle qui empeche la premiere ouverture d'allumer
   * les 500 lignes deja en base : ce qui existait avant qu'on regarde n'est
   * pas une nouveaute, c'est le fond de la collection.
   *
   * Elle ne se pose PAS tant que le reperage est eteint : sur une app ou le
   * reglage a toujours ete coupe, le rallumer doit repartir de l'instant ou on
   * le rallume, pas d'une date posee a son insu pendant que rien ne
   * s'affichait. Une date DEJA posee, elle, ne rebouge jamais : eteindre puis
   * rallumer retrouve les nouveautes d'avant, et le seul geste qui remet le
   * compteur a zero est "Tout marquer comme lu".
   *
   * C'est le SEUL des deux effets a garder sa garde sur `newBadgeOn` : poser
   * une date est un choix d'affichage, memoriser une lecture est un fait.
   */
  useEffect(() => {
    // La cle d'abord : on ne decide jamais sur la memoire d'une autre porte
    // que celle sur laquelle on ecrit.
    if (newMemoryState.key !== newMemoryKey) return;
    if (!newBadgeOn || newMemoryState.memory.since != null) return;
    const memory: NewMemory = { since: Date.now(), seen: newMemoryState.memory.seen };
    writeNewMemory(appId, screenId, doorId, memory);
    setNewMemoryState({ key: newMemoryKey, memory });
  }, [newBadgeOn, newMemoryState, newMemoryKey, appId, screenId, doorId]);

  /**
   * Marquer une fiche LUE depuis un callback STABLE (`openRecord` ne se recree
   * pas quand la porte change) : la fonction est reassignee a chaque rendu, la
   * ref ne l'est jamais. Meme patron que `setStoredFavoritesRef`.
   *
   * ON NE RELIT JAMAIS LE STOCKAGE POUR UN ETAT QU'ON VIENT DE CALCULER. C'est
   * la regle, et elle a une cause precise : `writePref` retombe en silence
   * quand localStorage est indisponible (mode prive, quota) tout en dispatchant
   * son event ; une relecture rendait alors une memoire VIDE qui ecrasait
   * l'etat React, `since` repassait a null, l'effet de date le reposait a
   * maintenant, et UN SEUL CLIC eteignait toutes les pastilles de l'ecran. On
   * calcule donc l'etat d'apres avec `markSeen` (pur, newRecords.ts), on
   * l'ecrit, et on le pose : la memoire tient meme quand le stockage ne tient
   * pas.
   *
   * IL MARQUE MEME REGLAGE ETEINT. Ce que `seen` enregistre est un FAIT ("on a
   * ouvert cette fiche sur ce poste"), pas un affichage : le fait reste vrai
   * quand l'affichage est coupe, et le memoriser coute une ecriture. Ne pas le
   * faire produisait exactement le bug que ce chantier ne doit pas produire :
   * tout ce qui avait ete lu pendant que le reperage etait eteint se rallumait
   * au rallumage, la fiche redevenant nouvelle toute seule.
   */
  const markReadRef = useRef<(id: string) => void>(() => {});
  markReadRef.current = (id: string): void => {
    if (id === '') return;
    // La porte du MOMENT, jamais celle du rendu : voir `doorIdRef`.
    const currentDoorId = doorIdRef.current;
    const key = newMemoryPrefKey(appId, screenId, currentDoorId);
    const state = newMemoryRef.current;
    // L'etat parle d'une autre porte (le rendu n'a pas encore rattrape) : on
    // ne s'en sert pas, on lit celle-ci, et la resynchronisation suivra.
    const known = state.key === key;
    const memory = known ? state.memory : readNewMemory(appId, screenId, currentDoorId);
    const next = markSeen(memory, id);
    // Identite : `markSeen` rend l'objet recu quand il n'y a rien a faire
    // (rouvrir la meme fiche, revenir dessus a la fleche). Ni ecriture, ni rendu.
    if (next === memory) return;
    writeNewMemory(appId, screenId, currentDoorId, next);
    if (!known) return;
    const updated = { key, memory: next };
    newMemoryRef.current = updated;
    setNewMemoryState(updated);
  };

  /**
   * OUVRIR UNE FICHE L'ETEINT. `openRecord` marque deja au clic, tout de
   * suite, sans attendre le chargement ; cet effet couvre les DEUX entrees qui
   * ne passent pas par lui : l'arrivee A FROID sur un lien `?id=...` (l'etat
   * de depart pose `openId` directement) et le selecteur de vues qui bascule
   * sur la fiche sans element ouvert. Sans lui, une fiche ouverte par un lien
   * partage restait allumee au retour en collection. Marquer deux fois ne
   * coute rien : rien n'est ecrit si l'id est deja le dernier lu.
   */
  useEffect(() => {
    if (openId == null) return;
    markReadRef.current(openId);
  }, [openId, newMemoryKey]);

  /**
   * CE QUE LA PROJECTION RECOIT. `undefined` des que le reperage est eteint ou
   * que la porte n'a pas encore sa date de reference : la projection ne lit
   * meme pas `createdAt` et rend `isNew: false` partout.
   */
  const newRecordsInput = useMemo<NewRecordsInput | undefined>(() => {
    if (!newBadgeOn || newMemory == null || newMemory.since == null) return undefined;
    return { since: newMemory.since, seen: new Set(newMemory.seen) };
  }, [newBadgeOn, newMemory]);

  /* ---- Donnees ---- */

  const [records, setRecords] = useState<T[]>([]);
  /**
   * Mode SERVEUR : la derniere page rendue etait pleine, donc il en reste
   * probablement. Etat a part et non deduit de `records.length` : une fois les
   * pages empilees, plus rien dans le jeu ne dit si le serveur avait la suite.
   */
  const [hasMore, setHasMore] = useState(false);
  const [inFlight, setInFlight] = useState(true);
  /**
   * Le meme "un chargement est en vol", lisible depuis un callback stable.
   * `loadMore` en a besoin AVANT le rendu suivant : au moment du clic, l'etat
   * `inFlight` de la closure peut dater d'un rendu, la ref ne date jamais.
   */
  const inFlightRef = useRef(true);
  inFlightRef.current = inFlight;
  const [error, setError] = useState<Error | undefined>(undefined);
  const [reloadTick, setReloadTick] = useState(0);
  /** Enregistrement charge seul (entree a froid, element sorti du filtre). */
  const [solo, setSolo] = useState<{ id: string; record: T } | null>(null);
  /** Patches optimistes, par id puis par role. Vides a chaque chargement reussi. */
  const [patches, setPatches] = useState<Record<string, Record<string, Cell>>>({});
  /**
   * Patches optimistes des COLONNES, par id puis par cle de colonne. Un SECOND
   * sac, pas une extension du premier : l'espace de cles n'est pas le meme et il
   * peut collisionner (une colonne `status` n'est pas le role `status`).
   */
  const [columnPatches, setColumnPatches] = useState<Record<string, Record<string, Cell>>>({});

  /* ---- Ecriture : etat visible, annulation, jetons de sequence ---- */

  const [saveState, setSaveState] = useState<SaveState>({ mode: 'idle' });
  /**
   * Les piles d'annulation sont du STATE et non des refs : `canUndo` alimente
   * une puce a l'ecran, une ref ne re-rendrait rien.
   */
  const [undoStack, setUndoStack] = useState<WriteStep[]>([]);
  const [redoStack, setRedoStack] = useState<WriteStep[]>([]);
  /** La derniere ecriture echouee, telle quelle : c'est ce que `retrySave` rejoue. */
  const failedRef = useRef<WriteRequest | null>(null);
  /**
   * Le dernier GESTE DE FICHE echoue (archiver, desarchiver, supprimer), tel
   * quel. Un sac a part de `failedRef` parce que ce n'est pas la meme chose a
   * rejouer (un document, pas une case), mais le MEME bouton "Reessayer" :
   * sans lui, un archivage rate affichait "Echec de l'enregistrement" avec une
   * relance qui ne relancait rien, exactement le controle mort que G16
   * interdit. Le plus RECENT des deux gagne : chacun efface l'autre en
   * s'inscrivant.
   */
  const failedRecordActionRef = useRef<{ kind: RecordActionKind; record: T } | null>(null);
  /**
   * Le jeton de sequence, par couple (cible). Une ecriture qui revient alors
   * qu'une plus recente est partie sur la MEME cible ne fait plus rien : ni
   * rollback, ni transition de `saveState`, ni rechargement.
   */
  const sequenceRef = useRef(new Map<string, number>());
  /**
   * LE JETON DE SESSION DE FICHE. Il change a chaque ouverture et a chaque
   * sortie (`resetRecordSession`). Le jeton de sequence ci-dessus ne repond pas
   * a la meme question : lui compare deux ecritures sur la MEME cible, celui-ci
   * dit si l'ecriture qui atterrit appartient encore a la fiche a l'ecran. Sans
   * lui, une ecriture partie juste avant un `Echap` revient 300 ms plus tard
   * reposer `saveState` a `saved` et re-empiler son pas d'annulation dans une
   * session que `resetRecordSession` venait de vider.
   */
  const recordSessionRef = useRef(0);
  /** Ecritures en vol : sert a ne pas effacer un optimisme encore utile. */
  const pendingWritesRef = useRef(0);

  const [selection, setSelection] = useState<string[]>([]);
  /**
   * VIDER LA SELECTION, d'un seul geste et sans rendu inutile.
   *
   * C'est `clearSelection` a la sortie (la croix de la barre de lot, `Echap`),
   * ET le point de passage unique de tout ce qui la fait TOMBER : changer de
   * porte, de recherche, de filtre, de mode Archives, recharger. La forme
   * fonctionnelle est ce qui rend l'appel gratuit quand il n'y a rien a vider
   * (React sort sur l'identite), sans quoi chaque frappe de recherche
   * reposerait un tableau neuf et ferait repartir tout ce qui en depend.
   *
   * Ce qu'il ne fait PAS : quitter le tableau. Ce cas-la ne passe par aucun
   * geste (on peut y arriver par l'adresse, par le bouton Retour, par le
   * selecteur de vues), il est donc tenu par l'invariant lui-meme, plus bas.
   */
  const dropSelection = useCallback((): void => {
    setSelection((current) => (current.length === 0 ? current : NO_SELECTION));
  }, []);
  const [viewStates, setViewStates] = useState<Record<string, unknown>>({});
  /** La liste d'ids GELEE a l'ouverture de la fiche. */
  const [frozenIds, setFrozenIds] = useState<string[] | null>(null);

  const scrollRef = useRef<HTMLElement>(null);

  const queryRef = useRef(query);
  queryRef.current = query;
  const patchesRef = useRef(patches);
  patchesRef.current = patches;
  const columnPatchesRef = useRef(columnPatches);
  columnPatchesRef.current = columnPatches;
  const undoRef = useRef(undoStack);
  undoRef.current = undoStack;
  const redoRef = useRef(redoStack);
  redoRef.current = redoStack;
  const recordsRef = useRef(records);
  recordsRef.current = records;
  const soloRef = useRef(solo);
  soloRef.current = solo;
  const openIdRef = useRef(openId);
  openIdRef.current = openId;
  const viewIdRef = useRef(viewId);
  viewIdRef.current = viewId;
  const lastCollectionRef = useRef(lastCollectionViewId);
  lastCollectionRef.current = lastCollectionViewId;
  const frozenRef = useRef(frozenIds);
  frozenRef.current = frozenIds;

  /* ---- Chargement ---- */

  // Seuls les parametres que le CHARGEUR peut exploiter declenchent un appel.
  // La recherche et les pastilles restent CLIENTES dans les deux modes (pas de
  // recherche plein texte cote Firestore) : les mettre ici relancerait une
  // requete a chaque frappe. Le TRI, lui, part au serveur en mode serveur,
  // donc il compte comme parametre de requete la-bas et pas ici.
  const loadKey = [
    query.limit,
    query.after ?? '',
    // La SECTION segmente les donnees a la source : la changer est un
    // rechargement, dans les deux modes. C'est ce qui a permis aux ecrans de
    // supprimer leur `useEffect(() => reload(), [section])` maison.
    query.section ?? '',
    query.range ? `${query.range.from.getTime()}-${query.range.to.getTime()}` : '',
    String(query.includeArchived),
    // LE MODE ARCHIVES est un parametre de REQUETE : le chargeur le relaie au
    // SDK (`list({ archived })`), donc l'allumer recharge, exactement comme
    // changer de porte. Cette liste doit rester alignee sur celle de
    // `nextQuery` : un champ ajoute ici sans l'autre empile les pages de deux
    // requetes differentes, en silence.
    String(query.archivesMode),
    // Le tri SERVEUR est le MIROIR (`query.sort` = niveau 1 de la pile, seul
    // `orderBy` que le SDK accepte) : empiler ou inverser un niveau 2 raffine
    // en memoire et ne recharge rien, changer le niveau 1 recharge.
    pagination === 'server' && query.sort != null ? `${query.sort.key}:${query.sort.dir}` : '',
    // La SOURCE a change en dehors de l'ecran (lecture partagee rechargee
    // ailleurs) : meme mecanique que la section, et meme benefice, l'ecran n'a
    // pas d'`useEffect(() => reload())` a ecrire.
    String(options.reloadKey ?? ''),
    String(reloadTick),
  ].join('|');

  useEffect(() => {
    let cancelled = false;
    const server = paginationRef.current === 'server';
    // PAGE SUIVANTE (on empile) ou chargement neuf (on remplace) : la question
    // se tranche sur le curseur, jamais sur "est-ce que records est vide".
    // Un jeu vide peut tres bien etre une page 2 qui n'a rien rendu.
    const append = server && queryRef.current.after != null;
    // La ref est posee ICI, avant tout await : `loadMore` la lit au clic, et
    // attendre le rendu suivant laisserait passer un clic pendant la fenetre.
    inFlightRef.current = true;
    setInFlight(true);
    setError(undefined);
    void (async () => {
      try {
        const result = await loadRef.current(queryRef.current);
        if (cancelled) return;
        if (server) {
          // "Il reste des pages" se lit "la page etait pleine". C'est le
          // contrat du SDK (`runList` ne rend pas de curseur, cf. list-query.ts
          // dans @taki/sdk) : une derniere page exactement pleine fait donc
          // proposer un "charger la suite" qui ne rendra rien, et le bandeau
          // disparait au clic suivant. Defaut connu, assume, auto-corrige.
          const full = result.length >= queryRef.current.limit;
          // MAIS : une page pleine dont TOUTES les lignes sont deja connues
          // n'avance pas. Ca arrive pour de vrai avec un tri sur `updatedAt`
          // quand un collegue remonte des lignes deja lues au-dessus du
          // curseur. Sans ce garde-fou, le curseur ne bouge plus, le bandeau
          // reste, et le bouton devient definitivement mort : l'utilisateur
          // clique sans que rien ne se passe et sans un mot d'explication.
          const known = new Set(recordsRef.current.map(getIdRef.current));
          const advanced = !append || result.some((row) => !known.has(getIdRef.current(row)));
          setHasMore(full && advanced);
          if (import.meta.env.DEV && result.length > queryRef.current.limit) {
            // Le chargeur ne respecte pas la taille de page demandee : la
            // deduction "page pleine = il en reste" ne veut plus rien dire, et
            // le symptome (bandeau perpetuel, ou jamais de bandeau) ne pointe
            // pas vers sa cause. Meme esprit qu'`assertBinding`.
            console.warn(
              `[taki/views] ${appId}.${screenId} : le chargeur a rendu ${result.length} lignes ` +
                `alors que la page demandee etait de ${queryRef.current.limit}. En pagination serveur, ` +
                'le chargeur DOIT relayer `query.limit` au SDK, sinon la detection de page suivante est fausse.',
            );
          }
        }
        setRecords((previous) =>
          append ? mergeById(previous, result, getIdRef.current) : result,
        );
        // Les patches optimistes ont fait leur travail : la donnee fraiche prend
        // le relais. SAUF si une ecriture est encore en vol : ce chargement-ci
        // ne la contient pas, vider le sac ferait disparaitre sa valeur sous les
        // doigts jusqu'a ce qu'elle atterrisse. Le sac sera vide au chargement
        // suivant, celui que cette ecriture declenchera en reussissant.
        if (pendingWritesRef.current === 0) {
          setPatches({});
          setColumnPatches({});
        }
      } catch (cause) {
        if (cancelled) return;
        // CURSEUR PERDU : l'element qui servait de curseur a disparu entre deux
        // pages. Ce n'est pas une panne a montrer, c'est un jeu a reprendre :
        // on repart de la premiere page, ce qui relance le chargement puisque
        // le curseur fait partie de `loadKey`.
        //
        // Les DEUX gardes comptent. `server` : le mode client n'a pas de
        // curseur, une erreur portant ce nom y serait une erreur ordinaire et
        // l'avaler afficherait un ecran vide silencieux a la place du bloc
        // d'erreur et de son bouton Reessayer. `after != null` : sans curseur a
        // annuler, remettre `after` a null ne change AUCUNE valeur de `loadKey`,
        // donc aucun chargement ne repartirait et l'ecran resterait vide, sans
        // erreur et sans reprise possible.
        if (server && queryRef.current.after != null && isCursorLost(cause)) {
          setHasMore(false);
          setRecords([]);
          setQueryState((previous) => ({ ...previous, after: null }));
          return;
        }
        setError(cause instanceof Error ? cause : new Error(String(cause)));
      } finally {
        if (!cancelled) {
          inFlightRef.current = false;
          setInFlight(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadKey]);

  /* ---- Verification des bindings (developpement uniquement) ---- */

  // Chaque porte a ses catalogues : on re-verifie au passage d'une porte, pas
  // seulement au montage (les refs portent deja ceux de la porte active).
  const activeDoorId = activeDoor?.id ?? null;
  useEffect(() => {
    assertBinding(sessionKey, typesRef.current, bindingsRef.current);
  }, [sessionKey, activeDoorId]);

  /*
   * Le champ `demand` des vues cesse d'etre decoratif ici. Une vue qui declare
   * `demand: 'all'` (le kanban : "une carte mal placee est un mensonge, il doit
   * voir tout le jeu") montee sur une pagination serveur incomplete regroupe la
   * PAGE COURANTE : ses colonnes et leurs compteurs decrivent un sous-ensemble
   * arbitraire, sans que rien ne le signale a l'ecran. On ne peut pas l'en
   * empecher (l'utilisateur a le droit de choisir sa vue), mais on peut refuser
   * que ce soit silencieux pendant la construction de l'app.
   */
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    if (pagination !== 'server' || !hasMore) return;
    const active = typesRef.current.find((type) => type.id === viewIdRef.current);
    if (active?.demand !== 'all') return;
    console.warn(
      `[taki/views] ${sessionKey} : la vue ${viewIdRef.current} demande tout le jeu ` +
        "(demand: 'all') alors que la pagination serveur n'a charge qu'une partie des lignes. " +
        'Elle ne decrit donc que les pages chargees. Chargez la suite, ou reservez cette vue ' +
        'aux ecrans en pagination cliente.',
    );
  }, [sessionKey, pagination, hasMore, viewId]);

  /* ---- Projection ---- */

  const activeType = useMemo<ViewType | null>(
    () => activeTypes.find((type) => type.id === viewId) ?? null,
    [activeTypes, viewId],
  );
  const level: ViewLevel = activeType?.level ?? 'collection';

  // Une seule passe : archives exclus, pastille appliquee, projection, patches
  // optimistes, recherche, tri, fenetre. Memoisee sur ce qui la fait changer ;
  // les bindings sont lus par ref (leurs closures sont stables en pratique).
  const projected = useMemo(() => {
    const binding = bindingsRef.current.find((candidate) => candidate.type === viewId);
    const getId = getIdRef.current;

    // Les archives sortent du jeu AVANT tout le reste : une vue n'en voit
    // jamais. `live` est le jeu VIVANT de la porte, et il ne bouge PAS avec le
    // mode Archives : c'est lui qui alimente les stats (2c) et les compteurs
    // du popover de filtres, que la regle G15 veut sur la porte BRUTE, donc
    // sur les actifs. Un compteur qui change de sens selon un mode est un
    // compteur auquel on ne peut plus se fier.
    const live = query.includeArchived
      ? records
      : records.filter((record) => !isArchived(record));
    /*
     * LE JEU QUE LES VUES RENDENT (chantier 2_0). Le MODE ARCHIVES decide
     * SEUL : allume, on voit les archivees et RIEN d'autre ; eteint, on n'en
     * voit aucune. Son test passe AVANT les filtres, et c'est tout le point du
     * chantier : ce n'est pas un filtre de plus, c'est un ETAT A PART de
     * l'ecran.
     *
     * `records` peut contenir les deux etats (un chargeur en pagination
     * cliente relaie `archived: 'all'` pour garder ses compteurs vrais) ou les
     * archives seules (`'only'`, pagination serveur) : le partage se fait ici,
     * dans les deux cas, et le moteur n'a pas a savoir lequel des deux
     * l'ecran a choisi.
     */
    const pool = query.archivesMode ? records.filter((record) => isArchived(record)) : live;
    // MULTI-FILTRES ADDITIFS : une ligne passe si AU MOINS UN filtre actif la
    // retient (un OU, jamais un ET : poser "Camera" puis "Optique" AGRANDIT la
    // selection, c'est le contrat des tags de la zone 4). Aucun matcher = pas
    // de filtrage : c'est a la fois l'etat vide du gabarit v2 ("Tout le parc")
    // et la lecture V1 d'une pastille active sans `match` ("Tous"), qui garde
    // ainsi exactement son comportement d'avant les portes.
    // LES FILTRES NE S'APPLIQUENT PAS AUX ARCHIVES (chantier 2_0) : le mode a
    // deja decide du jeu. Une archive est hors du travail courant, la trancher
    // par statut n'aurait pas de sens ; le popover le MONTRE en grisant ses
    // autres entrees plutot que de le dire.
    const matchers = query.archivesMode
      ? []
      : query.filterIds
          .map((id) => filterDefsRef.current.find((candidate) => candidate.id === id)?.match)
          .filter((match): match is (record: T) => boolean => match != null);
    const kept =
      matchers.length === 0
        ? pool
        : pool.filter((record) => matchers.some((match) => match(record)));

    const roles = rolesOf(binding);
    const catalogs = columnCatalogs(binding);
    // La pastille "Nouveau" est posee ICI, par le core : une vue lit
    // `row.isNew`, elle ne le calcule jamais (elle ne lit aucun contexte).
    // `newRecordsInput` est `undefined` quand le reglage est eteint, et la
    // projection retombe alors exactement sur son comportement d'avant.
    let rows = projectRecords(kept, getId, binding, newRecordsInput);

    // Ecriture optimiste : la vue voit le changement avant le serveur. Les deux
    // sacs s'appliquent AVANT la recherche, le tri et la fenetre : une valeur
    // fraichement saisie pese donc tout de suite sur ce qu'on voit.
    if (Object.keys(patches).length > 0) {
      rows = rows.map((row) => {
        const rowPatches = patches[row.id];
        if (!rowPatches) return row;
        let patched = row;
        for (const [role, value] of Object.entries(rowPatches)) {
          patched = applyRolePatch(patched, role, value, roles[role]);
        }
        return patched;
      });
    }
    if (Object.keys(columnPatches).length > 0) {
      rows = rows.map((row) => {
        const rowPatches = columnPatches[row.id];
        if (!rowPatches) return row;
        let patched = row;
        for (const [key, value] of Object.entries(rowPatches)) {
          patched = applyColumnPatch(patched, key, value, catalogs[key]);
        }
        return patched;
      });
    }

    rows = rows.filter((row) => matchesSearch(row, query.search));

    // LA PILE DE TRI, niveau par niveau : le niveau 1 commande, les suivants
    // departagent ses egalites. Egalite sur TOUS les niveaux : on ne tranche
    // pas, `Array.prototype.sort` est stable (ES2019), donc l'ordre du
    // chargeur fait foi et deux rendus du meme etat sont identiques. En mode
    // serveur, seul le niveau 1 est parti dans la requete (le SDK ne prend
    // qu'un `orderBy`) : les niveaux suivants raffinent ICI, sur les pages
    // chargees, ce qui est exactement la promesse du bandeau du ViewHost.
    const stack = query.sortStack;
    if (stack.length > 0) {
      /*
       * LE TRI APPARTIENT A L'ECRAN, PAS A LA VUE ACTIVE. `sortValueOf` lit
       * `row.cells[key]`, or seules les colonnes du binding ACTIF produisent
       * des cellules : en liste ou en kanban (aucune colonne declaree), une
       * cle de colonne ("reference", "dailyRate") ne resoudrait RIEN et le tag
       * de tri se poserait sans rien trier, en silence. Pour chaque cle de la
       * pile que la vue active ne sait pas resoudre (`resolvesSortKey`), on
       * precalcule donc un SAC id -> valeur en evaluant la colonne sur l'UNION
       * des bindings de la porte (premiere declaration gagnante), sur les
       * enregistrements retenus, et le comparateur le consulte a la place de
       * la ligne. Valeur BRUTE de la colonne, sans patch optimiste : la vue
       * active n'affiche rien pour ces cles, et le rechargement d'apres
       * ecriture reconcilie.
       */
      const fallbacks = new Map<string, Map<string, Cell>>();
      for (const { key } of stack) {
        if (fallbacks.has(key) || resolvesSortKey(binding, key)) continue;
        let source: Column<T> | undefined;
        for (const candidate of bindingsRef.current) {
          source = candidate.columns?.find((column) => column.key === key);
          if (source != null) break;
        }
        if (source == null) continue;
        const bag = new Map<string, Cell>();
        for (const record of kept) bag.set(getId(record), source.value(record));
        fallbacks.set(key, bag);
      }
      rows = [...rows].sort((a, b) => {
        for (const levelSort of stack) {
          const bag = fallbacks.get(levelSort.key);
          const compared = bag
            ? compareCells(bag.get(a.id), bag.get(b.id))
            : compareCells(sortValueOf(a, levelSort.key), sortValueOf(b, levelSort.key));
          if (compared !== 0) return levelSort.dir === 'desc' ? -compared : compared;
        }
        return 0;
      });
    }

    const columns: ViewColumn[] = (binding?.columns ?? []).map((column) => ({
      key: column.key,
      label: column.label,
      sortable: column.sortable ?? false,
      ...(column.agg != null ? { agg: column.agg } : {}),
      // Les METADONNEES d'edition passent la frontiere, jamais les fonctions :
      // le catalogue d'un `select` se demande a `optionsOf(key)`, il n'est pas
      // resolu ici (ce memo tourne a chaque frappe, il n'a pas a appeler le
      // `options()` de l'app pour des colonnes que personne n'edite).
      editable: typeof column.set === 'function',
      ...(column.kind != null ? { kind: column.kind } : {}),
    }));

    return {
      binding,
      roles,
      catalogs,
      rows,
      // Le jeu VIVANT de la porte (archives exclues, avant recherche et
      // filtres) : ce sur quoi les stats de la porte et les compteurs du
      // popover de filtres se calculent (regle "un compteur ne bouge pas
      // quand on filtre"). Insensible au mode Archives, par construction.
      live,
      // Le jeu du MODE : ce a quoi repond "y a-t-il des donnees du tout ?"
      // (l'etat vide), question distincte de "la recherche ne rend rien". En
      // mode archives, un zero ici veut dire "aucune fiche archivee", et le
      // ViewHost doit le dire avec ses mots a lui.
      poolCount: pool.length,
      orderedIds: rows.map((row) => row.id),
      // Mode SERVEUR : la fenetre a deja ete decidee par le serveur, page par
      // page. Re-couper a `query.limit` ici ne montrerait que la premiere page
      // quoi qu'on charge, et "Afficher plus" n'afficherait jamais rien.
      windowRows: pagination === 'server' ? rows : rows.slice(0, query.limit),
      truncated: pagination === 'server' ? false : rows.length > query.limit,
      columns,
    };
  }, [
    pagination,
    records,
    viewId,
    patches,
    // Oublier ce terme donne un optimisme qui ne s'affiche JAMAIS : la ligne ne
    // bougerait qu'au rechargement, et le symptome ("ca finit par marcher")
    // detourne du vrai coupable.
    columnPatches,
    query.search,
    // Les identites de `filterIds` et `sortStack` ne changent QUE quand leur
    // contenu change (`nextQuery` recopie la reference telle quelle sur les
    // patchs qui ne les touchent pas) : la projection ne repart donc pas pour
    // une frappe de recherche ou un rechargement sans rapport.
    query.filterIds,
    query.sortStack,
    query.limit,
    query.includeArchived,
    // Le MODE ARCHIVES change le jeu rendu ET desactive les filtres : oublier
    // ce terme laisserait la liste des actifs a l'ecran jusqu'au retour du
    // chargeur, donc un battement ou l'ecran ment.
    query.archivesMode,
    // La memoire du "nouveau" : ouvrir une fiche eteint sa pastille tout de
    // suite, sans attendre un rechargement.
    newRecordsInput,
  ]);

  const orderedIdsRef = useRef(projected.orderedIds);
  orderedIdsRef.current = projected.orderedIds;
  /**
   * Le binding de la vue ACTIVE, lisible depuis un callback stable. Les trois
   * primitives de lot en ont besoin pour retrouver le `set()` d'un champ, et
   * elles sont appelees en boucle par le moteur de lot : leur identite ne doit
   * pas changer sous ses pieds au milieu de douze ecritures.
   */
  const bindingRef = useRef(projected.binding);
  bindingRef.current = projected.binding;

  /* ---- La fiche : ligne ouverte, chargement solo, curseur ---- */

  const openIsLoaded = openId != null && projected.orderedIds.includes(openId);

  // Element absent du jeu courant (entree a froid, ou sorti du filtre) : on le
  // charge seul plutot que d'aller chercher toute la collection.
  useEffect(() => {
    if (openId == null) {
      if (soloRef.current != null) setSolo(null);
      return;
    }
    if (openIsLoaded) {
      if (soloRef.current != null) setSolo(null);
      return;
    }
    if (inFlight) return;
    if (soloRef.current?.id === openId) return;
    const getOne = getOneRef.current;
    if (getOne == null) return;
    let cancelled = false;
    void (async () => {
      try {
        const record = await getOne(openId);
        if (!cancelled) setSolo(record == null ? null : { id: openId, record });
      } catch {
        if (!cancelled) setSolo(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [openId, openIsLoaded, inFlight]);

  // Gel differe : entree a froid, la collection n'etait pas la a l'ouverture.
  // UNIQUEMENT au niveau fiche : de retour en collection, `setView` a vide le
  // gel expres, pour que la PROCHAINE ouverture gele sur le jeu courant. Sans
  // cette garde, l'effet regelerait aussitot sur le jeu d'avant la recherche.
  useEffect(() => {
    if (level !== 'record') return;
    if (openId == null || frozenIds != null) return;
    if (orderedIdsRef.current.length === 0) return;
    setFrozenIds(freezeIds(orderedIdsRef.current));
  }, [level, openId, frozenIds, projected.orderedIds]);

  const openRow = useMemo<ProjectedRow | null>(() => {
    if (openId == null) return null;
    const found = projected.rows.find((row) => row.id === openId);
    if (found) return found;
    // Sorti du jeu affiche (filtre, recherche, tri) mais DEJA EN MEMOIRE : on le
    // projette directement. Passer par `solo` ferait un aller-retour SDK inutile,
    // et sans `get()` la fiche s'afficherait "introuvable" alors que la donnee
    // est la. Le curseur, lui, dit "hors du filtre courant" (`outOfScope`).
    //
    // CES DEUX REPLIS REPROJETTENT DEPUIS LE RECORD BRUT : il faut leur rejouer
    // les patches a la main, sinon la valeur qu'on vient de saisir sur une fiche
    // hors filtre (ou ouverte a froid) revient en arriere sous les doigts.
    const getId = getIdRef.current;
    const rolePatches = patches[openId];
    const cellPatches = columnPatches[openId];
    const inMemory = records.find((candidate) => getId(candidate) === openId);
    if (inMemory !== undefined) {
      const row = projectRecord(inMemory, openId, projected.binding);
      return replayPatches(row, rolePatches, cellPatches, projected.roles, projected.catalogs);
    }
    if (solo?.id === openId) {
      const row = projectRecord(solo.record, openId, projected.binding);
      return replayPatches(row, rolePatches, cellPatches, projected.roles, projected.catalogs);
    }
    return null;
  }, [openId, projected, records, solo, patches, columnPatches]);

  /**
   * L'ENREGISTREMENT OUVERT, BRUT (pas sa ligne projetee). Les trois gestes de
   * fiche s'appliquent au DOCUMENT, pas a ses cellules : ils ont besoin du
   * `T`, exactement comme `commitWrite`, et le cherchent aux memes deux
   * endroits (le jeu en memoire, puis l'element charge seul). `null` quand
   * rien n'est ouvert, ou quand la fiche est introuvable : il n'y a alors rien
   * a archiver, et le menu de la rangee 3c reste vide.
   */
  const openRecordValue = useMemo<T | null>(() => {
    if (openId == null) return null;
    const getId = getIdRef.current;
    const inMemory = records.find((candidate) => getId(candidate) === openId);
    if (inMemory !== undefined) return inMemory;
    return solo?.id === openId ? solo.record : null;
  }, [openId, records, solo]);

  /** La fiche ouverte est-elle archivee : ce que le bandeau de tete rend. */
  const openArchived = openRecordValue != null && isArchived(openRecordValue);

  const cursor = useMemo(
    () => buildCursor(frozenIds, projected.orderedIds, openId),
    [frozenIds, projected.orderedIds, openId],
  );

  /* ---- Persistance de session ---- */

  useEffect(() => {
    writeSession(sessionKey, { viewId, openId, lastCollectionViewId });
  }, [sessionKey, viewId, openId, lastCollectionViewId]);

  /* ---- Actions ---- */

  /* ---- Filtres, tri : les mutations qui posent des tags ---- */

  /**
   * LE CHEMIN UNIQUE des mutations de filtres et de tri. Deux raisons d'exister :
   * - passer par `nextQuery`, qui tient les miroirs V1 et la peremption des
   *   pages serveur (aucune mutation n'ecrit `filterIds` ou `sortStack` a la
   *   main ailleurs) ;
   * - tenir la regle du TIROIR : il s'ouvre tout seul quand l'etat passe de
   *   "aucun tag" a "des tags", et JAMAIS sur les tags suivants (refermer a la
   *   main reste respecte). C'est une transition, pas une presence : la
   *   detecter ici, au seul endroit qui voit l'avant et l'apres, evite a
   *   chaque surface de la recoder en comparant des compteurs.
   * `compute` rend null quand il n'y a rien a faire : aucun setState, aucun
   * rendu, aucune fausse transition.
   *
   * La transition se juge sur l'etat NORMALISE (`nextQuery`), jamais sur le
   * patch brut : un patch peut porter les MIROIRS V1 (`sort`, `filterId`,
   * l'en-tete du tableau patche encore `sort`) et compter les seuls champs
   * canoniques du patch raterait leur effet.
   */
  const mutateTags = useCallback(
    (compute: (previous: ScreenQuery) => Partial<ScreenQuery> | null): void => {
      const previous = queryRef.current;
      const patch = compute(previous);
      if (patch == null) return;
      const server = paginationRef.current === 'server';
      const next = nextQuery(previous, patch, server);
      // LE MODE ARCHIVES COMPTE POUR UN TAG : il en pose un en zone 4 (de
      // genre "Mode", pas "Filtre"), donc il ouvre le tiroir tout seul quand
      // il est le PREMIER pose, exactement comme un filtre. C'est la seule
      // chose que le tiroir a besoin de savoir de lui.
      const countTags = (state: ScreenQuery): number =>
        state.filterIds.length + state.sortStack.length + (state.archivesMode ? 1 : 0);
      const before = countTags(previous);
      const after = countTags(next);
      /*
       * ET IL S'OUVRE AUSSI A L'ENTREE DANS LE MODE, meme quand des tags sont
       * deja poses. La transition "aucun tag -> des tags" ne suffisait pas : un
       * filtre ou un tri deja pose, le tiroir referme a l'etoile, et entrer
       * dans le mode laissait le tiroir FERME. Or la fenetre d'entree vient
       * d'enseigner qu'on en sort par la croix du tag : la sortie qu'on montre
       * doit exister a l'ecran juste apres.
       *
       * Un filtre montre une TRANCHE de la liste attendue, un mode en montre
       * une AUTRE : son tag ne peut pas naitre dans un tiroir replie. Refermer
       * a la main reste possible ensuite, comme pour les tags.
       */
      const entersArchives = !previous.archivesMode && next.archivesMode;
      /*
       * LA SELECTION TOMBE quand la TRANCHE change (chantier 3_0) : un filtre
       * pose ou retire, une entree ou une sortie du mode Archives. On agit sur
       * ce qu'on voit, et ces deux gestes changent ce qu'on voit.
       *
       * LE TRI N'EST PAS DANS CETTE LISTE, et la nuance vaut d'etre lue en
       * entier, parce qu'elle n'est pas la meme dans les deux modes de
       * pagination. En pagination CLIENTE, trier ne retire aucune ligne du jeu,
       * il les remet dans un autre ordre : vider la selection la-dessus serait
       * une punition sans cause. En pagination SERVEUR, changer le NIVEAU 1
       * perime les pages deja empilees (`nextQuery` remet `after` a null) et
       * recharge une page 1 REORDONNEE : les lignes cochees qui n'y sont plus
       * ont quitte l'ecran, donc l'invariant (`visibleSelection`) les retire
       * tout seul, et le compte suit. Ce ne sont pas deux regles, c'est la meme,
       * tenue au meme endroit : on garde ce qu'on voit encore.
       *
       * La comparaison porte sur l'IDENTITE de `filterIds` : `nextQuery`
       * recopie la reference telle quelle quand le patch ne la touche pas
       * (poser un tri ne fabrique pas un tableau de filtres neuf).
       */
      if (next.filterIds !== previous.filterIds || next.archivesMode !== previous.archivesMode) {
        dropSelection();
      }
      setQueryState((current) => nextQuery(current, patch, server));
      if ((before === 0 && after > 0) || entersArchives) setDrawerOpenState(true);
    },
    [dropSelection],
  );

  const setQuery = useCallback(
    (patch: Partial<ScreenQuery>): void => {
      /*
       * LES CHAMPS A TAGS PASSENT PAR `mutateTags` : une vue qui patche le tri
       * ou le filtre (contrat `onQueryChange`, l'en-tete du tableau patche
       * `sort`) pose un tag en zone 4a, et la regle du tiroir ("il s'ouvre au
       * PREMIER tag pose, quel que soit le geste") vaut pour ce chemin aussi.
       * Sans ce detour, le premier clic d'en-tete posait son tag dans un
       * tiroir ferme : seul le point d'encre de l'outil tri bougeait, et rien
       * ne montrait ce que le clic venait de creer. Ecran sans portes :
       * ecriture directe, le tiroir n'y existe pas.
       */
      if (
        doorsRef.current.length > 0 &&
        ('sort' in patch ||
          'sortStack' in patch ||
          'filterId' in patch ||
          'filterIds' in patch ||
          // Le mode Archives pose lui aussi un tag : par le meme chemin.
          'archivesMode' in patch)
      ) {
        mutateTags(() => patch);
        return;
      }
      /*
       * ECRAN SANS PORTES : le detour par `mutateTags` n'existe pas, donc la
       * chute de la selection se pose ici, sur les MEMES champs (la tranche et
       * le jeu), jamais sur le tri. Les deux chemins tiennent la meme regle ;
       * celui du haut la tient par `mutateTags`.
       */
      if (
        'search' in patch ||
        'filterId' in patch ||
        'filterIds' in patch ||
        'archivesMode' in patch ||
        'section' in patch
      ) {
        dropSelection();
      }
      setQueryState((previous) => nextQuery(previous, patch, paginationRef.current === 'server'));
    },
    [mutateTags, dropSelection],
  );

  const setSearch = useCallback(
    (value: string): void => {
      // CHERCHER FAIT TOMBER LA SELECTION, entierement : la recherche change
      // ce qu'on voit, et on agit sur ce qu'on voit. Gratuit quand il n'y a
      // rien a vider (voir `dropSelection`), donc sans effet sur la frappe.
      dropSelection();
      setQueryState((previous) => ({ ...previous, search: value }));
    },
    [dropSelection],
  );

  /** Lecture V1 : la pastille a choix unique REMPLACE l'etat. */
  const setFilter = useCallback(
    (id: string): void => {
      mutateTags(() => ({ filterIds: [id] }));
    },
    [mutateTags],
  );

  const toggleFilter = useCallback(
    (id: string): void => {
      // Verifie contre le catalogue de la porte : un id inconnu poserait un
      // tag qui ne filtre rien, l'etat actif menteur qu'on refuse deja aux
      // adresses.
      if (!filterDefsRef.current.some((def) => def.id === id)) {
        if (import.meta.env.DEV) {
          console.warn(
            `[taki/views] toggleFilter("${id}") : cet id n'est pas dans le catalogue ` +
              'de filtres de la porte active. Rien n\'est pose.',
          );
        }
        return;
      }
      mutateTags((previous) => ({
        filterIds: previous.filterIds.includes(id)
          ? previous.filterIds.filter((other) => other !== id)
          : [...previous.filterIds, id],
      }));
    },
    [mutateTags],
  );

  const removeFilter = useCallback(
    (id: string): void => {
      mutateTags((previous) =>
        previous.filterIds.includes(id)
          ? { filterIds: previous.filterIds.filter((other) => other !== id) }
          : null,
      );
    },
    [mutateTags],
  );

  const clearFilters = useCallback((): void => {
    mutateTags((previous) => (previous.filterIds.length === 0 ? null : { filterIds: [] }));
  }, [mutateTags]);

  /** Popover de tri : ajoute un niveau ASCENDANT en bout de pile, ou le retire. */
  const toggleSort = useCallback(
    (key: string): void => {
      mutateTags((previous) => ({
        sortStack: previous.sortStack.some((levelSort) => levelSort.key === key)
          ? previous.sortStack.filter((levelSort) => levelSort.key !== key)
          : [...previous.sortStack, { key, dir: 'asc' as const }],
      }));
    },
    [mutateTags],
  );

  /** Le corps d'un tag de tri, et le second clic d'un en-tete de colonne. */
  const invertSort = useCallback(
    (key: string): void => {
      mutateTags((previous) => {
        if (!previous.sortStack.some((levelSort) => levelSort.key === key)) return null;
        return {
          sortStack: previous.sortStack.map((levelSort) =>
            levelSort.key === key
              ? { key, dir: levelSort.dir === 'asc' ? ('desc' as const) : ('asc' as const) }
              : levelSort,
          ),
        };
      });
    },
    [mutateTags],
  );

  const removeSort = useCallback(
    (key: string): void => {
      mutateTags((previous) =>
        previous.sortStack.some((levelSort) => levelSort.key === key)
          ? { sortStack: previous.sortStack.filter((levelSort) => levelSort.key !== key) }
          : null,
      );
    },
    [mutateTags],
  );

  const clearSort = useCallback((): void => {
    mutateTags((previous) => (previous.sortStack.length === 0 ? null : { sortStack: [] }));
  }, [mutateTags]);

  /**
   * "Effacer filtres" (zone 4a) : les filtres ET la pile de tri d'un coup.
   *
   * UN SEUL patch, donc UN SEUL rendu et UNE SEULE ecriture d'adresse. Enchainer
   * `clearFilters()` puis `clearSort()` ferait deux passages dans `mutateTags`,
   * donc deux setState en cascade : la liste se reprojette deux fois (elle
   * clignote, l'etat intermediaire "plus de filtres mais encore un tri" est
   * visible) et le parametre `f` de l'adresse est reecrit deux fois. C'est le
   * meme patron qu'`applyFavorite`, qui pose lui aussi les deux champs en une
   * passe.
   *
   * `null` quand il n'y a rien a vider : aucun rendu, et surtout aucune fausse
   * transition de tiroir. Rien d'autre ne bouge : ni la recherche, ni la porte,
   * ni la fiche ouverte.
   */
  const clearAll = useCallback((): void => {
    mutateTags((previous) =>
      previous.filterIds.length === 0 && previous.sortStack.length === 0 && !previous.archivesMode
        ? null
        : { filterIds: [], sortStack: [], archivesMode: false },
    );
  }, [mutateTags]);

  /**
   * LE MODE ARCHIVES (chantier 2_0). Il passe par `mutateTags` comme les
   * filtres et le tri, et pas par `setQueryState` : c'est ce qui lui donne
   * gratuitement les trois comportements du tiroir et de la requete, sans en
   * recoder aucun (l'ouverture du tiroir au premier tag pose, la peremption
   * des pages en pagination serveur, l'ecriture de l'adresse).
   *
   * Il ne touche a RIEN d'autre : ni la recherche, ni la porte, ni la fiche
   * ouverte, ni les filtres poses. Les filtres survivent VOLONTAIREMENT a
   * l'aller-retour : ils ne s'appliquent pas dans les archives, mais les vider
   * en entrant obligerait a les reposer un par un en sortant, alors que le
   * mode est fait pour qu'on y passe une seconde.
   */
  const setArchivesMode = useCallback(
    (on: boolean): void => {
      mutateTags((previous) => (previous.archivesMode === on ? null : { archivesMode: on }));
    },
    [mutateTags],
  );

  /** L'etoile (3b) : le tiroir a la demande. */
  const setDrawerOpen = useCallback((open: boolean): void => {
    setDrawerOpenState(open);
  }, []);

  /* ---- Favoris : appliquer, garder, oublier ---- */

  const applyFavorite = useCallback(
    (id: string): void => {
      const favorite = favoritesRef.current.find((candidate) => candidate.id === id);
      if (favorite == null) return;
      // Les filtres d'un favori garde hier peuvent citer un id retire du
      // catalogue depuis : on ne pose que ce qui existe. L'etat obtenu ne
      // correspondra alors plus exactement au favori, qui ne s'allumera pas :
      // honnete, et visible, plutot qu'un tag mort.
      const defs = filterDefsRef.current;
      const filterIds = favorite.filterIds.filter((filterId) =>
        defs.some((def) => def.id === filterId),
      );
      mutateTags(() => ({
        filterIds,
        sortStack: favorite.sortStack.map((levelSort) => ({ ...levelSort })),
      }));
    },
    [mutateTags],
  );

  const keepFavorite = useCallback((): void => {
    if (!canKeepFavoriteRef.current) return;
    const current = queryRef.current;
    // Nom auto SIMPLE : "Favori <n>", premier numero libre. L'id suit le meme
    // numero, prefixe pour ne jamais croiser un id de preset.
    const existing = favoritesRef.current;
    let number = 1;
    while (existing.some((favorite) => favorite.id === `user-${number}`)) number += 1;
    const kept: ScreenFavorite = {
      id: `user-${number}`,
      label: `Favori ${number}`,
      // Copies defensives : l'etat continue de vivre, pas le favori.
      filterIds: [...current.filterIds],
      sortStack: current.sortStack.map((levelSort) => ({ ...levelSort })),
    };
    setStoredFavoritesRef.current([...storedFavoritesRef.current, kept]);
  }, []);

  const forgetFavorite = useCallback((id: string): void => {
    const stored = storedFavoritesRef.current;
    const next = stored.filter((favorite) => favorite.id !== id);
    // Un id de preset (ou inconnu) ne change rien : les presets appartiennent
    // a la porte, seule la liste gardee s'oublie.
    if (next.length !== stored.length) setStoredFavoritesRef.current(next);
  }, []);

  /**
   * LES DEUX BORDS DE LA SESSION DE FICHE. La pile d'annulation et
   * `saveState` vivent le temps d'UNE fiche ouverte : ils survivent aux fleches
   * precedent / suivant (on continue de travailler sur le meme jeu), et ils
   * tombent aux deux bouts.
   *
   * A LA SORTIE : "Enregistre a 14:32" n'a aucun sens de retour en collection,
   * et `Cmd+Z` ne doit pas defaire depuis la collection une modification
   * invisible a l'ecran.
   *
   * A L'OUVERTURE : une fiche qu'on vient d'ouvrir n'a rien enregistre. Le
   * kanban ecrit au niveau COLLECTION (`onPatchRecord(id, 'groupKey', ...)`) :
   * sans ce nettoyage, deposer une carte puis ouvrir la fiche d'un AUTRE
   * element afficherait aussitot "Enregistre a 14:32" (la spec exige `idle`,
   * rien du tout, avant la premiere ecriture), et `Cmd+Z` depilerait le pas du
   * kanban, teleporterait sur la carte deplacee et n'annulerait rien (le role
   * `groupKey` n'est pas declare dans le binding de la fiche).
   *
   * Le jeton de session change ici : ce qui atterrit apres ne parle plus.
   */
  const resetRecordSession = useCallback((): void => {
    recordSessionRef.current += 1;
    setUndoStack([]);
    setRedoStack([]);
    undoRef.current = [];
    redoRef.current = [];
    failedRef.current = null;
    // Le geste de fiche rate meurt avec la session, comme l'ecriture ratee :
    // proposer de rejouer un archivage sur la fiche SUIVANTE serait pire que
    // de ne rien proposer.
    failedRecordActionRef.current = null;
    setSaveState({ mode: 'idle' });
  }, []);

  const openRecord = useCallback((id: string | null): void => {
    if (id == null) {
      setOpenId(null);
      setFrozenIds(null);
      resetRecordSession();
      // Sortie de la fiche : retour a la derniere vue collection.
      setViewIdState(lastCollectionRef.current);
      return;
    }
    // OUVRIR, C'EST LIRE : la pastille "Nouveau" s'eteint IMMEDIATEMENT, sans
    // attendre le chargement de la fiche. Attendre la donnee laisserait la
    // ligne allumee pendant l'aller-retour, et une ouverture qui echoue
    // laisserait croire que le geste n'a pas compte. `openRecord(null)` est la
    // FERMETURE de la fiche : elle est deja repartie plus haut, elle ne marque
    // rien (sans quoi refermer eteindrait une ligne qu'on n'a pas ouverte).
    markReadRef.current(id);
    // On ENTRE dans la fiche quand la vue active n'est pas encore de niveau
    // `record` : c'est une session NEUVE. Naviguer au curseur (on y est deja)
    // reste la MEME session, sinon les fleches videraient la pile a chaque pas.
    const current = typesRef.current.find((type) => type.id === viewIdRef.current);
    const entering = current?.level !== 'record';
    if (entering) resetRecordSession();
    else if (openIdRef.current !== id) {
      /*
       * CHANGER DE FICHE AU CURSEUR DESARME LA RELANCE, meme si la SESSION,
       * elle, continue (les fleches gardent volontairement la pile
       * d'annulation : on travaille sur le meme jeu).
       *
       * Un geste de fiche et une ecriture de cellule portent sur UN document
       * NOMME, jamais sur celui qu'on regarde : `retrySave` rejoue la cible
       * memorisee. Sans cette remise a zero, le bandeau rouge et son
       * "Reessayer" survivaient au passage a la fiche SUIVANTE, et cliquer
       * dessus en croyant relancer la fiche affichee rejouait le `remove` de
       * la PRECEDENTE : supprimee definitivement, sans fenetre, sans que rien
       * a l'ecran ne l'ait designee.
       *
       * La relance n'existe donc que tant que la fiche fautive est celle qu'on
       * a sous les yeux. La trace console, elle, reste : l'echec n'est pas
       * efface, il cesse d'etre rejouable a l'aveugle.
       */
      failedRecordActionRef.current = null;
      failedRef.current = null;
      setSaveState({ mode: 'idle' });
    }
    // Le curseur ne se gele qu'a l'OUVERTURE, jamais en naviguant dedans.
    if (openIdRef.current == null || frozenRef.current == null) {
      const ids = orderedIdsRef.current;
      setFrozenIds(ids.length > 0 ? freezeIds(ids) : null);
    }
    setOpenId(id);
    if (entering) {
      const recordType = typesRef.current.find((type) => type.level === 'record');
      if (recordType) setViewIdState(recordType.id);
    }
  }, [resetRecordSession]);

  /**
   * La MEME marque que celle d'`openRecord`, offerte a l'ecran : une fiche
   * qu'on vient de creer soi-meme n'est pas une nouveaute pour soi. Callback
   * STABLE, comme tout ce que l'ecran met en dependance : la ref porte la
   * porte et la memoire du moment, cette fonction ne fait que la relayer.
   */
  const markRead = useCallback((id: string): void => {
    markReadRef.current(id);
  }, []);

  /**
   * CHANGER DE SECTION (zone 1). Deux effets, et le second n'est pas cosmetique :
   * la fiche ouverte se REFERME. Une section segmente les donnees, donc
   * l'element ouvert n'appartient pas forcement a celle qu'on vient de choisir ;
   * le garder affiche montrerait une fiche que la liste d'a cote ne contient
   * pas, et le curseur ("3 / 47") compterait dans un jeu auquel elle n'est plus.
   */
  const applySection = useCallback((id: string): void => {
    if (queryRef.current.section === id) return;
    // Une section segmente les donnees : les lignes choisies appartiennent a
    // celle qu'on quitte. Meme raison qu'au changement de porte, ou la
    // selection tombe deja (`openDoor`).
    dropSelection();
    setQueryState((previous) =>
      // LE MODE ARCHIVES S'ETEINT ICI AUSSI, comme il s'eteint au changement de
      // porte (`openDoor`) : changer de section, c'est changer de jeu de
      // donnees, et rien de l'etat de tranche ne survit a ce geste. C'est le
      // SECOND verrou de la nasse du chantier 2_0 : sur un ecran sans portes,
      // aucune surface ne montre ni n'eteint le mode, donc si quoi que ce soit
      // parvenait a l'y allumer (une app qui force `setQuery`), un changement
      // de section reste une sortie. Le premier verrou, lui, l'empeche de
      // s'allumer par l'adresse (voir `initialArchives`).
      nextQuery(previous, { section: id, archivesMode: false }, paginationRef.current === 'server'),
    );
  }, [dropSelection]);

  /**
   * CHANGER DE PORTE (gabarit v2). Un AUTRE JEU DE DONNEES : rien de l'etat de
   * tranche ne survit. Dans l'ordre :
   * - la fiche se referme (elle appartient a l'ancien jeu), et la vue
   *   d'atterrissage est REVALIDEE contre le catalogue de la nouvelle porte
   *   (deux portes n'ont pas forcement les memes vues) ;
   * - la selection, les lignes chargees et l'element solo tombent : laisser
   *   les lignes de l'ancienne porte a l'ecran pendant le rechargement les
   *   ferait projeter par les bindings de la nouvelle, un melange qu'aucun
   *   message n'explique ;
   * - recherche, filtres, pile de tri et MODE ARCHIVES sont REMIS A ZERO (ce
   *   que l'adresse impose, lui, s'applique par-dessus : c'est le cas du lien
   *   partage et du bouton Retour) ;
   * - le tiroir se replie, les actions, stats et placeholder suivent la porte
   *   par construction (`state.door` se derive de `query.section`).
   * Le rechargement, lui, est automatique : la section fait partie de la cle
   * de chargement, exactement comme avant les portes.
   */
  const openDoor = useCallback(
    (id: string, nextFilterIds: string[], nextArchives: boolean): void => {
      const door = doorsRef.current.find((candidate) => candidate.id === id);
      // Le menu n'offre que le catalogue, et l'adresse inconnue est retombee
      // plus tot : un id inconnu ici est un appel programmatique errone, on ne
      // demonte pas l'ecran pour lui.
      if (door == null) return;
      const cameFromRecord = openIdRef.current != null;
      if (cameFromRecord) openRecord(null);
      // Les refs de FICHE suivent tout de suite (les etats, eux, attendent le
      // rendu) : quand l'adresse enchaine "changer de porte" et "ouvrir une
      // fiche" dans le meme tick (lien partage, bouton Suivant), la reouverture
      // doit repartir d'une fiche FERMEE et d'un jeu VIDE. Sans ca, elle se
      // croyait deja ouverte (meme id via deux portes : la fiche restait
      // fermee) ou gelait son curseur sur le jeu ordonne de l'ANCIENNE porte ;
      // avec un jeu vide, le gel differe attend les donnees de la nouvelle,
      // exactement le chemin de l'entree a froid.
      openIdRef.current = null;
      frozenRef.current = null;
      orderedIdsRef.current = [];
      // La vue d'atterrissage : celle qu'on regardait (ou la sortie de fiche),
      // GARDEE si la nouvelle porte la propose (deux portes qui partagent
      // liste + tableau ne sautent pas de vue), sinon la vue d'arrivee
      // declaree par la porte, sinon sa premiere collection.
      const base = optionsRef.current;
      const nextTypes = door.types ?? base.types;
      const landing = cameFromRecord ? lastCollectionRef.current : viewIdRef.current;
      if (!nextTypes.some((type) => type.id === landing)) {
        const declaredType =
          door.defaultView != null
            ? nextTypes.find((type) => type.id === door.defaultView)
            : undefined;
        const fallback =
          (declaredType?.level === 'collection' ? declaredType.id : null) ??
          nextTypes.find((type) => type.level === 'collection')?.id ??
          nextTypes[0]?.id ??
          FALLBACK_VIEW_ID;
        setViewIdState(fallback);
        // La memoire de sortie de fiche suit : elle designerait sinon une vue
        // que cette porte n'a pas. La ref tout de suite, `openRecord` la lit.
        setLastCollectionViewId(fallback);
        lastCollectionRef.current = fallback;
      }
      // LES REFS DE CATALOGUE SUIVENT TOUT DE SUITE, comme `lastCollectionRef` :
      // la suite du MEME geste raisonne deja avec la nouvelle porte. Le cas qui
      // l'exige : le bouton Suivant du navigateur vers la fiche d'une autre
      // porte, ou `fromSearch` enchaine openDoor puis openRecord dans le meme
      // tick ; openRecord choisit la vue fiche dans `typesRef`, qui pointerait
      // sinon l'ancien catalogue jusqu'au rendu suivant. Le rendu re-assigne
      // ensuite les memes valeurs, sans rien y changer.
      typesRef.current = nextTypes;
      // La PORTE elle-meme suit ici : `markReadRef.current` la lit pour savoir
      // sous quelle memoire ranger la fiche qu'on ouvre. Sans cette ligne, le
      // bouton Suivant du navigateur vers la fiche d'une AUTRE porte la
      // marquait lue sous l'ancienne, et elle restait nouvelle dans la sienne.
      doorIdRef.current = id;
      bindingsRef.current = door.bindings ?? base.bindings;
      loadRef.current = door.load ?? base.load;
      getOneRef.current = door.get ?? base.get;
      filterDefsRef.current =
        door.sections === false ? [] : (door.filters ?? base.filters ?? []);
      // Les trois verbes de la fiche suivent la porte cible dans le MEME tick,
      // au meme titre que le chargeur : une fiche rouverte par l'adresse
      // derriere la nouvelle porte doit deja lire les gestes de celle-la.
      archiveRef.current = door.archive ?? base.archive;
      unarchiveRef.current = door.unarchive ?? base.unarchive;
      removeRef.current = door.remove ?? base.remove;
      setSelection([]);
      setRecords([]);
      setHasMore(false);
      setSolo(null);
      // Le chargement neuf part au prochain commit (la section est dans la cle
      // de chargement), mais `inFlight` passe a vrai TOUT DE SUITE : sans ca,
      // la frame entre ce rendu et l'effet montrerait "Rien pour l'instant"
      // (lignes vides, aucun chargement en vol), un etat vide mensonger le
      // temps d'un battement. Meme raison pour l'erreur : celle de l'ancienne
      // porte ne doit pas s'afficher une frame au-dessus de la nouvelle.
      setError(undefined);
      inFlightRef.current = true;
      setInFlight(true);
      setQueryState((previous) =>
        nextQuery(
          previous,
          {
            section: id,
            search: '',
            filterIds: nextFilterIds,
            sortStack: [],
            // La FENETRE ("Afficher plus") et l'INTERVALLE du calendrier
            // retombent aussi : un autre jeu de donnees ne garde rien de
            // l'etat de tranche precedent. `nextQuery` marque la peremption
            // serveur tout seul (`limit` et `range` sont dans sa liste stale).
            limit:
              optionsRef.current.limit ??
              (paginationRef.current === 'server' ? DEFAULT_PAGE_SIZE : DEFAULT_LIMIT),
            range: null,
            // LE MODE ARCHIVES S'ETEINT AU CHANGEMENT DE PORTE, comme tout le
            // reste de l'etat de tranche : un autre jeu de donnees ne garde
            // rien du precedent. Il ne reste allume que si l'ADRESSE le
            // demande (lien partage, bouton Retour), et il est alors pose ici
            // dans le meme patch, sans rechargement de plus.
            archivesMode: nextArchives,
          },
          paginationRef.current === 'server',
        ),
      );
      // Replie par defaut derriere une porte neuve ; OUVERT quand l'adresse
      // pose deja des tags (filtres, ou le mode Archives), pour que le
      // destinataire d'un lien voie ce qui explique sa liste.
      setDrawerOpenState(nextFilterIds.length > 0 || nextArchives);
    },
    [openRecord],
  );

  const setSection = useCallback(
    (id: string): void => {
      if (queryRef.current.section === id) return;
      // Avec des portes, changer de section EST changer de porte. Sans, le
      // comportement historique ne bouge pas d'un cheveu : les sections
      // restent des tranches du meme jeu, recherche et filtres survivent.
      if (doorsRef.current.length > 0) {
        // Un clic de menu n'apporte aucun etat : ni filtre, ni mode.
        openDoor(id, [], false);
        return;
      }
      if (openIdRef.current != null) openRecord(null);
      applySection(id);
    },
    [applySection, openRecord, openDoor],
  );

  const setView = useCallback((id: ViewId): void => {
    const type = typesRef.current.find((candidate) => candidate.id === id);
    if (type == null) return;
    if (type.level === 'record') {
      // Fiche sans element ouvert : on ouvre le premier du jeu ordonne
      // courant. Entree legitime, pas un accident.
      if (openIdRef.current == null) {
        const first = firstOf(orderedIdsRef.current);
        if (first == null) return; // Collection vide : l'entree est grisee.
        setFrozenIds(freezeIds(orderedIdsRef.current));
        setOpenId(first);
      }
      // Le SELECTEUR est la seconde ENTREE dans la fiche : session NEUVE, au
      // meme titre qu'un clic sur une ligne (cf. `resetRecordSession`). Rien a
      // faire si on y est deja : on ne coupe pas la session en cours.
      const current = typesRef.current.find((candidate) => candidate.id === viewIdRef.current);
      if (current?.level !== 'record') resetRecordSession();
    } else {
      // `lastCollectionViewId` n'est ecrit QUE par une vue collection : la
      // fiche ne peut pas corrompre la memoire de sortie. La vue collection
      // choisie devient aussi la vue par defaut au prochain passage.
      setLastCollectionViewId(id);
      setPrefViewRef.current(id);
      // Retour au niveau COLLECTION : le gel du curseur tombe. Sans ca, la
      // prochaine ouverture rejouerait la liste d'ids de la premiere fiche de
      // la session, et "3 / 47" designerait un rang de l'ancien jeu (ou
      // resterait a ".. / .."). Le gel se refait a l'ouverture suivante, sur le
      // jeu courant. La ref suit tout de suite : `openRecord` la lit.
      frozenRef.current = null;
      setFrozenIds(null);
      // Le SELECTEUR est la seconde sortie de la fiche, et elle ne passe pas
      // par `openRecord(null)` : `openId` reste pose (voir le commentaire
      // au-dessus). La session de fiche se termine quand meme, sinon la pile
      // d'annulation survivrait a une fiche qu'on ne voit plus, et `Cmd+Z`
      // defairait depuis la collection une modification invisible.
      resetRecordSession();
    }
    setViewIdState(id);
  }, [resetRecordSession]);

  /**
   * LE RECHARGEMENT NU : il relit la porte et ne touche a rien d'autre.
   *
   * Il existe a part parce qu'un lot termine recharge la porte SANS vider la
   * selection : les fiches modifiees ont peut-etre quitte la vue (le lot vient
   * peut-etre de changer le champ sur lequel l'ecran filtre ou trie), et c'est
   * l'INVARIANT qui doit alors les retirer du lot, une par une, en laissant
   * celles qui sont encore la. La barre de lot se referme ainsi d'elle-meme
   * quand elle se vide, au lieu de se refermer a tous les coups.
   */
  const reloadPort = useCallback((): void => {
    // Mode SERVEUR : recharger, c'est repartir de la premiere page. Garder le
    // curseur relirait la DERNIERE page seulement, et une modification faite
    // page 1 resterait invisible. Les deux setState sont groupes par React :
    // un seul chargement part.
    if (paginationRef.current === 'server') {
      setQueryState((previous) => (previous.after == null ? previous : { ...previous, after: null }));
    }
    setReloadTick((tick) => tick + 1);
  }, []);

  /**
   * RECHARGER, le geste offert a l'ecran et au bouton "Reessayer" du ViewHost.
   * Il fait tomber la selection, comme changer de porte ou de filtre : on
   * redemande le jeu au serveur, donc plus rien ne garantit que les lignes
   * qu'on avait cochees soient encore la ni encore les memes.
   */
  const reload = useCallback((): void => {
    dropSelection();
    reloadPort();
  }, [dropSelection, reloadPort]);

  /**
   * Rafraichir UN element dans le jeu, sans toucher a la pagination.
   *
   * C'est ce qui remplace le rechargement complet apres une ecriture en mode
   * serveur : relire toute la liste ferait retomber l'utilisateur en page 1
   * apres chaque champ modifie, ce qui est intenable des qu'on travaille au
   * fond d'une longue liste. Une lecture d'un document, la ligne est a jour.
   * Sans `get()` fourni par l'ecran, on n'invente rien : l'optimisme reste
   * affiche et la prochaine navigation fera foi.
   */
  const refreshRecord = useCallback(async (id: string): Promise<boolean> => {
    const getOne = getOneRef.current;
    if (getOne == null) return false;
    try {
      const fresh = await getOne(id);
      if (fresh == null) return false;
      let applied = false;
      setRecords((previous) => {
        const index = previous.findIndex((candidate) => getIdRef.current(candidate) === id);
        if (index < 0) return previous;
        const next = [...previous];
        next[index] = fresh;
        applied = true;
        return next;
      });
      return applied;
    } catch {
      /* Relecture ratee : l'ecriture, elle, est passee. On garde l'optimisme. */
      return false;
    }
  }, []);

  /**
   * LE CHEMIN D'ECRITURE UNIQUE, roles et colonnes confondus.
   *
   * `patchRecord` et `patchColumn` ne sont que deux portes vers ici : dupliquer
   * ce corps, c'est se garantir deux comportements qui divergent au premier
   * ajustement (le jeton de sequence d'un cote, pas de l'autre).
   *
   * L'ordre des gestes n'est pas negociable :
   * 1. valeur INCHANGEE : on sort sans rien ecrire et sans coche (une fausse
   *    coche serait un mensonge) ;
   * 2. jeton de sequence PAR CIBLE : une ecriture qui revient alors qu'une plus
   *    recente est partie sur la meme cible ne fait plus rien du tout ;
   * 3. echec : on remet la valeur en place ET on le DIT (`saveState.error` +
   *    `retrySave`), au lieu de la faire disparaitre dans la console ;
   * 4. succes : `saveState.saved` D'ABORD, `reload()` ENSUITE. L'inverse
   *    remettrait la zone 5 en chargement pendant que la coche s'affiche ;
   * 5. JAMAIS de rechargement sur echec : il ramenerait l'ancienne valeur,
   *    ecraserait le rollback, et l'utilisateur ne verrait pas son erreur ;
   * 6. `saveState` porte la CIBLE (`SaveTarget`) a chaque transition : c'est
   *    ce qui permet a la fiche de rendre la coche ou le cadre rouge sur la
   *    bonne case quand deux ecritures se chevauchent ;
   * 7. ce qui atterrit HORS de la session de fiche qui l'a demande ne touche ni
   *    `saveState`, ni la pile, ni l'echec rejouable. Le rollback et le
   *    rechargement, eux, valent toujours : ce sont des faits sur la DONNEE,
   *    pas des messages a l'utilisateur.
   */
  const commitWrite = useCallback(
    async (request: WriteRequest, history: 'push' | 'skip'): Promise<void> => {
      const { kind, id, key, value } = request;
      const target: SaveTarget = { kind, id, key };
      // La session au moment du DEPART : on la compare au retour (point 7).
      const session = recordSessionRef.current;
      const sameSession = (): boolean => recordSessionRef.current === session;
      const binding = bindingsRef.current.find((candidate) => candidate.type === viewIdRef.current);
      const field = kind === 'role' ? rolesOf(binding)[key] : undefined;
      const column: Column<T> | undefined =
        kind === 'column' ? binding?.columns?.find((candidate) => candidate.key === key) : undefined;

      /*
       * La cible : un ROLE (`FieldMap`) ou une COLONNE (`Column`). Les deux
       * portent un `set()` de signature identique, c'est exactement ce qui
       * permet aux deux entrees de partager ce chemin. On garde l'OBJET plutot
       * que d'extraire la fonction : un binding qui ecrit `set()` en methode
       * garderait ainsi son `this`.
       */
      const source: FieldMap<T> | Column<T> | undefined = field ?? column;
      if (source?.set == null) {
        // Pas de `set()` : pas d'ecriture. Sans affordance mensongere en amont,
        // ce cas ne devrait jamais arriver ; il reste sur, pas silencieux.
        const what = kind === 'role' ? 'le role' : 'la colonne';
        console.warn(`[taki/views] Ecriture refusee : ${what} ${key} n'expose pas set().`);
        return;
      }

      const record =
        recordsRef.current.find((candidate) => getIdRef.current(candidate) === id) ??
        (soloRef.current?.id === id ? soloRef.current.record : undefined);
      if (record === undefined) {
        // Arrive quand on annule un pas fait sur un element qui a quitte la
        // memoire (fiche ouverte a froid, puis navigation ailleurs).
        console.warn(`[taki/views] Ecriture impossible : ${id} n'est plus en memoire.`);
        return;
      }

      const bag = kind === 'role' ? patchesRef.current : columnPatchesRef.current;
      const setBag = kind === 'role' ? setPatches : setColumnPatches;
      const stored = field != null ? field.get(record) : (column?.value(record) ?? null);
      const before = currentValueOf(bag, id, key, stored);

      // (1) Rien n'a change : on referme sans ecrire, donc sans coche.
      if (sameCell(before, value)) return;

      // (2) Le jeton se pose AVANT le depart : c'est lui qui dira, au retour, si
      // une ecriture plus recente est passee devant sur la MEME cible.
      const seqKey = writeKeyOf(kind, id, key);
      const token = (sequenceRef.current.get(seqKey) ?? 0) + 1;
      sequenceRef.current.set(seqKey, token);

      // On ne retient QUE la valeur de CETTE clef, jamais le sac entier de la
      // ligne : deux champs d'une meme fiche s'ecrivent couramment coup sur
      // coup, et restaurer le sac complet ferait disparaitre de l'ecran la
      // saisie voisine dont l'ecriture est encore en vol.
      const previousCell = bag[id]?.[key];
      setBag((current) => ({ ...current, [id]: { ...current[id], [key]: value } }));
      setSaveState({ mode: 'saving', target });
      pendingWritesRef.current += 1;

      try {
        await source.set(record, value);
      } catch (cause) {
        if (sequenceRef.current.get(seqKey) !== token) return;
        // (3) Echec : on remet la ligne en place. Une carte qui revient toute
        // seule dans sa colonne est le comportement voulu, un mensonge ne l'est
        // pas. La difference avec avant : ca se VOIT dans la rangee 3.
        setBag((current) => {
          const row = current[id];
          if (row === undefined) return current;
          const nextRow = { ...row };
          if (previousCell === undefined) delete nextRow[key];
          else nextRow[key] = previousCell;
          const next = { ...current };
          // Sac de ligne vide : on retire l'entree, sinon la projection
          // repasserait sur une ligne qui n'a plus rien a patcher.
          if (Object.keys(nextRow).length === 0) delete next[id];
          else next[id] = nextRow;
          return next;
        });
        console.error(`[taki/views] Ecriture echouee sur ${id}.${key} :`, cause);
        // (7) Session terminee : afficher "Echec de l'enregistrement" et un
        // "Reessayer" sur la fiche suivante designerait une ecriture qui ne la
        // concerne pas. La trace console, elle, reste.
        if (!sameSession()) return;
        failedRef.current = request;
        // Le plus RECENT des deux echecs gagne : un geste de fiche rate avant
        // celui-ci ne doit pas voler le bouton "Reessayer" a cette saisie-la.
        failedRecordActionRef.current = null;
        setSaveState({ mode: 'error', target });
        return;
      } finally {
        pendingWritesRef.current -= 1;
      }

      if (sequenceRef.current.get(seqKey) !== token) return;

      // (4) Succes. Tout ce qui s'ADRESSE a l'utilisateur (indicateur, pile
      // d'annulation, echec rejouable) appartient a la session qui a demande
      // l'ecriture, et a elle seule (point 7).
      if (sameSession()) {
        failedRef.current = null;
        setSaveState({ mode: 'saved', at: new Date(), target });

        if (history === 'push') {
          // On empile APRES coup : on ne propose jamais d'annuler ce qui n'a
          // pas ete ecrit. Les refs suivent tout de suite, le clavier les lit
          // avant le prochain rendu.
          const step: WriteStep = { ...request, before };
          const grown = [...undoRef.current, step];
          const capped =
            grown.length > UNDO_LIMIT ? grown.slice(grown.length - UNDO_LIMIT) : grown;
          undoRef.current = capped;
          setUndoStack(capped);
          // Une ecriture neuve coupe la branche du retablissement, comme partout.
          redoRef.current = [];
          setRedoStack([]);
        }
      }

      // Un element ouvert a FROID ne revient jamais par `reload()` : celui-ci
      // recharge la LISTE, et la garde `soloRef.current?.id === openId` empeche
      // tout refetch. On le rafraichit donc a la main. Surtout pas de
      // `setSolo(null)` : la fiche afficherait "introuvable" le temps du
      // rechargement.
      if (soloRef.current?.id === id) {
        const getOne = getOneRef.current;
        if (getOne != null) {
          try {
            const fresh = await getOne(id);
            if (fresh != null) setSolo({ id, record: fresh });
          } catch {
            /* Refetch rate : l'ecriture, elle, est passee. La fiche garde son optimisme. */
          }
        }
      }

      // MODE SERVEUR : relire toute la liste ferait retomber l'utilisateur en
      // page 1 a chaque champ modifie, intenable des qu'on travaille au fond
      // d'une longue liste. On relit la SEULE ligne touchee, puis on retire son
      // patch optimiste : la donnee fraiche a pris le relais.
      if (paginationRef.current === 'server') {
        // SANS `get()`, il n'y a pas de relecture d'une ligne possible. On ne
        // fait alors PAS semblant : on retombe sur le rechargement complet du
        // mode client. La liste revient en page 1, ce qui est desagreable, mais
        // c'est infiniment moins grave que l'alternative silencieuse : des
        // stats de zone 3 fausses pour toujours et les modifications des
        // collegues qui ne remontent plus jamais.
        if (getOneRef.current == null) {
          reload();
          return;
        }
        const applied = await refreshRecord(id);
        // Le patch ne tombe QUE si la relecture a vraiment eu lieu ET qu'aucune
        // ecriture plus recente n'est partie sur la meme cible entre-temps.
        // Le cas vecu que ca protege : ecrire un champ puis faire `Cmd+Z` tout
        // de suite. L'annulation pose son propre patch pendant que la relecture
        // de la premiere ecriture est en vol ; sans ce jeton, elle effacerait le
        // patch de l'annulation et la valeur sauterait sous les doigts avant de
        // revenir. Meme jeton que le reste du chemin d'ecriture, pas un
        // mecanisme parallele.
        const current = sequenceRef.current.get(seqKey);
        if (applied && current === token) {
          const setBag = kind === 'role' ? setPatches : setColumnPatches;
          setBag((previous) => dropPatch(previous, id, key));
        }
        return;
      }

      // MODE CLIENT : la seule parade au multi-utilisateur de ce lot, on relit.
      // Ca ramene les modifications des autres et ca reconcilie les stats de la
      // zone 3, que le core calcule sur les records BRUTS et que l'optimisme ne
      // touche pas.
      reload();
    },
    [reload, refreshRecord],
  );

  const patchRecord = useCallback(
    (id: string, role: string, value: Cell): Promise<void> =>
      commitWrite({ kind: 'role', id, key: role, value }, 'push'),
    [commitWrite],
  );

  const patchColumn = useCallback(
    (id: string, key: string, value: Cell): Promise<void> =>
      commitWrite({ kind: 'column', id, key, value }, 'push'),
    [commitWrite],
  );

  /**
   * LES TROIS GESTES DE FICHE (chantier 2_0) : archiver, desarchiver,
   * supprimer. Un chemin A PART de `commitWrite`, et pour une raison de fond :
   * ceux-la portent sur le DOCUMENT entier, pas sur une case. Il n'y a donc
   * ni valeur d'avant a retenir, ni optimisme a poser (la fiche quitte
   * l'ecran), ni pas d'annulation a empiler (`Cmd+Z` est fait pour les
   * cellules ; annuler une suppression n'est pas une ecriture inverse).
   *
   * Ce que ce chemin garantit, et qu'aucune app n'a donc a recoder :
   * 1. la fiche se REFERME apres coup. Elle vient de quitter le jeu (ou de le
   *    rejoindre) : la garder ouverte laisserait un curseur qui compte "3 / 47"
   *    dans un jeu auquel elle n'appartient plus ;
   * 2. la porte se RECHARGE (`reload`), et c'est le socle qui le fait, jamais
   *    l'app (regle G19 : pas de bouton "Rafraichir", pas de rechargement a la
   *    main) ;
   * 3. l'ecriture REND COMPTE par `saveState` : le rond pendant, l'echec en
   *    rouge avec sa relance. Le succes, lui, n'a pas de coche a montrer : la
   *    fiche s'est refermee, et la ligne qui disparait de la liste est le
   *    meilleur accuse de reception qui soit ;
   * 4. ce qui atterrit HORS de la session de fiche qui l'a demande ne touche
   *    ni l'indicateur ni la fiche a l'ecran (meme regle que `commitWrite`,
   *    point 7). Le rechargement, lui, vaut toujours : c'est un fait sur la
   *    DONNEE, pas un message a l'utilisateur.
   */
  const runRecordAction = useCallback(
    async (kind: RecordActionKind, record: T): Promise<void> => {
      const verb =
        kind === 'archive'
          ? archiveRef.current
          : kind === 'unarchive'
            ? unarchiveRef.current
            : removeRef.current;
      if (verb == null) {
        // L'entree du menu est grisee quand le verbe manque : ce chemin ne
        // devrait pas exister. Il reste sur, pas silencieux. C'est ICI que vit
        // la consigne TECHNIQUE, pas dans l'infobulle du bouton : celle-la
        // s'adresse a qui survole, celle-ci a qui code.
        console.warn(
          `[taki/views] Geste de fiche refuse : la porte active ne declare pas ${kind}(). ` +
            `Pour l'offrir, declare ${kind}(record) sur la porte (ScreenDoor) ou, pour ` +
            "toutes les portes a la fois, sur l'ecran (option de meme nom de useScreen).",
        );
        return;
      }
      const session = recordSessionRef.current;
      const sameSession = (): boolean => recordSessionRef.current === session;
      setSaveState({ mode: 'saving' });
      try {
        await verb(record);
      } catch (cause) {
        console.error(
          `[taki/views] ${kind} a echoue sur ${getIdRef.current(record)} :`,
          cause,
        );
        // Session terminee : afficher l'echec et un "Reessayer" sur la fiche
        // suivante designerait un geste qui ne la concerne pas.
        if (!sameSession()) return;
        failedRef.current = null;
        failedRecordActionRef.current = { kind, record };
        setSaveState({ mode: 'error' });
        return;
      }
      failedRecordActionRef.current = null;
      // La fermeture remet `saveState` a `idle` (elle passe par
      // `resetRecordSession`) : l'indicateur ne vit qu'au niveau fiche, il
      // n'a plus rien a dire une fois de retour en collection.
      if (sameSession()) openRecord(null);
      reload();
    },
    [openRecord, reload],
  );

  /* ------------------------------------------------------------------ */
  /* LES TROIS PRIMITIVES DE LOT (chantier 3_0), a DOCUMENT UNIQUE       */
  /* ------------------------------------------------------------------ */

  /**
   * L'enregistrement BRUT d'un id, cherche aux deux MEMES endroits que la
   * fiche ouverte : le jeu en memoire, puis l'element charge seul. Les trois
   * primitives ecrivent sur le DOCUMENT, elles ont donc besoin du `T` et pas
   * de sa ligne projetee.
   */
  const recordById = useCallback((id: string): T | null => {
    const getId = getIdRef.current;
    const inMemory = recordsRef.current.find((candidate) => getId(candidate) === id);
    if (inMemory !== undefined) return inMemory;
    const soloValue = soloRef.current;
    return soloValue?.id === id ? soloValue.record : null;
  }, []);

  /**
   * LE COMPTEUR D'ECRITURES DE LOT EN VOL, et le rechargement qu'il declenche.
   *
   * Un lot ecrit un document a la fois, donc le compteur repasse par zero
   * entre chaque fiche : recharger a ce moment-la rechargerait la porte douze
   * fois pour douze fiches. On attend donc un souffle apres la DERNIERE
   * ecriture, et toute ecriture qui repart dans cette fenetre annule
   * l'attente.
   *
   * POURQUOI LE CORE LE FAIT LUI-MEME, plutot que de laisser la fenetre de lot
   * appeler `reload()` : un rechargement oublie ne rougit aucun build. La
   * derniere ecriture d'un lot est le seul moment ou l'on SAIT qu'il faut
   * relire, et ce moment est ici.
   */
  const bulkInFlightRef = useRef(0);
  const bulkReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const beginBulkWrite = useCallback((): void => {
    bulkInFlightRef.current += 1;
    if (bulkReloadTimerRef.current != null) {
      clearTimeout(bulkReloadTimerRef.current);
      bulkReloadTimerRef.current = null;
    }
  }, []);

  const endBulkWrite = useCallback((): void => {
    bulkInFlightRef.current = Math.max(0, bulkInFlightRef.current - 1);
    if (bulkInFlightRef.current > 0) return;
    if (bulkReloadTimerRef.current != null) clearTimeout(bulkReloadTimerRef.current);
    bulkReloadTimerRef.current = setTimeout(() => {
      bulkReloadTimerRef.current = null;
      if (bulkInFlightRef.current > 0) return;
      // Le rechargement NU : l'invariant retire du lot les fiches qui ont
      // quitte la vue, et laisse les autres. Voir `reloadPort`.
      reloadPort();
    }, BULK_RELOAD_DELAY_MS);
  }, [reloadPort]);

  // Un lot en vol au demontage de l'ecran ne doit pas laisser un rechargement
  // programme derriere lui.
  useEffect(
    () => () => {
      if (bulkReloadTimerRef.current != null) clearTimeout(bulkReloadTimerRef.current);
    },
    [],
  );

  /**
   * ARCHIVER UNE fiche, et rien d'autre. Pas de boucle, pas de progression,
   * pas de fermeture de fiche, pas de `saveState` : ce chemin sert un LOT, et
   * c'est le moteur de lot qui rend compte.
   *
   * L'ERREUR REMONTE TELLE QUELLE. C'est la seule facon pour l'appelant de
   * NOMMER la fiche tombee, et un lot a moitie ecrit ne se voit dans aucun
   * build vert.
   */
  const archiveOne = useCallback(
    async (id: string): Promise<void> => {
      const verb = archiveRef.current;
      if (verb == null) {
        // Le bouton est grise avec sa raison (`bulkAvailability`) : ce chemin
        // ne devrait pas exister. Il reste sur, pas silencieux. Le message
        // JETE s'adresse a qui lit le compte rendu du lot ; la consigne
        // technique va en console, la ou un developpeur regarde.
        console.warn(
          "[taki/views] Archivage en lot refuse : la porte active ne declare pas archive(). " +
            'Pour l\'offrir, declare archive(record) sur la porte (ScreenDoor) ou, pour toutes ' +
            "les portes a la fois, sur l'ecran (option de meme nom de useScreen).",
        );
        throw new Error(RECORD_ACTION_REASON.archive);
      }
      const record = recordById(id);
      if (record == null) throw new Error("Cette fiche n'est plus dans la liste.");
      beginBulkWrite();
      try {
        await verb(record);
      } finally {
        endBulkWrite();
      }
    },
    [recordById, beginBulkWrite, endBulkWrite],
  );

  /** SUPPRIMER UNE fiche. Meme patron, meme silence, meme remontee d'erreur. */
  const removeOne = useCallback(
    async (id: string): Promise<void> => {
      const verb = removeRef.current;
      if (verb == null) {
        console.warn(
          "[taki/views] Suppression en lot refusee : la porte active ne declare pas remove(). " +
            'Pour l\'offrir, declare remove(record) sur la porte (ScreenDoor) ou, pour toutes ' +
            "les portes a la fois, sur l'ecran (option de meme nom de useScreen).",
        );
        throw new Error(RECORD_ACTION_REASON.remove);
      }
      const record = recordById(id);
      if (record == null) throw new Error("Cette fiche n'est plus dans la liste.");
      beginBulkWrite();
      try {
        await verb(record);
      } finally {
        endBulkWrite();
      }
    },
    [recordById, beginBulkWrite, endBulkWrite],
  );

  /**
   * ECRIRE UN CHAMP SUR UNE fiche. `fieldKey` est la cle OPAQUE d'un
   * `BulkEditField` : elle porte son espace de cles, ce qui empeche une
   * colonne `status` d'aller ecrire le role `status`.
   *
   * CE CHEMIN NE PASSE PAS PAR `commitWrite`, et ce n'est pas un oubli :
   * `commitWrite` avale l'echec (son contrat est de ne jamais rejeter, pour
   * que le kanban puisse faire `void onPatchRecord(...)`), il empile un pas
   * d'annulation par fiche et il recharge la porte a chaque ecriture. Trois
   * comportements justes pour UNE case, ruineux pour douze : `Cmd+Z` defairait
   * un lot une fiche a la fois, le "Reessayer" de la rangee 3 rejouerait une
   * fiche prise au hasard dans le lot, et la porte se rechargerait douze fois.
   */
  const writeCell = useCallback(
    async (id: string, fieldKey: string, value: unknown): Promise<void> => {
      const record = recordById(id);
      if (record == null) throw new Error("Cette fiche n'est plus dans la liste.");
      const cell = asCell(value);
      const binding = bindingRef.current;
      const target = parseBulkFieldKey(fieldKey);
      const role = target.kind === 'column' ? undefined : rolesOf(binding)[target.key];
      const column =
        target.kind === 'role'
          ? undefined
          : binding?.columns?.find((candidate) => candidate.key === target.key);
      // Clef non prefixee : la COLONNE d'abord, le role ensuite (meme priorite
      // que `sortValueOf`). Une clef prefixee, elle, n'a qu'un seul candidat.
      const write = column?.set ?? role?.set;
      if (write == null) {
        throw new Error("Ce champ ne se modifie pas sur cet ecran.");
      }
      beginBulkWrite();
      try {
        await write(record, cell);
      } finally {
        endBulkWrite();
      }
    },
    [recordById, beginBulkWrite, endBulkWrite],
  );

  const retrySave = useCallback((): void => {
    // LE GESTE DE FICHE D'ABORD : c'est le plus recent des deux par
    // construction (chacun efface l'autre en s'inscrivant), et c'est lui que
    // l'echec affiche a l'ecran designe.
    const failedAction = failedRecordActionRef.current;
    if (failedAction != null) {
      void runRecordAction(failedAction.kind, failedAction.record);
      return;
    }
    const request = failedRef.current;
    if (request == null) return;
    // On rejoue la MEME ecriture, telle quelle : l'optimisme ayant ete remis en
    // place a l'echec, `commitWrite` reverra bien une valeur differente.
    void commitWrite(request, 'push');
  }, [commitWrite, runRecordAction]);

  /**
   * Annuler, c'est REECRIRE la valeur d'avant par le meme chemin : meme
   * optimisme, meme rollback, meme indicateur. Jamais un mecanisme parallele qui
   * remettrait une valeur sans passer par le serveur.
   */
  const undo = useCallback((): void => {
    const step = undoRef.current[undoRef.current.length - 1];
    if (step == null) return;
    const remaining = undoRef.current.slice(0, -1);
    undoRef.current = remaining;
    setUndoStack(remaining);
    const grown = [...redoRef.current, step];
    redoRef.current = grown;
    setRedoStack(grown);
    // Le pas vient d'une AUTRE fiche : on y ramene dans le MEME geste, pour
    // qu'on voie ce qu'on defait. Exiger une seconde frappe se lirait comme
    // "Cmd+Z n'a rien fait".
    if (openIdRef.current !== step.id) openRecord(step.id);
    void commitWrite({ kind: step.kind, id: step.id, key: step.key, value: step.before }, 'skip');
  }, [commitWrite, openRecord]);

  const redo = useCallback((): void => {
    const step = redoRef.current[redoRef.current.length - 1];
    if (step == null) return;
    const remaining = redoRef.current.slice(0, -1);
    redoRef.current = remaining;
    setRedoStack(remaining);
    const grown = [...undoRef.current, step];
    undoRef.current = grown;
    setUndoStack(grown);
    if (openIdRef.current !== step.id) openRecord(step.id);
    void commitWrite({ kind: step.kind, id: step.id, key: step.key, value: step.value }, 'skip');
  }, [commitWrite, openRecord]);

  const setViewState = useCallback((state: unknown): void => {
    setViewStates((current) => ({ ...current, [viewIdRef.current]: state }));
  }, []);

  /* ---- Le clavier de la fiche ---- */

  /*
   * LE CLAVIER EST ECOUTE ICI, JAMAIS PAR UNE VUE. Fermer, naviguer, annuler
   * sont des gestes de NAVIGATION et d'ETAT, et une vue ne navigue jamais
   * (c'est la regle absolue du contrat). L'ecoute n'est posee qu'au niveau
   * `record`, le seul ou ces trois gestes ont un sens.
   */
  const cursorRef = useRef(cursor);
  cursorRef.current = cursor;

  useEffect(() => {
    if (level !== 'record') return;

    const onKeyDown = (event: KeyboardEvent): void => {
      /*
       * PREMIERE GARDE, celle du contrat : le focus est dans un editeur, on ne
       * fait RIEN. C'est elle qui rend possibles les deux gestes d'`Echap`
       * (dans un champ il annule la saisie, hors champ il ferme la fiche), et
       * elle qui laisse l'annulation NATIVE du navigateur gagner sur `Cmd+Z`
       * pendant qu'on tape.
       */
      const active = document.activeElement;
      const tag = active?.tagName.toLowerCase();
      if (tag === 'input' || tag === 'select' || tag === 'textarea') return;
      if (active instanceof HTMLElement && active.isContentEditable) return;

      /*
       * SECONDE GARDE : un OVERLAY est ouvert par-dessus la fiche (le `Modal`
       * de creation, le lanceur rapide ou Ask AI du shell). Le `Modal` ecoute
       * deja `Escape` sur `document` sans stopper la propagation
       * (components/Modal.tsx) : sans cette garde, une seule frappe fermerait
       * l'overlay ET la fiche derriere lui. Tous portent `role="dialog"`, c'est
       * le seul signal commun disponible depuis le socle.
       */
      if (document.querySelector('[role="dialog"]') != null) return;

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        openRecord(null);
        return;
      }

      // Le curseur au clavier : exactement le geste des fleches de la fiche, on
      // relit les ids que le core a deja calcules.
      if (event.altKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        const target =
          event.key === 'ArrowUp' ? cursorRef.current.prevId : cursorRef.current.nextId;
        if (target == null) return;
        event.preventDefault();
        openRecord(target);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [level, openRecord, undo, redo]);

  const loadMore = useCallback((): void => {
    if (paginationRef.current === 'server') {
      // UN CHARGEMENT A LA FOIS. Sans cette garde, cliquer "Charger la suite"
      // pendant qu'un chargement est en vol (changement de tri, reprise apres
      // erreur, bascule d'archives) posait un curseur pris sur le jeu de
      // l'ANCIENNE requete : la page 1 de la nouvelle etait annulee, et la
      // liste finissait par montrer les pages d'un tri sous celles d'un autre.
      // Le bandeau reste affiche pendant ce temps, il n'y a rien a masquer :
      // le clic est simplement sans effet, comme un second clic sur un bouton
      // deja parti.
      if (inFlightRef.current) return;
      // Le curseur est le dernier element du JEU BRUT, dans l'ordre rendu par
      // le serveur, jamais la derniere LIGNE affichee : celle-la a traverse la
      // recherche, les pastilles et le tri client, elle n'est pas a la meme
      // place dans l'ordre du serveur, et repartir de la ferait sauter des
      // pages entieres sans que personne ne le voie.
      const last = recordsRef.current[recordsRef.current.length - 1];
      if (last == null) return;
      const cursor = getIdRef.current(last);
      setQueryState((previous) =>
        previous.after === cursor ? previous : { ...previous, after: cursor },
      );
      return;
    }
    // Mode client : agrandir la fenetre en memoire. Le chargeur est rappele
    // avec la nouvelle limite, au cas ou il sache en tirer parti, et c'est
    // justement pourquoi elle est PLAFONNEE : un ecran qui relaie `query.limit`
    // vers `sdk.<famille>.list({ limit })` verrait le SDK lever au-dela de son
    // propre plafond (1000), et l'utilisateur passerait d'une liste qui marche
    // a un ecran d'erreur au clic suivant.
    setQueryState((previous) => ({
      ...previous,
      limit: Math.min(previous.limit + limitDefault, MAX_CLIENT_WINDOW),
    }));
  }, [limitDefault]);

  const setRange = useCallback((from: Date, to: Date): void => {
    // Par `nextQuery` et jamais en ecrivant directement : en mode serveur,
    // changer de mois doit REMPLACER les pages, pas empiler le nouveau mois
    // sous l'ancien. Le calendrier appelle ceci depuis un effet de montage,
    // donc ce chemin est emprunte des la premiere bascule de vue.
    setQueryState((previous) =>
      nextQuery(previous, { range: { from, to } }, paginationRef.current === 'server'),
    );
  }, []);

  /**
   * Le catalogue d'une cle : les ROLES d'abord, les COLONNES ensuite. L'ordre
   * compte, il preserve le comportement actuel quand une colonne et un role
   * portent le meme nom (meme priorite inverse que `sortValueOf`, qui sert le
   * tri et donne, lui, la priorite aux colonnes).
   */
  const optionsOf = useCallback(
    (role: string): Option[] => {
      const field = projected.roles[role];
      if (field != null) return catalogOf(role, field, projected.rows);
      const column = projected.binding?.columns?.find((candidate) => candidate.key === role);
      if (column?.options != null) return column.options();
      // Ni role ni colonne a catalogue : `catalogOf` rend quand meme les valeurs
      // hors catalogue rencontrees, personne ne perd sa ligne.
      return catalogOf(role, undefined, projected.rows);
    },
    [projected],
  );

  /* ---- Les champs modifiables en LOT (chantier 3_0) ---- */

  /**
   * LE CATALOGUE DES CHAMPS QU'UN LOT PEUT ECRIRE, pris sur la vue ACTIVE et
   * nulle part ailleurs : ce qu'on modifie sur douze fiches, ce sont les
   * colonnes qu'on a sous les yeux.
   *
   * IL NE FABRIQUE RIEN. Un champ n'entre ici que si l'ecran a declare son
   * `set()` : ce qui ne s'ecrit pas une fiche a la fois ne s'ecrit pas non
   * plus douze d'un coup, et l'inverse ferait promettre au lot un pouvoir que
   * la fiche n'a pas.
   *
   * LES ROLES D'ABORD, LES COLONNES ENSUITE : c'est l'ordre de lecture du
   * tableau (le titre, le statut, puis les colonnes declarees), donc la liste
   * deroulante range les champs comme l'oeil les a rencontres.
   */
  const bulkEditFields = useMemo<BulkEditField[]>(() => {
    const fields: BulkEditField[] = [];
    const sack = projected.binding?.options ?? {};
    // Les deux libelles que le TABLEAU accepte de renommer par son sac libre
    // (`titleLabel`, `statusLabel`) : la fenetre de lot doit nommer le champ
    // exactement comme la colonne qu'on voit, sinon on croit modifier autre
    // chose. Le `label` du role sert de second recours, le vocabulaire du core
    // de dernier.
    const sackLabel = (key: string): string | undefined => {
      const value = sack[key];
      return typeof value === 'string' && value !== '' ? value : undefined;
    };
    const roleLabelOf = (name: string, field: FieldMap<T>): string => {
      const fromSack =
        name === 'title' ? sackLabel('titleLabel') : name === 'status' ? sackLabel('statusLabel') : undefined;
      return fromSack ?? field.label ?? BULK_ROLE_LABEL[name] ?? name;
    };

    const push = (kind: WriteKind, key: string, label: string, editor: EditorKind, options?: Option[]): void => {
      fields.push({
        key: fieldKeyOf(kind, key),
        label,
        editor,
        // Le type s'annonce DANS l'option, jamais dans une colonne a part.
        editorLabel: `${label}, ${EDITOR_LABEL[editor]}`,
        ...(editor === 'select' ? { options: options ?? [] } : {}),
      });
    };

    for (const [name, field] of Object.entries(projected.roles)) {
      if (field == null || typeof field.set !== 'function') continue;
      // Multi-values : voir `BULK_EXCLUDED_ROLES`. Une seule valeur ecrite sur
      // un role qui en porte plusieurs efface les autres, en silence.
      if (BULK_EXCLUDED_ROLES.has(name)) continue;
      const label = roleLabelOf(name, field);
      // Un role a CATALOGUE est une liste de choix, quoi qu'il arrive : c'est
      // la presence d'`options()` qui le dit, exactement comme pour la fiche.
      if (typeof field.options === 'function') {
        push('role', name, label, 'select', optionsOf(name));
        continue;
      }
      if (name === 'dateStart' || name === 'dateEnd') {
        push('role', name, label, 'date');
        continue;
      }
      // Aucune valeur declaree ne dit le type d'un role de texte : on prend la
      // premiere valeur RENCONTREE dans les lignes affichees, sinon `text`.
      // Meme deduction que la fiche, parce que c'est LA MEME FONCTION.
      let sample: Cell = null;
      for (const row of projected.rows) {
        const raw = row.extra[name];
        if (raw !== undefined && raw !== null && raw !== '') {
          sample = raw;
          break;
        }
      }
      push('role', name, label, editorKindOf(sample, undefined));
    }

    for (const column of projected.columns) {
      if (!column.editable) continue;
      // La cellule porte la VALEUR ; le type declare par l'ecran gagne, sinon
      // la premiere valeur renseignee de la fenetre affichee decide.
      let sample: Cell = null;
      for (const row of projected.rows) {
        const cell = row.cells[column.key];
        if (cell != null && cell.value !== null && cell.value !== '') {
          sample = cell.value;
          break;
        }
      }
      const editor = editorKindOf(sample, column.kind);
      // Le catalogue d'une COLONNE est celui que `columnCatalogs` a deja
      // resolu : on ne passe pas par `optionsOf`, qui regarde les roles
      // d'abord et rendrait le catalogue du role homonyme.
      push('column', column.key, column.label, editor, projected.catalogs[column.key]);
    }

    return fields;
  }, [projected, optionsOf]);

  /* ---- Selecteur de vues ---- */

  const views = useMemo<ViewOption[]>(() => {
    const collectionEmpty = projected.orderedIds.length === 0;
    return activeTypes.map((type) => {
      const binding = bindingsRef.current.find((candidate) => candidate.type === type.id);
      let reason = unmetRequirement(type, binding);
      if (reason == null && type.level === 'record' && collectionEmpty && openId == null) {
        reason = 'Aucun element';
      }
      return {
        id: type.id,
        label: type.label,
        icon: type.icon,
        level: type.level,
        disabled: reason != null,
        ...(reason != null ? { reason } : {}),
      };
    });
  }, [activeTypes, projected.orderedIds, openId]);

  /* ---- L'URL : l'etat d'ecran devient une adresse, et l'inverse ---- */

  /**
   * L'etat d'ecran, vu comme une adresse. Que des IDENTIFIANTS.
   *
   * LE TEXTE DE RECHERCHE N'Y EST PAS, et c'est volontaire (decision du
   * fondateur, 30/07/2026) : on tape un nom ou un email dans cette barre, et
   * une adresse se copie dans un mail, un ticket, l'historique d'un navigateur
   * et les journaux d'un serveur. Le chantier l'exigeait en toutes lettres :
   * des ids, jamais un nom. Cout assume : un lien partage ne rejoue pas la
   * recherche de celui qui l'a envoye.
   */
  const toSearch = useCallback((): Record<string, string> => {
    const params: Record<string, string> = { view: viewIdRef.current };
    // L'`id` ne sort QUE si la vue active est de niveau fiche. `openId` reste
    // volontairement pose quand on quitte la fiche par le SELECTEUR de vues
    // (voir `setView`) : l'ecrire quand meme mettait dans l'adresse un couple
    // que la relecture tranche a l'envers ("l'id fait foi sur le niveau").
    // L'utilisateur regardait un tableau, F5 le posait sur une fiche, et le
    // lien qu'il copiait ouvrait chez son collegue autre chose que ce qu'il
    // avait sous les yeux.
    const active = typesRef.current.find((type) => type.id === viewIdRef.current);
    if (active?.level === 'record' && openIdRef.current != null) {
      params['id'] = openIdRef.current;
    }
    const current = queryRef.current;
    // `s` : l'id de porte, ou la section libre d'un ecran d'avant les portes.
    // Meme parametre, meme sens.
    if (current.section != null) params['s'] = current.section;
    // `f` : les filtres actifs, en liste separee par des virgules (contrat
    // etendu du gabarit v2, voir screenUrl.tsx). Un ecran V1 n'a jamais plus
    // d'un id actif : son adresse est OCTET POUR OCTET celle d'avant.
    const filterParam = joinFilterIds(current.filterIds);
    if (filterParam != null) params['f'] = filterParam;
    // `arch` : le MODE ARCHIVES, et lui seul des etats de tranche, parce qu'il
    // change QUELS enregistrements existent a l'ecran (le tri, qui ne change
    // que leur ordre, reste hors de l'adresse). Ecrit uniquement quand il est
    // allume : une adresse ordinaire ne change pas d'un octet.
    const archParam = writeArchivesParam(current.archivesMode);
    if (archParam != null) params['arch'] = archParam;
    return params;
  }, []);

  const fromSearch = useCallback(
    (params: Record<string, string | undefined>): void => {
      const id = params['id'] != null && params['id'] !== '' ? params['id'] : null;
      const view = asViewId(params['view']);
      const section = params['s'];
      const filter = params['f'];
      /*
       * LE MODE ARCHIVES, comme les filtres : l'adresse fait foi, y compris
       * par son ABSENCE. Un `arch` disparu veut dire "mode eteint" (le Retour
       * du navigateur depuis un etat archives vers un etat nu doit bien
       * eteindre le mode ; nos adresses ecrivent toujours `arch` quand il est
       * allume, son absence est donc une information, pas un oubli). Une
       * valeur inconnue vaut eteint elle aussi, meme regle que `?s=` inconnu.
       *
       * Il n'est lu QUE par la branche a portes, plus bas : sans portes, aucune
       * surface ne peut montrer ni eteindre le mode (le detail y est ecrit).
       */
      const archives = readArchivesParam(params['arch']);

      if (doorsRef.current.length > 0) {
        /*
         * ECRAN A PORTES. `s` designe une porte, `f` la liste des filtres, et
         * l'adresse fait foi sur les DEUX, y compris leur absence : un `f`
         * disparu veut dire "aucun filtre" (le Retour du navigateur depuis un
         * etat filtre vers un etat nu doit bien retirer les tags ; nos
         * adresses ecrivent toujours `f` quand des filtres sont actifs, son
         * absence est donc une information, pas un oubli). Un `s` inconnu ou
         * absent laisse la porte courante : une adresse retouchee a la main ne
         * demonte pas l'ecran.
         */
        const doors = doorsRef.current;
        const target =
          section != null && doors.some((door) => door.id === section)
            ? section
            : queryRef.current.section;
        if (target != null && target !== queryRef.current.section) {
          // Changement de porte par l'adresse : MEME remise a zero qu'un clic
          // de menu, avec les filtres de l'adresse par-dessus, verifies contre
          // le catalogue de la porte CIBLE (les refs portent encore l'ancienne
          // porte a cet instant : on lit le catalogue a la source).
          const door = doors.find((candidate) => candidate.id === target);
          const defs =
            door?.sections === false
              ? []
              : (door?.filters ?? optionsRef.current.filters ?? []);
          const asked = splitFilterIds(filter).filter((filterId) =>
            defs.some((def) => def.id === filterId),
          );
          openDoor(target, asked, archives);
        } else {
          // Meme porte : seuls les filtres et le mode bougent. LES DEUX DANS
          // LE MEME PATCH, jamais deux appels : deux passages dans
          // `mutateTags` recalculeraient la liste deux fois et reecriraient
          // l'adresse deux fois, exactement ce que `clearAll` evite deja.
          const asked = splitFilterIds(filter).filter((filterId) =>
            filterDefsRef.current.some((def) => def.id === filterId),
          );
          mutateTags((previous) =>
            sameIdList(previous.filterIds, asked) && previous.archivesMode === archives
              ? null
              : { filterIds: asked, archivesMode: archives },
          );
        }
      } else {
        // ECRAN SANS PORTES : comportement historique, au cheveu pres.
        //
        // La section passe par `applySection`, PAS par `setSection` : ce
        // dernier referme la fiche, or une adresse peut tres bien porter une
        // section ET un element (un lien vers la fiche d'un materiel de la
        // section Camera). L'appeler ici cassait ce lien sans un mot :
        // `openRecord` lit des refs que le rendu n'a pas encore rafraichies,
        // donc la fermeture demandee juste avant lui faisait croire qu'on
        // etait deja au bon niveau, et la fiche ne s'ouvrait pas. Ici
        // l'adresse fait foi : elle dit deja si un element est ouvert,
        // personne n'a a le deduire.
        if (section != null) applySection(section);
        // Une pastille venue de l'adresse est verifiee, comme a
        // l'initialisation : un `f` inconnu (faute de frappe, pastille masquee
        // depuis dans les reglages) laisserait la rangee 3 sans aucune pastille
        // allumee et sans filtrage, un etat que rien a l'ecran n'explique. On
        // tolere la forme liste (une adresse v2 collee sur un ecran V1) en
        // prenant le premier id connu : la pastille reste a choix unique.
        const askedFilter = splitFilterIds(filter).find((filterId) =>
          filterDefsRef.current.some((def) => def.id === filterId),
        );
        if (askedFilter != null) setFilter(askedFilter);
        /*
         * LE MODE ARCHIVES N'EST PAS RELU ICI, et c'est le meme verrou qu'a
         * l'initialisation (voir `initialArchives`) : un ecran sans portes ne
         * passe pas `tools` au gabarit, donc il n'a ni outil filtre, ni tag,
         * ni tiroir, ni "tout effacer". Le mode s'y allumerait sans qu'aucune
         * surface ne le montre ni ne puisse l'eteindre, et la liste resterait
         * vide pour toujours : une nasse, ouverte par un simple lien.
         *
         * Le jour ou l'un de ces ecrans passera `tools={screen}`, il aura des
         * portes (c'est le meme passage au gabarit v2) et ce verrou tombera
         * tout seul.
         */
      }

      if (id == null) {
        // Adresse sans element : la fiche se ferme. C'est ce chemin qui donne
        // son sens au bouton Retour du navigateur, et il n'existait pas dans
        // la version "prete pour l'URL" de ce hook.
        if (openIdRef.current != null) openRecord(null);
        if (view != null && view !== viewIdRef.current) setView(view);
        return;
      }
      // L'`id` fait foi sur le niveau : `view=core.fiche` n'est qu'un
      // raccourci, et une vue de collection en presence d'un `id` est ignoree
      // (l'ecriture reecrira l'adresse avec la vue reellement active).
      if (id !== openIdRef.current) openRecord(id);
      const asked = typesRef.current.find((type) => type.id === view);
      if (asked?.level === 'record' && asked.id !== viewIdRef.current) setView(asked.id);
    },
    [applySection, mutateTags, openDoor, openRecord, setFilter, setView],
  );

  /**
   * LA SYNCHRONISATION, dans les deux sens, avec UNE seule empreinte partagee.
   *
   * `urlSyncedRef` porte l'adresse que l'etat et le routeur sont censes dire
   * tous les deux. Chaque sens ne parle que si ce qu'il voit en differe : sans
   * ce garde-fou, l'ecriture declencherait la relecture, qui declencherait
   * l'ecriture, et l'ecran tournerait en rond a chaque clic.
   *
   * Elle est initialisee avec l'adresse du PREMIER rendu, dont l'etat de depart
   * est deja issu (plus haut). ATTENTION, ca ne suffit PAS a proteger le
   * montage : les deux effets tournent dans le meme flush, le sens 1 peut avoir
   * bouge l'empreinte avant que le sens 2 ne la compare, et le sens 2 lit alors
   * une adresse que le routeur n'a pas encore eu le temps de mettre a jour.
   * C'est `urlFirstReadRef` qui tient ce bord, pas cette empreinte.
   */
  const urlSyncedRef = useRef<string>(screenSearchKey(initialSearchRef.current));
  const urlLastRef = useRef<ScreenSearch>(initialSearchRef.current);
  /**
   * L'ADRESSE DEPUIS LAQUELLE ON A EMPILE LA FICHE, ou `null` si l'etape de
   * fiche courante ne nous appartient pas (entree a froid par un lien partage,
   * F5 dans une fiche : il n'y a rien a depiler derriere, l'etape d'avant peut
   * meme appartenir a un autre site).
   *
   * Elle sert a trancher la sortie de fiche : DEPILER (le Retour du navigateur
   * et la puce de sortie de la rangee 3 font alors la meme chose, sans laisser
   * une etape morte qui rouvrirait la fiche), ou REMPLACER. On ne peut pas se
   * contenter de "l'`id` a disparu" : changer de section depuis une fiche
   * referme la fiche ET change la section, et depiler ramenerait l'ANCIENNE
   * section, donc annulerait le clic qu'on vient de faire. On ne depile donc
   * que si ce qu'on s'apprete a ecrire est EXACTEMENT l'adresse d'ou l'on vient.
   */
  const urlRecordOriginRef = useRef<ScreenSearch | null>(null);
  /**
   * Le tout premier passage du sens 2 ne doit RIEN appliquer : l'etat de depart
   * est deja issu de cette adresse (plus haut). Les deux effets tournent dans
   * le meme flush au montage, et le sens 1 a pu ecrire entre-temps : le sens 2
   * lirait alors l'adresse d'AVANT et defairait ce que l'etat vient de
   * resoudre. Symptome vecu : on revient dans une app, la reprise de session
   * rouvre la fiche, et le sens 2 la referme aussitot en relisant une adresse
   * vide.
   */
  const urlFirstReadRef = useRef(true);
  /**
   * Le montage n'empile jamais : il remplace l'etape courante. Une reprise (de
   * session ou de lien) n'est pas un geste de l'utilisateur, et l'etape
   * parasite se paierait en un appui de Retour qui ne fait rien.
   */
  const urlFirstWriteRef = useRef(true);

  /*
   * Sens 1 : l'etat ecrit l'adresse.
   *
   * EFFET DE MISE EN PAGE, et pas un effet ordinaire : un effet ordinaire
   * tourne APRES la peinture, donc l'adresse arrivait une image en retard. En
   * changeant d'app, la barre d'adresse affichait d'abord `/app/<id>` tout nu
   * (le routeur a navigue), puis les parametres apparaissaient : ca CLIGNOTAIT,
   * avec un etat intermediaire bien visible. Ici l'ecriture se fait dans le
   * meme commit que le rendu qui l'a provoquee, avant que l'ecran ne soit
   * peint : l'adresse et l'image changent ensemble.
   *
   * Le sens 2, lui, reste un effet ordinaire : relire l'adresse n'a aucune
   * raison de bloquer la peinture, et l'ordre entre les deux ne change pas (le
   * garde-fou d'empreinte est le meme des deux cotes).
   */
  useLayoutEffect(() => {
    if (urlPort == null) return;
    // Le drapeau se consomme AU PREMIER PASSAGE, pas a la premiere ecriture.
    // Sinon une arrivee par lien (ou l'adresse dit deja tout, donc rien a
    // ecrire) laisserait le drapeau leve, et la premiere fiche ouverte ensuite
    // serait prise pour le montage : elle n'empilerait pas son etape, et le
    // Retour du navigateur quitterait la page au lieu de refermer la fiche.
    const first = urlFirstWriteRef.current;
    urlFirstWriteRef.current = false;
    const next = pickScreenSearch(toSearch());
    const key = screenSearchKey(next);
    if (key === urlSyncedRef.current) return;
    const before = urlLastRef.current;
    // Empiler une etape est reserve a l'OUVERTURE d'une fiche, et jamais au
    // montage : au montage il n'y a pas eu de geste, seulement une reprise (de
    // session ou de lien), et l'etape parasite se paierait en un appui de
    // Retour qui ne fait rien.
    const entering = !first && before['id'] == null && next['id'] != null;
    const leaving = before['id'] != null && next['id'] == null;
    const origin = urlRecordOriginRef.current;
    urlSyncedRef.current = key;
    urlLastRef.current = next;
    if (leaving) {
      urlRecordOriginRef.current = null;
      if (origin != null && screenSearchKey(origin) === key) {
        urlPort.back();
        return;
      }
    }
    if (entering) urlRecordOriginRef.current = before;
    urlPort.setParams(next, { push: entering });
    // `query.filterIds` et non le miroir `filterId` : le miroir s'annule des
    // qu'un second filtre se pose, l'adresse doit suivre CHAQUE changement de
    // la liste (son identite ne change que quand son contenu change).
    // `query.archivesMode` y est au meme titre : sans lui, la barre d'adresse
    // garderait `arch=1` apres la sortie du mode, et le lien qu'on copie
    // ouvrirait une autre liste que celle qu'on a sous les yeux.
  }, [urlPort, toSearch, viewId, openId, query.section, query.filterIds, query.archivesMode]);

  // Sens 2 : l'adresse ecrit l'etat (retour navigateur, lien colle, F5).
  useEffect(() => {
    if (urlPort == null) return;
    const incoming = pickScreenSearch(urlPort.params);
    if (urlFirstReadRef.current) {
      urlFirstReadRef.current = false;
      // Surtout ne pas toucher a `urlSyncedRef` : le sens 1 y a peut-etre deja
      // pose l'empreinte qu'il vient d'ecrire, et c'est celle-la qui fait foi.
      return;
    }
    const key = screenSearchKey(incoming);
    if (key === urlSyncedRef.current) return;
    const hadId = urlLastRef.current['id'] != null;
    urlSyncedRef.current = key;
    urlLastRef.current = incoming;
    if (incoming['id'] == null) {
      // L'etape de fiche qu'on avait empilee vient d'etre depilee.
      urlRecordOriginRef.current = null;
    } else if (!hadId) {
      // Une fiche retrouvee par le bouton SUIVANT du navigateur : son etape
      // existe bel et bien dans l'historique, et la collection est derriere.
      // Sans ca, la sortie suivante remplacait au lieu de depiler et laissait
      // deux etapes identiques, donc un appui de Retour sans aucun effet.
      // L'origine se reconstruit telle qu'on l'ECRIRA en sortant : meme section,
      // meme pastille, sans element, et la derniere vue collection (c'est celle
      // ou `openRecord(null)` ramene).
      urlRecordOriginRef.current = pickScreenSearch({
        ...incoming,
        id: undefined,
        view: lastCollectionRef.current,
      });
    }
    fromSearch(incoming);
  }, [urlPort, fromSearch]);

  /* ---- Sortie ---- */

  const status: ViewStatus = {
    // Doctrine du gabarit : `loading` est reserve au PREMIER chargement, sinon
    // l'ecran clignote a chaque rafraichissement.
    loading: inFlight && records.length === 0,
    ...(error != null ? { error } : {}),
    // "Aucune donnee n'existe", jamais "aucun resultat de recherche" : la
    // recherche et les pastilles s'appliquent APRES, et leur absence de
    // resultat se rend dans la vue, pas dans l'etat vide.
    empty: !inFlight && error == null && projected.poolCount === 0,
    // Mode serveur : le serveur seul sait s'il reste des pages. Mode client :
    // la projection le sait, elle a tout le jeu sous la main.
    truncated: pagination === 'server' ? hasMore : projected.truncated,
  };

  /**
   * Le catalogue de filtres pour l'UI. Les COMPTEURS se calculent sur le jeu
   * vivant de la porte, jamais sur le resultat courant (regle "un compteur ne
   * bouge pas quand on filtre") : cliquer un filtre ne fait bouger aucun
   * nombre du popover. Un filtre sans `match` n'a pas de compteur : le core ne
   * sait pas l'evaluer, et un zero serait un mensonge.
   */
  const filters = useMemo<ScreenFilterOption[]>(
    () =>
      activeFilterDefs.map((def) => {
        const match = def.match;
        if (match == null) return { id: def.id, label: def.label };
        return { id: def.id, label: def.label, count: projected.live.filter(match).length };
      }),
    // La dependance est l'IDENTITE du catalogue : un ecran V1 qui memoise ses
    // defs ne recalcule qu'a bon escient, un ecran a portes qui les recree a
    // chaque rendu recompte a chaque rendu (quelques `filter` sur le jeu
    // vivant, negligeable, et surtout jamais faux).
    [activeFilterDefs, projected.live],
  );

  // Niveau fiche : la vue ne recoit QUE la ligne ouverte (une, ou aucune).
  const rows = level === 'record' ? (openRow ? [openRow] : []) : projected.windowRows;

  /* ---- LA SELECTION : sa surface, et son INVARIANT (chantier 3_0) ---- */

  /**
   * QUELLE SURFACE PORTE LA SELECTION. Le tableau, et lui seul : agir sur un
   * lot demande de VOIR les valeurs sur lesquelles on agit. Le test se fait
   * sur le descripteur RESOLU, pas sur `viewId` seul : un id de vue qui ne
   * correspond a aucun type monte (catalogue de porte reduit) ne monte rien du
   * tout, et une selection sur une vue absente n'aurait aucune surface.
   */
  const selectionLevel: 'table' | null =
    activeType != null && activeType.id === SELECTION_VIEW_ID && activeType.level === 'collection'
      ? 'table'
      : null;

  /**
   * L'INVARIANT, LE PLUS IMPORTANT DE CE CHANTIER : une ligne qui sort de
   * l'ecran sort de la selection.
   *
   * Il est DERIVE, dans le rendu meme, et pas seulement range par un effet :
   * un effet s'execute apres la peinture, et il resterait une image ou la
   * barre annonce "12 selectionnes" sur une liste qui n'en montre plus trois.
   * Une selection invisible est une bombe : on croit agir sur trois fiches et
   * on en modifie douze.
   *
   * C'est aussi ce qui tient QUITTER LE TABLEAU sans un seul appel a la
   * sortie : hors tableau, il n'y a plus de surface, donc plus de selection.
   * On y arrive par le selecteur de vues, par l'adresse, par le bouton Retour
   * du navigateur ou en ouvrant une fiche : aucun de ces chemins ne partage un
   * geste ou l'on aurait pu poser la remise a zero.
   *
   * ET UNE LIGNE SORT DE L'ECRAN AUSSI QUAND L'ECRAN ENTIER EST REMPLACE PAR UN
   * BLOC D'ETAT. Sortir de `rows` n'est pas la seule facon de ne plus rien
   * montrer : en ERREUR, le `ViewHost` remplace la vue par son bloc d'alerte,
   * alors que le chargeur qui a echoue n'efface RIEN (`setRecords` n'est jamais
   * appele dans la branche d'echec). Les lignes restent donc en memoire, et sans
   * cette ligne-ci la barre de lot continuerait d'annoncer "3 elements
   * selectionnes" avec ses trois boutons VIVANTS au-dessus d'un ecran qui ne
   * montre plus une seule ligne : Archiver ou Supprimer ecriraient sur des
   * fiches que l'utilisateur ne peut plus ni voir ni verifier. C'est ici, dans
   * le MOTEUR, que ca se ferme : le chassis ne recoit pas les etats, c'est le
   * ViewHost qui tient leur priorite. Les autres etats du ViewHost (chargement,
   * delai depasse, vide, fiche introuvable) sont deja couverts : tous supposent
   * `rows` VIDE, donc l'invariant les traite par sa regle ordinaire.
   *
   * La liste d'origine est RENDUE TELLE QUELLE quand rien n'a disparu :
   * fabriquer un tableau neuf a chaque rendu ferait repartir tout ce qui en
   * depend, pour rien.
   */
  const visibleSelection = useMemo<string[]>(() => {
    if (selectionLevel == null) return NO_SELECTION;
    if (error != null) return NO_SELECTION;
    if (selection.length === 0) return NO_SELECTION;
    const shown = new Set(rows.map((row) => row.id));
    const kept = selection.filter((id) => shown.has(id));
    return kept.length === selection.length ? selection : kept;
  }, [selectionLevel, error, selection, rows]);

  /*
   * ET LE MEME INVARIANT, RANGE DANS L'ETAT. Sans cette remise en place, une
   * ligne masquee par une recherche puis RAMENEE par son effacement
   * reviendrait cochee : l'utilisateur l'a vue quitter la selection, elle
   * n'avait aucune raison d'y revenir. La comparaison porte sur la longueur :
   * `visibleSelection` est toujours un sous-ensemble ordonne de `selection`,
   * donc a longueur egale les deux disent la meme chose.
   */
  useEffect(() => {
    if (visibleSelection.length === selection.length) return;
    setSelection(visibleSelection);
  }, [visibleSelection, selection]);

  /**
   * CE QUE LA PORTE SAIT FAIRE, geste par geste. `null` = disponible ; une
   * chaine = la RAISON, ecrite pour celui qui survole le bouton (regle G16 :
   * on grise avec sa raison, on ne cache pas).
   *
   * Recompose a chaque rendu, comme `recordMenuActions` : les verbes vivent
   * dans des refs que la porte active reassigne, memoiser figerait le geste de
   * la porte precedente.
   */
  const bulkAvailability = {
    // EN MODE ARCHIVES, elles y sont deja : le geste n'aurait rien a faire, et
    // proposer un bouton qui ne change rien est pire que l'expliquer.
    archive: query.archivesMode
      ? BULK_REASON.alreadyArchived
      : archiveRef.current == null
        ? RECORD_ACTION_REASON.archive
        : null,
    remove: removeRef.current == null ? RECORD_ACTION_REASON.remove : null,
    edit: bulkEditFields.length === 0 ? BULK_REASON.noEditableField : null,
  };

  /* ---- Ce que la VUE recoit, assemble a UN seul endroit ---- */

  const viewProps: ViewProps = {
    rows,
    columns: projected.columns,
    optionsOf,
    status,
    query,
    onQueryChange: setQuery,
    cursor,
    onOpenRecord: openRecord,
    onPatchRecord: patchRecord,
    onPatchColumn: patchColumn,
    saveState,
    // `onLoadMore` n'est fourni que s'il reste vraiment des lignes a montrer :
    // une vue ne doit pas proposer un bouton qui ne fait rien.
    ...(status.truncated ? { onLoadMore: loadMore } : {}),
    onRangeChange: setRange,
    viewState: viewStates[viewId],
    setViewState,
    // La cle se compose ICI : la vue ne connait ni l'app, ni l'ecran, ni la
    // porte, et une cle fabriquee a la main dans un composant divergerait du
    // moule des trois autres helpers.
    columnWidthsKey: columnWidthsPrefKey(appId, screenId, doorId, viewId),
    scrollRef,
    options: projected.binding?.options ?? {},
    /*
     * LA SELECTION (chantier 3_0), SEULEMENT EN TABLEAU. Les deux props sont
     * ABSENTES partout ailleurs, et c'est ce qui met la decision hors de
     * portee d'une vue : la liste et le kanban ne recoivent rien a ignorer, ils
     * ne recoivent RIEN. Une vue qui les recoit sait, par leur seule presence,
     * qu'elle a le droit de dessiner sa colonne de choix.
     */
    ...(selectionLevel === 'table'
      ? { selection: visibleSelection, onSelectionChange: setSelection }
      : {}),
    /*
     * LE BANDEAU D'ARCHIVE (chantier 2_0), au niveau fiche uniquement : une
     * collection n'a pas d'etat d'archive, et poser `archived: false` sur
     * toutes les vues ferait croire l'inverse. La sortie ne part QUE si la
     * porte declare le verbe : un bandeau qui porterait un bouton mort serait
     * pire que pas de bouton du tout.
     */
    ...(level === 'record'
      ? {
          archived: openArchived,
          ...(openArchived && openRecordValue != null && unarchiveRef.current != null
            ? {
                onUnarchive: (): void => {
                  void runRecordAction('unarchive', openRecordValue);
                },
              }
            : {}),
        }
      : {}),
  };

  /*
   * La SEULE porte d'une vue vers la rangee 3a. Les actions sont calculees ici
   * (le core), pas dans la vue, et forcees en secondaire : une vue ne peut pas
   * s'attribuer le bouton lime, qui appartient a l'ecran.
   */
  const viewActions: ScreenAction[] = (activeType?.actions?.(viewProps) ?? []).map((action) => ({
    ...action,
    variant: 'secondary',
  }));

  /*
   * LES GESTES RARES DE LA FICHE (menu "..." de la rangee 3c, chantier 2_0).
   * Calcules ici, par le core, pour la meme raison que `viewActions` : le
   * gabarit rend, il ne decide pas ; et les 55 apps n'ecrivent pas une ligne
   * pour ca.
   *
   * Recomposes a chaque rendu, comme le reste de la sortie : les verbes vivent
   * dans des refs que la porte active reassigne a chaque passage, memoiser
   * figerait le geste de la porte precedente.
   *
   * VIDE quand aucune fiche n'est ouverte (ou quand elle est introuvable) : il
   * n'y a alors rien sur quoi agir. Le menu n'est de toute facon rendu qu'au
   * niveau record.
   */
  const recordMenuActions: ScreenAction[] = [];
  if (openRecordValue != null) {
    const target = openRecordValue;
    /*
     * UNE SEULE ENTREE, DEUX SENS : "Archiver" devient "Desarchiver" quand la
     * fiche est archivee, jamais deux entrees dont une grisee. L'id, lui, ne
     * change PAS avec le sens : c'est le meme element de menu qui bascule, et
     * un id qui change le ferait remonter a chaque aller-retour.
     *
     * Desarchiver ne demande AUCUNE confirmation : le geste est reversible et
     * remet la fiche la ou on la cherchait. Archiver en demande une, parce que
     * la fiche quitte les listes et que rien a l'ecran ne dirait ou elle part.
     */
    const toggleKind: RecordActionKind = openArchived ? 'unarchive' : 'archive';
    const toggleVerb = openArchived ? unarchiveRef.current : archiveRef.current;
    recordMenuActions.push({
      id: 'record.archive',
      label: openArchived ? 'Desarchiver' : 'Archiver',
      variant: 'secondary',
      disabled: toggleVerb == null,
      ...(toggleVerb == null ? { disabledReason: RECORD_ACTION_REASON[toggleKind] } : {}),
      // UNE fiche : l'accord de la fenetre suit ce nombre, jusque dans le
      // corps du texte (`archiveConfirm`, ConfirmDialog.tsx).
      ...(openArchived ? {} : { confirm: archiveConfirm(1) }),
      onClick: (): void => {
        void runRecordAction(toggleKind, target);
      },
    });
    /*
     * SUPPRIMER reste SECONDAIRE dans le menu : le rouge ne vit que dans la
     * fenetre, et il y est le DERNIER geste d'un chemin. Un aplat rouge
     * propose en premier attire le clic qu'il devrait retenir.
     *
     * La fenetre ne mentionne PAS que la ligne survit en base : c'est un
     * garde-fou d'exploitation pour le support Taki, pas une corbeille
     * utilisateur. Pour l'utilisateur, supprime veut dire supprime.
     */
    const removeVerb = removeRef.current;
    recordMenuActions.push({
      id: 'record.remove',
      label: 'Supprimer',
      variant: 'secondary',
      disabled: removeVerb == null,
      ...(removeVerb == null ? { disabledReason: RECORD_ACTION_REASON.remove } : {}),
      confirm: removeConfirm(1),
      onClick: (): void => {
        void runRecordAction('remove', target);
      },
    });
  }

  /*
   * LA PORTE ACTIVE, RESOLUE pour l'ecran : ce qu'il passe au gabarit tel
   * quel. Les stats en forme fonction recoivent le jeu VIVANT (archives
   * exclues, avant recherche et filtres) : insensibles au filtrage par
   * construction, doctrine inchangee. Recomposee a chaque rendu, comme le
   * reste de la sortie : ce sont les champs qui comptent, pas l'identite.
   */
  const doorStats: ScreenStat[] =
    activeDoor?.stats == null
      ? []
      : typeof activeDoor.stats === 'function'
        ? activeDoor.stats(projected.live)
        : activeDoor.stats;
  const door: ScreenDoorState = {
    id: doorId,
    // La porte implicite n'a pas de libelle : le titre appartient a l'ecran.
    label: activeDoor?.label ?? '',
    searchPlaceholder: activeDoor?.searchPlaceholder ?? DEFAULT_SEARCH_PLACEHOLDER,
    sections: doorSections,
    actions: activeDoor?.actions ?? [],
    stats: doorStats,
  };

  return {
    records,
    rows,
    orderedIds: projected.orderedIds,
    columns: projected.columns,
    status,

    query,
    setQuery,
    setSearch,
    filters,
    activeFilterId: query.filterId ?? undefined,
    setFilter,
    activeFilterIds: query.filterIds,
    toggleFilter,
    removeFilter,
    clearFilters,
    sortStack: query.sortStack,
    toggleSort,
    invertSort,
    removeSort,
    clearSort,
    clearAll,
    // Le MODE ARCHIVES au PREMIER NIVEAU de l'etat, et pas dans un sous-objet :
    // c'est ce qui permet a `tools={screen}` de continuer a marcher sans un
    // seul recablage dans les apps.
    archivesMode: query.archivesMode,
    setArchivesMode,
    recordMenuActions,
    drawerOpen,
    setDrawerOpen,
    favorites,
    activeFavoriteId,
    applyFavorite,
    keepFavorite,
    canKeepFavorite,
    forgetFavorite,
    door,
    section: query.section,
    setSection,

    /*
     * LA SELECTION ET LES ACTIONS DE LOT au PREMIER NIVEAU de l'etat, et pas
     * dans un sous-objet : c'est ce qui permet a `tools={screen}` de porter la
     * barre de lot sans un seul recablage dans les apps, exactement comme
     * `archivesMode` et `recordMenuActions` au chantier 2_0.
     *
     * C'est `visibleSelection` qui sort, jamais l'etat brut : l'invariant est
     * tenu a la SORTIE, donc aucune surface ne peut lire un id qu'on ne voit
     * plus, meme le temps d'un rendu.
     */
    selection: visibleSelection,
    setSelection,
    clearSelection: dropSelection,
    selectionLevel,
    bulkEditFields,
    archiveOne,
    removeOne,
    writeCell,
    bulkAvailability,

    views,
    viewId,
    setView,
    lastCollectionViewId,
    activeType,
    viewOptions: projected.binding?.options ?? {},
    viewProps,
    viewActions,

    openId,
    openRecord,
    cursor,
    markRead,

    patchRecord,
    patchColumn,
    saveState,
    retrySave,
    undo,
    redo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,

    viewState: viewStates[viewId],
    setViewState,

    optionsOf,
    pagination,
    reload,
    loadMore,
    setRange,
    scrollRef,

    toSearch,
    fromSearch,
  };
}
